import { useKnownAddresses, matchKnownAddresses } from "@/lib/knownAddresses";
import { displayLocalDate } from "@/lib/dateHelpers";
import { useEffect, useMemo, useRef, useState } from "react";
import { AdminShell } from "@/components/Layout";
import StatusFlag from "@/components/StatusFlag";
import { useIndustry, useLabel } from "@/context/IndustryContext";
import { useVendorFilter } from "@/contexts/VendorFilterContext";
import NeedsDocsBanner from "@/components/NeedsDocsBanner";
import QuickUpdateCard from "@/components/QuickUpdateCard";
import HabitabilityCard from "@/components/HabitabilityCard";
import WoFilesTab from "@/components/WoFilesTab";
import WoAnswerSheet from "@/components/WoAnswerSheet";
import BulkAssignModal from "@/components/BulkAssignModal";
import PinToSweeperButton from "@/components/PinToSweeperButton";
import ReopenAndPinButton from "@/components/ReopenAndPinButton";
import { formatUnit } from "@/lib/formatters";
import PropertyDetailsCard from "@/components/PropertyDetailsCard";
import PmActivityReportCard from "@/components/PmActivityReportCard";
import { WoLeaseContextChip } from "@/components/UnitsEditor";
import ComplianceRadarCard from "@/components/ComplianceRadarCard";
import OwnerApprovalCard from "@/components/OwnerApprovalCard";
import DuplicatesBanner from "@/components/DuplicatesBanner";
import AutoDiagnoseBanner from "@/components/AutoDiagnoseBanner";
import TriageLinkCard from "@/components/TriageLinkCard";
import WOPermitPanel from "@/components/WOPermitPanel";
import AutoDispatchSuggestModal from "@/components/AutoDispatchSuggestModal";
import TenantThreadPanel from "@/components/TenantThreadPanel";
import InlineWoFix from "@/components/InlineWoFix";
import TenantHistoryPanel from "@/components/TenantHistoryPanel";
import EstimatorModal from "@/components/EstimatorModal";
import ChargebackPanel from "@/components/ChargebackPanel";
import PropertyHistoryPanel from "@/components/PropertyHistoryPanel";
import GuardrailMonitorCard from "@/components/GuardrailMonitorCard";
import AppFolioSyncCard from "@/components/AppFolioSyncCard";
import MoraleCard from "@/components/MoraleCard";
import ProposalReviewCard from "@/components/ProposalReviewCard";
import { useAccessLog } from "@/pages/AdminAccessLog";
import PropertyNotesCard from "@/components/PropertyNotesCard";
import LegalProtocolPanel from "@/components/LegalProtocolPanel";
import LandlordQuoteModal from "@/components/LandlordQuoteModal";
import MarkFundedButton from "@/components/MarkFundedButton";
import FundingGuardBanner from "@/components/FundingGuardBanner";
import KraaftJobPanel from "@/components/KraaftJobPanel";
import PublicStatusLinkCard from "@/components/PublicStatusLinkCard";
import InspectionReportShareCard from "@/components/InspectionReportShareCard";
import ChangeOrderPanel from "@/components/ChangeOrderPanel";
import QuotePanel from "@/components/QuotePanel";
import WOBundleModal from "@/components/WOBundleModal";
import PhotoAlbumCard from "@/components/PhotoAlbumCard";
import JobHistoryTimeline from "@/components/JobHistoryTimeline";
import ESignPanel from "@/components/ESignPanel";
import BulkImportModal from "@/components/BulkImportModal";
import InspectionWizard from "@/components/InspectionWizard";
import InspectionFlowSimple from "@/components/InspectionFlowSimple";
import PhotoRetagModal from "@/components/PhotoRetagModal";
import JobMessages from "@/components/JobMessages";
import { AIGatedButton, useTenantCtx } from "@/lib/pro";
import { createPortal } from "react-dom";
import { api, fileUrl, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, MapPin, Clock, User, Briefcase, ChartLine, Users, ListChecks,
  CheckCircle, Warning, X, Trash, Camera, Receipt, UploadSimple, Sparkle, FilePdf,
  Repeat, PencilSimple, MagnifyingGlass, Wrench, Envelope, Lightning, PlayCircle,
  PaintBrush, CloudRain, XCircle, Notepad, Copy, GitMerge, Fire, WarningOctagon, Compass, DownloadSimple, Download, ArrowsClockwise,
  ClipboardText, Truck
} from "@phosphor-icons/react";
import QuickJobModal from "@/components/QuickJobModal";
import EtaRequestModal from "@/components/EtaRequestModal";
import HabitabilityCountdown from "@/components/HabitabilityCountdown";
import OwnerNoticeModal from "@/components/OwnerNoticeModal";
import PhotoOpen from "@/components/PhotoOpen";
import VendorSpendPill from "@/components/VendorSpendPill";
import LiveActivityFeed from "@/components/LiveActivityFeed";
import ShowUpCard from "@/components/ShowUpCard";
import PortfolioHealthScore from "@/components/PortfolioHealthScore";
import DepositCountdownWidget from "@/components/DepositCountdownWidget";
import QuoteConfidenceChip from "@/components/QuoteConfidenceChip";
import AdminJobsToolbar from "@/components/admin-jobs/AdminJobsToolbar";
import JobCard from "@/components/admin-jobs/JobCard";
import RadarHero from "@/components/RadarHero";
import VendorChipBar from "@/components/VendorChipBar";
import KraaftReconciliationWidget from "@/components/KraaftReconciliationWidget";
const _useState = useState;

// Category → color map for the AI-classified job labels
const CATEGORY_COLORS = {
  "Plumbing": "bg-blue-600",
  "Electrical": "bg-amber-500",
  "HVAC": "bg-cyan-600",
  "Roof": "bg-stone-700",
  "Appliance": "bg-violet-600",
  "High Water Usage": "bg-sky-700",
  "Windows/Doors": "bg-teal-600",
  "Drywall/Paint": "bg-rose-500",
  "Landscaping": "bg-green-600",
  "Pest": "bg-orange-600",
  "General Maintenance": "bg-zinc-500",
};

/**
 * TechChipRow — multi-select tech filter for the WOs list.
 *
 * Slack-style: tap any tech chip to toggle it in/out of the filter set.
 * Shows the top 4 techs (by name) inline; the rest live behind a
 * "+N More" picker sheet identical in feel to VendorChipBar. Selected
 * chips get the cyan accent + count badge so multi-select is obvious.
 *
 * Ciarra ask: "I want to be able to select multiple people or companies
 * from list or only see certain people or companies sometime 2 or 3."
 */
function TechChipRow({ workers, selected, onToggle, onClear }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const active = selected.size > 0;
  const sorted = useMemo(
    () => [...workers].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [workers]
  );
  const primary = [];
  const rest = [];
  for (const w of sorted) {
    const key = (w.name || "").toLowerCase();
    if (primary.length < 4 || selected.has(key)) primary.push(w);
    else rest.push(w);
  }
  const filteredPickerRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(w => (w.name || "").toLowerCase().includes(q));
  }, [sorted, query]);
  return (
    <div className="w-full" data-testid="jobs-tech-chiprow">
      <div className="flex items-center gap-1.5 flex-wrap">
        <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-600 mr-1">
          👷 Tech
          {active && (
            <button onClick={onClear} data-testid="jobs-tech-clear"
              className="ml-1 text-cyan-700 hover:text-rose-600 inline-flex items-center gap-0.5">
              <X weight="bold" size={10}/> Clear
            </button>
          )}
        </div>
        <button onClick={onClear}
          data-testid="jobs-tech-chip-any"
          className={`h-7 px-2 border-2 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 transition ${
            !active
              ? "bg-slate-900 border-slate-900 text-white"
              : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
          }`}>
          Any
        </button>
        {primary.map(w => {
          const key = (w.name || "").toLowerCase();
          const on = selected.has(key);
          return (
            <button key={w.id || w.name}
              onClick={() => onToggle(w.name)}
              data-testid={`jobs-tech-chip-${key.replace(/\W+/g, "-")}`}
              className={`h-7 px-2 border-2 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 transition ${
                on
                  ? "bg-cyan-600 border-cyan-700 text-white"
                  : "bg-white border-slate-300 text-slate-700 hover:border-cyan-500"
              }`}>
              {on && <span>✓</span>}
              <span className="max-w-[14ch] truncate">{w.name}</span>
            </button>
          );
        })}
        {rest.length > 0 && (
          <button onClick={() => setPickerOpen(true)}
            data-testid="jobs-tech-more"
            className="h-7 px-2 border-2 border-dashed border-slate-400 bg-white hover:bg-slate-50 text-slate-600 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            +{rest.length} more ▾
          </button>
        )}
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 ml-1 hidden sm:inline">
          Tap to add · tap again to remove
        </span>
      </div>
      {pickerOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
          data-testid="jobs-tech-picker" onClick={() => setPickerOpen(false)}>
          <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-slate-900 bg-slate-50 flex items-center gap-2">
              <div className="font-display font-black text-base uppercase tracking-tight flex-1">
                Pick Techs · {selected.size} selected
              </div>
              <button onClick={() => setPickerOpen(false)} data-testid="jobs-tech-picker-close"
                className="h-9 w-9 flex items-center justify-center text-slate-500 hover:text-slate-800">
                <X size={20}/>
              </button>
            </div>
            <div className="p-3 border-b border-slate-200">
              <input
                type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search techs…"
                data-testid="jobs-tech-picker-search"
                className="w-full h-10 px-3 border-2 border-slate-300 rounded-md text-sm font-medium focus:border-cyan-500 focus:outline-none"/>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {filteredPickerRows.length === 0 && (
                <div className="p-8 text-center text-sm font-black uppercase tracking-widest text-slate-400">
                  No matches
                </div>
              )}
              {filteredPickerRows.map(w => {
                const key = (w.name || "").toLowerCase();
                const on = selected.has(key);
                return (
                  <button key={w.id || w.name}
                    onClick={() => onToggle(w.name)}
                    data-testid={`jobs-tech-picker-row-${key.replace(/\W+/g, "-")}`}
                    className={`w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 ${on ? "bg-cyan-50" : ""}`}>
                    <div className={`h-6 w-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      on ? "bg-cyan-500 border-cyan-700" : "bg-white border-slate-300"
                    }`}>
                      {on && <span className="text-white text-xs font-black">✓</span>}
                    </div>
                    <div className={`flex-1 text-sm font-black truncate ${on ? "text-cyan-900" : "text-slate-800"}`}>
                      {w.name}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="p-3 border-t-2 border-slate-200 bg-slate-50 flex gap-2">
              {selected.size > 0 && (
                <button onClick={onClear} data-testid="jobs-tech-picker-clear"
                  className="h-10 px-4 border-2 border-red-400 bg-white text-red-600 font-black uppercase tracking-widest text-xs rounded-md hover:bg-red-50">
                  Clear
                </button>
              )}
              <button onClick={() => setPickerOpen(false)} data-testid="jobs-tech-picker-done"
                className="flex-1 h-10 border-2 border-slate-900 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-xs rounded-md">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryBadge({ label, id }) {
  const cls = CATEGORY_COLORS[label] || "bg-zinc-500";
  return (
    <span data-testid={`cat-badge-${id}`}
      className={`inline-flex items-center h-5 px-1.5 ${cls} text-white text-[10px] font-black uppercase tracking-widest whitespace-nowrap flex-shrink-0`}>
      {label}
    </span>
  );
}

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-red-700 flex-shrink-0"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
);

export function AdminDashboard() {
  return (
    <AdminShell>
      <AdminDashboardBody/>
    </AdminShell>
  );
}

// Extracted body — same widgets, no shell. Reused by AdminDashboardV2 which
// wraps this in the new BrandedHero + polished visual container.
export function AdminDashboardBody({ v2 = false }) {
  const { industry } = useIndustry();
  const isPM = industry === "pm";
  const portalLabel = useLabel("customer_portal", "Customer Portal");
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [aging, setAging] = useState({ count: 0, items: [] });
  const [subsBacklog, setSubsBacklog] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [quickOpen, setQuickOpen] = useState(false);
  const [etaOpen, setEtaOpen] = useState(false);
  const [etaCounts, setEtaCounts] = useState({ open: 0, urgent: 0 });
  const reload = () => {
    api.get("/stats/admin").then(r => setStats(r.data));
    api.get("/jobs").then(r => setJobs(r.data));
    api.get("/maintenance/aging-equipment?min_age=8").then(r => setAging(r.data)).catch(() => {});
    api.get("/stats/subs-backlog").then(r => setSubsBacklog(r.data?.subs || [])).catch(() => {});
    api.get("/sweep/clusters").then(r => setClusters(r.data?.clusters || [])).catch(() => {});
    api.get("/eta-requests/counts").then(r => setEtaCounts(r.data || { open: 0, urgent: 0 })).catch(() => {});
  };
  useEffect(() => { reload(); }, []);
  const recent = jobs.slice(0, 8);
  return (
      <div className={`p-4 sm:p-8 max-w-[1600px] mx-auto ${v2 ? "space-y-4" : ""}`}>
        <RadarHero/>

        {/* Vendor filter — same context feeds Jobs Board + Radar + WOs list */}
        <VendorChipBar className="mb-3"/>

        {/* Compliance Radar — auto-hides if no upcoming expiries */}
        <div className="mb-3">
          <ComplianceRadarCard/>
        </div>

        {/* Upload Guardrail — flags any WO gaining >10 files in an hour */}
        <div className="mb-3"><MoraleCard/></div>
        <div className="mb-3"><ProposalReviewCard/></div>
        <div className="mb-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
          <GuardrailMonitorCard/>
          <AppFolioSyncCard/>
        </div>

        {/* Compact stat row — Open · Pool · Life Safety only */}
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="dashboard-top-stats">
          <Link to="/admin/jobs?status=open" className="flex flex-col items-start p-3 rounded-lg bg-white border border-slate-200 hover:border-cyan-500 transition" data-testid="stat-open">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Open</div>
            <div className="text-2xl font-black text-cyan-700 leading-none mt-0.5 tabular-nums">{stats?.open ?? 0}</div>
          </Link>
          <Link to="/admin/jobs?status=pool" className="flex flex-col items-start p-3 rounded-lg bg-white border border-slate-200 hover:border-amber-500 transition" data-testid="stat-pool">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Pool</div>
            <div className="text-2xl font-black text-amber-600 leading-none mt-0.5 tabular-nums">{stats?.pool ?? 0}</div>
          </Link>
          <Link to="/admin/jobs?status=habitability" className="flex flex-col items-start p-3 rounded-lg bg-white border border-slate-200 hover:border-red-500 transition" data-testid="stat-life-safety">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Life Safety</div>
            <div className="text-2xl font-black text-red-600 leading-none mt-0.5 tabular-nums">{stats?.habitability_open ?? stats?.habitability ?? 0}</div>
          </Link>
        </div>

        {/* Primary actions — 2 buttons: Quick Job + New Job */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <button onClick={() => setQuickOpen(true)} data-testid="dashboard-quick-job"
            className="h-11 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-white text-sm font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 shadow-lg">
            <Lightning weight="fill" size={16}/> Quick Job
          </button>
          <Link to="/admin/jobs"
            className="h-11 px-4 rounded-xl bg-[#00C4CC] hover:bg-[#1cd5dc] text-slate-950 font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 shadow-lg"
            data-testid="dashboard-new-job">
            <Plus weight="bold" size={16}/> New Job
          </Link>
        </div>

        {/* Secondary actions — smaller, less prominent */}
        <div className="grid grid-cols-2 gap-2 mb-3" data-testid="dashboard-more-actions">
          <button onClick={() => setEtaOpen(true)} data-testid="dashboard-log-eta"
            className="h-9 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-[11px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
            <WarningOctagon weight="fill" size={13} className="text-red-500"/> Log ETA
          </button>
          <Link to="/tenant/login" target="_blank" rel="noreferrer"
            data-testid="dashboard-tenant-portal"
            className="h-9 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-[11px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
            <Users weight="fill" size={13} className="text-violet-500"/> {portalLabel}
          </Link>
        </div>
        {/* legacy hero content removed */}
        <QuickJobModal open={quickOpen} onClose={() => { setQuickOpen(false); reload(); }}/>
        <EtaRequestModal open={etaOpen} onClose={() => setEtaOpen(false)} onCreated={reload}/>

        {/* Portfolio Health — slim tap row (not a big card) */}
        <PortfolioHealthScore slim/>

        {/* Kraaft / AppFolio sync tile */}
        <KraaftReconciliationWidget/>

        {/* Live pulse — real-time nervous system for demos */}
        <LiveActivityFeed/>

        <div className="mt-4">
          <DepositCountdownWidget/>
        </div>


        {etaCounts.open > 0 && (
          <Link to="/admin/jobs?status=new" data-testid="eta-banner"
            className="mt-5 block rounded-xl border border-red-200 bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 p-4 shadow-sm hover:shadow transition-all">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                <WarningOctagon weight="fill" size={22}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-base sm:text-lg tracking-tight text-red-900">
                  {etaCounts.open} ETA Request{etaCounts.open === 1 ? "" : "s"} Waiting
                </div>
                <div className="text-xs sm:text-sm font-medium text-red-700">
                  Property Managers &amp; picky tenants want an answer — assign a tech and share Live ETA now.
                </div>
              </div>
              <ArrowRightIcon/>
            </div>
          </Link>
        )}

        {/* Stats — tighter card treatment, 6-across on desktop, 2-across mobile */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
          <Stat label="Total Jobs" value={stats?.total_jobs ?? "—"} icon={<Briefcase weight="bold"/>} to="/admin/jobs" testId="stat-total"/>
          <Stat label="Open" value={stats?.open ?? "—"} subtitle={stats?.pool != null ? `${stats.pool} pool · ${stats.assigned} assigned · ${stats.in_progress} active` : null} accent="orange" icon={<Clock weight="bold"/>} to="/admin/jobs" testId="stat-open"/>
          <Stat label="Pool (Unassigned)" value={stats?.pool ?? "—"} accent="yellow" icon={<Warning weight="bold"/>} to="/admin/jobs?status=pool" testId="stat-pool"/>
          <Stat label="Completed" value={stats?.completed ?? "—"} accent="green" icon={<CheckCircle weight="bold"/>} to="/admin/jobs?status=completed" testId="stat-completed"/>
          <Stat label="Workers" value={stats?.workers ?? "—"} icon={<Users weight="bold"/>} to="/admin/workers" testId="stat-workers"/>
          <Stat
            label={stats?.subs_pending_invoice > 0 ? `Subs · ${stats.subs_pending_invoice} pending` : "Subs"}
            value={stats?.subs ?? "—"}
            accent={stats?.subs_pending_invoice > 0 ? "orange" : undefined}
            icon={<Wrench weight="bold"/>}
            to="/admin/sub-contractors"
            testId="stat-subs"/>
        </div>
        {aging.count > 0 && (
          <section className="mt-8 sm:mt-10" data-testid="aging-equipment-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display font-black text-xl sm:text-2xl uppercase tracking-wide flex items-center gap-2">
                <Warning weight="fill" size={22} className="text-yellow-600"/> Aging Equipment
              </h2>
              <span className="text-xs font-black uppercase tracking-widest text-slate-500">
                {aging.count} appliance{aging.count === 1 ? "" : "s"} 8+ yr old
              </span>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {aging.items.slice(0, 6).map((a, i) => (
                <Link key={a.job_id + i} to={`/admin/jobs/${a.job_id}`}
                  data-testid={`aging-item-${i}`}
                  className="rugged-card p-3 hover:bg-slate-50 transition-colors block">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display font-black text-sm">
                        {a.brand || "Unknown"} {a.type && <span className="text-slate-500 font-bold">· {a.type}</span>}
                      </div>
                      {a.model && <div className="text-[11px] font-mono text-slate-600 truncate">M/{a.model}</div>}
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{a.property_address}</div>
                    </div>
                    <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 flex-shrink-0 ${
                      a.estimated_age_years >= 15 ? "bg-red-500 text-white" : "bg-yellow-400 text-[#0f172a]"
                    }`}>
                      {a.estimated_age_years} yr
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            {aging.count > 6 && (
              <div className="mt-3 text-xs font-bold text-slate-500">
                Showing 6 of {aging.count} — sorted oldest first
              </div>
            )}
          </section>
        )}



        {clusters.length > 0 && (
          <section className="mt-8 sm:mt-10" data-testid="cluster-radar-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display font-black text-xl sm:text-2xl uppercase tracking-wide flex items-center gap-2">
                <MapPin weight="fill" size={22} className="text-red-500"/> SweepMode<sup className="text-xs align-super">™</sup> Radar
                <span className="text-xs font-black uppercase tracking-widest text-slate-500">· {clusters.length} clusters</span>
              </h2>
              <Link to="/admin/lineup" data-testid="lineup-cta"
                className="tactile-btn h-11 px-4 border-2 border-[#0f172a] bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-slate-800">
                <Lightning weight="fill" size={14}/> Plan Tomorrow's Lineup
              </Link>
            </div>
            <p className="mt-1 text-xs font-bold text-slate-500 uppercase tracking-widest">
              Same-building jobs waiting — bundle to avoid the "we were literally around the corner" backtrack
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {clusters.slice(0, 6).map(c => (
                <div key={c.signature} className="rugged-card p-3 bg-red-50 border-red-500" data-testid={`cluster-${c.signature}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-black text-sm truncate">{c.address}</div>
                      {c.assignees.length > 0 && (
                        <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-0.5 truncate">
                          Crew here: {c.assignees.join(", ")}
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 flex-shrink-0 bg-red-600 text-white">
                      {c.count} open
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {c.jobs.slice(0, 4).map(j => (
                      <Link key={j.id} to={`/admin/jobs/${j.id}`}
                        className="block text-[11px] hover:text-red-700 truncate"
                        data-testid={`cluster-job-${j.id}`}>
                        {j.work_order_number && <span className="font-mono font-black text-[9px] mr-1">WO#{j.work_order_number}</span>}
                        {j.title}{j.unit ? ` · #${j.unit}` : ""}
                      </Link>
                    ))}
                    {c.jobs.length > 4 && (
                      <div className="text-[10px] font-black uppercase text-slate-400">+ {c.jobs.length - 4} more</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {subsBacklog.length > 0 && (
          <section className="mt-8 sm:mt-10" data-testid="subs-backlog-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display font-black text-xl sm:text-2xl uppercase tracking-wide flex items-center gap-2">
                <Wrench weight="fill" size={22} className="text-[#00C4CC]"/> Sub Backlog
              </h2>
              <Link to="/admin/sub-contractors" data-testid="subs-manage-link"
                className="text-xs font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a] underline underline-offset-2">
                Manage subs →
              </Link>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {subsBacklog.map((s) => {
                const totalWork = s.open_jobs + s.quote_requests + s.ready_to_invoice;
                const hot = s.quote_requests > 0 || s.ready_to_invoice > 0 || s.unpaid_dollars > 0;
                return (
                  <Link key={s.id} to="/admin/sub-contractors" data-testid={`sub-card-${s.id}`}
                    className={`rugged-card p-3 block hover:-translate-y-0.5 transition-transform ${hot ? "bg-[#ecfeff] border-[#00C4CC]" : "bg-white"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display font-black text-sm truncate">{s.name}</div>
                        {(s.company || s.trade) && (
                          <div className="text-[11px] text-slate-500 font-bold truncate">
                            {s.company}{s.company && s.trade ? " · " : ""}{s.trade}
                          </div>
                        )}
                      </div>
                      {totalWork > 0 && (
                        <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 flex-shrink-0 bg-[#0f172a] text-white">
                          {totalWork} on deck
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px] font-medium">
                      <div className="flex items-center gap-1"><span className="font-black text-[#0f172a]">{s.open_jobs}</span><span className="text-slate-500">open</span></div>
                      <div className={`flex items-center gap-1 ${s.quote_requests > 0 ? "text-amber-700 font-black" : ""}`}>
                        <span className="font-black">{s.quote_requests}</span><span className={s.quote_requests > 0 ? "text-amber-700" : "text-slate-500"}>quote req</span>
                      </div>
                      <div className={`flex items-center gap-1 ${s.ready_to_invoice > 0 ? "text-[#00C4CC] font-black" : ""}`}>
                        <span className="font-black">{s.ready_to_invoice}</span><span className={s.ready_to_invoice > 0 ? "text-[#0e7490]" : "text-slate-500"}>ready inv</span>
                      </div>
                      <div className={`flex items-center gap-1 ${s.unpaid_dollars > 0 ? "text-red-700 font-black" : ""}`}>
                        <span className="font-black">${s.unpaid_dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span><span className={s.unpaid_dollars > 0 ? "text-red-700" : "text-slate-500"}>unpaid</span>
                      </div>
                    </div>
                    {!s.email && (
                      <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-red-600">
                        ⚠ Missing email — edit sub to send digests
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </section>
        )}



        <section className="mt-8 sm:mt-10">
          <h2 className="font-display font-black text-xl sm:text-2xl uppercase tracking-wide">Recent Jobs</h2>
          <div className="mt-4 rugged-card overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-[#0f172a] text-white">
                <tr className="text-left text-xs font-black uppercase tracking-wider">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3">Worker</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Priority</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(j => (
                  <tr key={j.id} className="border-t border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => window.location.href = `/admin/jobs/${j.id}`} data-testid={`row-${j.id}`}>
                    <td className="px-4 py-3 font-bold">
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        {j.recurrence?.active && (
                          <span title="Recurring template" data-testid={`recurring-badge-${j.id}`}
                            className="inline-flex items-center h-5 px-1.5 bg-[#00C4CC] text-white text-[10px] font-black uppercase tracking-widest">
                            <Repeat weight="bold" size={10}/>
                          </span>
                        )}
                        {j.work_order_number && (
                          <span className="inline-flex items-center h-5 px-1.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono"
                            data-testid={`wo-badge-${j.id}`}>
                            WO# {j.work_order_number}
                          </span>
                        )}
                        <JobTypePill job={j}/>
                        {j.category && <CategoryBadge label={j.category} id={j.id}/>}
                        {j.title}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {j.property_address}
                      {j.unit && <span className="ml-1.5 px-1.5 py-0.5 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm">#{j.unit}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">{j.assigned_to_name || <span className="text-zinc-400">Unassigned</span>}</td>
                    <td className="px-4 py-3"><StatusPill status={j.status}/></td>
                    <td className="px-4 py-3 text-xs font-black uppercase">{j.priority}</td>
                  </tr>
                ))}
                {recent.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-400 font-bold">No jobs yet. Create your first job.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>
  );
}

function Stat({ label, value, icon, accent, to, testId, subtitle }) {
  const bg = accent === "orange" ? "bg-orange-50 border-orange-200"
           : accent === "green"  ? "bg-emerald-50 border-emerald-200"
           : accent === "yellow" ? "bg-amber-50 border-amber-200"
           :                       "bg-white border-slate-200";
  const iconColor = accent === "orange" ? "text-orange-500"
                  : accent === "green"  ? "text-emerald-500"
                  : accent === "yellow" ? "text-amber-500"
                  :                       "text-slate-400";
  const content = (
    <>
      <div className="flex items-center justify-between">
        <div className={`text-lg ${iconColor}`}>{icon}</div>
      </div>
      <div className="mt-2.5 font-display font-black text-3xl sm:text-4xl leading-none">{value}</div>
      <div className="mt-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      {subtitle && <div className="mt-1 text-[10px] font-medium text-slate-400 leading-tight">{subtitle}</div>}
    </>
  );
  return to ? (
    <Link to={to} data-testid={testId}
      className={`block rounded-xl border ${bg} p-3.5 sm:p-4 hover:shadow-md hover:-translate-y-0.5 transition-all`}>
      {content}
    </Link>
  ) : (
    <div className={`rounded-xl border ${bg} p-3.5 sm:p-4`} data-testid={testId}>{content}</div>
  );
}

function StatusPill({ status }) {
  // Delegates to the new CP-style <StatusFlag> so every existing usage in
  // this file (Recent Jobs, JobDetail header, JobsList rows, etc.) upgrades
  // automatically. Keep this file-local wrapper so downstream code that
  // reaches for `<StatusPill status={...}/>` keeps working unchanged.
  return <StatusFlag status={status}/>;
}

function FollowUpBadge({ reason }) {
  if (!reason) return null;
  const cfg = {
    paint_touch_up: { label: "Paint Touch-Up", icon: <PaintBrush weight="fill" size={11}/>, bg: "bg-fuchsia-600" },
    roof_storm_verify: { label: "Storm/Rain Verify", icon: <CloudRain weight="fill" size={11}/>, bg: "bg-sky-600" },
    other: { label: "Follow-Up · Other", icon: <Warning weight="fill" size={11}/>, bg: "bg-yellow-500" },
  }[reason];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 h-5 px-1.5 text-white text-[10px] font-black uppercase tracking-widest ${cfg.bg}`}
      data-testid={`follow-up-badge-${reason}`}
      title={cfg.label}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function JobTypePill({ job }) {
  if (job.inspection_type === "move_out") {
    return <span data-testid={`type-badge-${job.id}`}
      className="inline-flex items-center h-5 px-1.5 bg-orange-600 text-white text-[10px] font-black uppercase tracking-widest rounded-md">
      Move-Out
    </span>;
  }
  if (job.inspection_type === "move_in") {
    return <span data-testid={`type-badge-${job.id}`}
      className="inline-flex items-center h-5 px-1.5 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest rounded-md">
      Move-In
    </span>;
  }
  if (job.inspection_type === "periodic") {
    return <span data-testid={`type-badge-${job.id}`}
      className="inline-flex items-center h-5 px-1.5 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-md">
      Periodic
    </span>;
  }
  if (job.is_turn) {
    return <span data-testid={`type-badge-${job.id}`}
      className="inline-flex items-center h-5 px-1.5 bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest rounded-md">
      Turn
    </span>;
  }
  return null;
}

// ------- Jobs list & create --------
export function AdminJobs() {
  const { industry } = useIndustry();
  const isPM = industry === "pm";
  const { asQuery: vendorQuery } = useVendorFilter();
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [combineOpen, setCombineOpen] = useState(false);
  const nav = useNavigate();
  const statusFilter = searchParams.get("status");
  const legacyFilter = searchParams.get("legacy") === "1";
  const groupByZip = searchParams.get("group") === "zip";
  // Sort + filter state (URL-persisted so refresh keeps view)
  const sortBy = searchParams.get("sort") || "newest";  // newest|oldest|address|title|status|tech
  const techFilter = searchParams.get("tech") || "";      // comma-separated list of worker names
  const ageBucket = searchParams.get("age") || "";        // today|week|month|older
  // Auto-open New Job modal when landing with ?new=1 (used by Office AI Help "Turn into WO")
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get("import") === "1") {
      setCsvOpen(true);
      searchParams.delete("import");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Stale-open drill-in from Kraaft widget: server-side filter (?stale=1) matches the widget count.
  const staleFilter = searchParams.get("stale") === "1";
  const STATUS_LABEL = { assigned: "Assigned", in_progress: "Active", completed: "Completed", canceled: "Canceled", merged: "Merged", follow_up: "Follow-up", needs_docs: "Needs Docs", life_safety: "Life Safety", habitability: "Habitability", new: "New", pool: "Pool" };

  const load = () => {
    if (statusFilter === "needs_docs") {
      return api.get("/needs-docs-queue").then(r => setJobs(r.data));
    }
    // Kraaft stale-open drill-in: server-side filter matches the widget count exactly.
    if (staleFilter) {
      return api.get("/jobs?stale=1&limit=1000").then(r => setJobs(r.data));
    }
    // Legacy drill-down: fetch archived legacy CSV rows only (optionally filtered by status).
    if (legacyFilter) {
      const legacyStatuses = ["completed", "canceled", "assigned", "new"];
      const qs = statusFilter && legacyStatuses.includes(statusFilter)
        ? `?legacy=1&status=${encodeURIComponent(statusFilter)}&limit=1000`
        : "?legacy=1&limit=1000";
      return api.get(`/jobs${qs}`).then(r => setJobs(r.data));
    }
    // Forward status to backend so completed/canceled/etc. don't get hidden by the default active filter.
    const backendStatuses = ["completed", "canceled", "in_progress", "follow_up", "new", "assigned", "pool"];
    const vq = vendorQuery ? `&vendors=${encodeURIComponent(vendorQuery)}` : "";
    const url = (statusFilter && backendStatuses.includes(statusFilter))
      ? `/jobs?status=${encodeURIComponent(statusFilter)}&limit=1000${vq}`
      : `/jobs?limit=1000${vq}`;
    return api.get(url).then(r => setJobs(r.data));
  };
  useEffect(() => {
    load();
    api.get("/assignable-users").then(r => setWorkers(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, legacyFilter, staleFilter, vendorQuery]);

  // Live radar poll — every 30s refetch so new WOs (webhooks, other admins) surface.
  // Toolbar watches jobs.length delta and fires a sweep animation.
  useEffect(() => {
    const id = setInterval(() => { load().catch(() => {}); }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, legacyFilter, staleFilter]);

  const quickClose = async (jobId, targetStatus) => {
    const verb = targetStatus === "canceled" ? "Cancel" : "Close";
    const reason = window.prompt(`${verb} this job now. Reason (optional — office can add docs later):`, "");
    if (reason === null) return; // user cancelled prompt
    try {
      await api.post(`/jobs/${jobId}/quick-close`, { status: targetStatus, reason: reason || "" });
      toast.success(`${verb}d — job lands in Needs Docs queue`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Could not ${verb.toLowerCase()}`);
    }
  };

  const statusFilteredJobs = statusFilter === "needs_docs"
    ? jobs.filter(j => j.needs_docs && !j.docs_completed_at)
    : statusFilter === "life_safety"
    ? jobs.filter(j => j.life_safety && j.status !== "completed" && j.status !== "canceled" && j.status !== "merged")
    : statusFilter === "habitability"
    ? jobs.filter(j => j.habitability && j.status !== "merged")
    : statusFilter
    ? jobs.filter(j => statusFilter === "in_progress"
        ? (j.status === "in_progress" || j.status === "assigned")
        : j.status === statusFilter)
    : jobs;

  const q = query.trim().toLowerCase();
  const filteredJobs = q
    ? statusFilteredJobs.filter(j => {
        const hay = [
          j.property_address, j.title, j.customer_name, j.customer_phone,
          j.work_order_number, j.assigned_to_name, j.helper_name, j.description,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
    : statusFilteredJobs;

  // Group-by-ZIP dispatch view. Uses 5-digit ZIP when present; falls back to street-name token
  // (e.g. "maplewood") so ProCall's zip-less AppFolio data still clusters cleanly.
  const zipOf = (addr) => {
    const m = /\b(\d{5})(?:-\d{4})?\b/.exec(addr || "");
    return m ? m[1] : "";
  };
  const streetOf = (addr) => {
    for (const raw of (addr || "").split(/\s+/)) {
      const t = raw.replace(/[.,]$/g, "").toLowerCase();
      if (t && !/^\d+$/.test(t)) return t;
    }
    return "";
  };
  const clusterKeyOf = (addr) => zipOf(addr) || streetOf(addr) || "zzz";

  // Age-bucket filter — filter jobs by created_at relative to today.
  const filteredByAge = ageBucket
    ? filteredJobs.filter(j => {
        if (!j.created_at) return false;
        const dt = new Date(j.created_at);
        const now = new Date();
        const diffDays = (now - dt) / 86400000;
        if (ageBucket === "today") return diffDays < 1;
        if (ageBucket === "week")  return diffDays < 7;
        if (ageBucket === "month") return diffDays < 30;
        if (ageBucket === "older") return diffDays >= 30;
        return true;
      })
    : filteredJobs;

  // Tech (assigned_to_name) filter
  // Tech filter now supports MULTI-select — user asked (Ciarra) to see
  // WOs for 2-3 techs at once, not just one. Stored comma-separated in
  // URL so state deep-links. Empty string = "any tech".
  const techFilterSet = useMemo(
    () => new Set((techFilter || "").split(",").map(s => s.trim().toLowerCase()).filter(Boolean)),
    [techFilter]
  );
  const filteredByTech = techFilterSet.size > 0
    ? filteredByAge.filter(j => techFilterSet.has((j.assigned_to_name || "").toLowerCase()))
    : filteredByAge;

  // Sort — applied only when NOT grouping by ZIP (group-by-ZIP has its own sort).
  const sortedJobs = groupByZip
    ? [...filteredByTech].sort((a, b) => {
        const ka = clusterKeyOf(a.property_address);
        const kb = clusterKeyOf(b.property_address);
        if (ka !== kb) return ka.localeCompare(kb);
        return (a.property_address || "").localeCompare(b.property_address || "");
      })
    : [...filteredByTech].sort((a, b) => {
        if (sortBy === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
        if (sortBy === "address") return (a.property_address || "").localeCompare(b.property_address || "");
        if (sortBy === "title") return (a.title || "").localeCompare(b.title || "");
        if (sortBy === "status") return (a.status || "").localeCompare(b.status || "");
        if (sortBy === "tech") return (a.assigned_to_name || "zzz").localeCompare(b.assigned_to_name || "zzz");
        // "newest" (default)
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
  const visibleJobs = sortedJobs;

  const visibleIds = visibleJobs.map(j => j.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someVisibleSelected = visibleIds.some(id => selected.has(id));

  const toggleOne = (id) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected(s => {
      const next = new Set(s);
      if (allVisibleSelected) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} job${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    const t = toast.loading(`Deleting ${ids.length} job${ids.length === 1 ? "" : "s"}…`);
    try {
      const r = await api.post("/jobs/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} job${r.data.deleted === 1 ? "" : "s"}`, { id: t });
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Bulk delete failed", { id: t });
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDownloadPdfs = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkBusy(true);
    const t = toast.loading(`Building ${ids.length} PDF${ids.length === 1 ? "" : "s"}…`);
    try {
      const r = await api.post("/jobs/bulk-pdf-zip", { job_ids: ids },
        { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `work-orders-${ids.length}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length} report${ids.length === 1 ? "" : "s"}`, { id: t });
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Download failed", { id: t });
    } finally { setBulkBusy(false); }
  };

  const bulkSyncClickUp = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Sync ${ids.length} selected WOs with ClickUp?\n\nPulls each one's real ClickUp status (done → Completed) and address hint. Runs in batches of 60; nothing is notified.`)) return;
    setBulkBusy(true);
    let done = 0, completed = 0, addr = 0;
    try {
      for (let k = 0; k < ids.length; k += 60) {
        const r = await api.post("/admin/auto-created-wos/sync-status-from-clickup", { ids: ids.slice(k, k + 60), max_fetches: 60, soft_deadline_sec: 100 });
        const st = r.data?.stats || {};
        done += st.fetched || 0; completed += st.mapped_completed || 0; addr += st.address_found || 0;
        toast(`ClickUp sync ${Math.min(k + 60, ids.length)}/${ids.length}…`, { duration: 1500 });
      }
      toast.success(`Synced ${done} WOs · ${completed} marked completed · ${addr} addresses found`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "ClickUp sync failed")); }
    finally { setBulkBusy(false); }
  };

  const bulkCombinedReport = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const forWhom = window.prompt("Report is for… (name shown on the cover)", "") || "Property Manager";
    setBulkBusy(true);
    const t = toast.loading(`Building combined report for ${ids.length} WO${ids.length === 1 ? "" : "s"}…`);
    try {
      const r = await api.post("/reports/combined-report-pdf",
        { job_ids: ids, for_whom: forWhom },
        { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `combined-report-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Report ready · ${ids.length} WO${ids.length === 1 ? "" : "s"}`, { id: t });
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Report failed", { id: t });
    } finally { setBulkBusy(false); }
  };

  const bulkEmailPdfs = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const to = window.prompt(
      `Email ${ids.length} work-order PDF${ids.length === 1 ? "" : "s"} to:`,
      "",
    );
    if (!to || !to.includes("@")) {
      if (to !== null) toast.error("Enter a valid email address");
      return;
    }
    const note = window.prompt("Add a note (optional):", "") || "";
    setBulkBusy(true);
    const t = toast.loading(`Emailing ${ids.length} PDF${ids.length === 1 ? "" : "s"} to ${to}…`);
    try {
      const r = await api.post("/jobs/bulk-email-pdfs",
        { job_ids: ids, to: to.trim(), note, customer: true },
        { timeout: 120000 });
      toast.success(`Sent ${r.data.attached} PDF${r.data.attached === 1 ? "" : "s"} to ${r.data.to}`, { id: t });
      clearSelection();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Email failed", { id: t });
    } finally { setBulkBusy(false); }
  };

  const bulkQuickClose = async (targetStatus) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const verb = targetStatus === "canceled" ? "Cancel" : "Complete";
    const reason = window.prompt(
      `${verb} ${ids.length} job${ids.length === 1 ? "" : "s"} now — they land in Needs Docs for photos/write-ups later.\n\nReason (optional):`,
      "AppFolio backfill — already handled",
    );
    if (reason === null) return;
    setBulkBusy(true);
    const t = toast.loading(`${verb === "Cancel" ? "Cancel" : "Clos"}ing ${ids.length} job${ids.length === 1 ? "" : "s"}…`);
    try {
      const r = await api.post("/jobs/bulk-quick-close", { job_ids: ids, status: targetStatus, reason: reason || "" });
      const closed = r.data.closed ?? 0;
      const skipped = r.data.skipped ?? 0;
      toast.success(
        `${closed} job${closed === 1 ? "" : "s"} landed in Needs Docs${skipped ? ` · ${skipped} skipped` : ""}`,
        { id: t, duration: 5000 },
      );
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || `Bulk ${verb.toLowerCase()} failed`, { id: t });
    } finally {
      setBulkBusy(false);
    }
  };

  const aiClassifyAll = async () => {
    if (!window.confirm("Run AI classification on every un-tagged job? This uses AI credits.")) return;
    const t = toast.loading("Classifying jobs with AI…");
    try {
      const r = await api.post("/jobs/backfill-categories");
      toast.success(`Tagged ${r.data.updated} jobs · ${r.data.skipped} skipped`, { id: t });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed", { id: t });
    }
  };

  const backfillGeocode = async () => {
    const force = window.confirm("Re-geocode every job (even ones already on the map)?\n\nOK = Force re-run everything (fixes jobs pinned in wrong cities).\nCancel = Only fill in missing coords.");
    const t = toast.loading("Re-geocoding addresses…");
    let total = 0, failedAll = 0;
    try {
      for (let skip = 0; skip < 400; skip += 40) {
        const r = await api.post(`/jobs/backfill-geocode?force=${force}&limit=40&skip=${skip}`);
        total += r.data.updated || 0;
        failedAll += r.data.skipped || 0;
        if ((r.data.updated || 0) + (r.data.skipped || 0) < 40) break;
        toast.loading(`Re-geocoding… ${total} fixed so far`, { id: t });
      }
      toast.success(`Fixed ${total} jobs · ${failedAll} unresolvable`, { id: t });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed", { id: t });
    }
  };

  // ── Bulk WO Actions ────────────────────────────────────────────────────
  const bulkStatus = async () => {
    if (selected.size === 0) return;
    const status = window.prompt(
      "New status for the selected jobs?\n\n" +
      "Choose one:\n" +
      "  new · assigned · in_progress · on_hold · en_route · on_scene · completed · canceled · needs_docs\n",
      "in_progress"
    );
    if (!status) return;
    setBulkBusy(true);
    const t = toast.loading(`Updating ${selected.size} jobs → ${status}…`);
    try {
      const r = await api.post("/jobs/bulk-status", {
        job_ids: Array.from(selected), status: status.trim().toLowerCase(),
      });
      toast.success(`${r.data.updated} updated · ${r.data.skipped} skipped`, { id: t });
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Bulk status failed"), { id: t });
    } finally { setBulkBusy(false); }
  };

  const bulkExportCsv = () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected).join(",");
    // Use fetch with Bearer auth to trigger a browser download
    const url = `${api.defaults.baseURL}/jobs/bulk-export.csv?ids=${encodeURIComponent(ids)}`;
    const tok = localStorage.getItem("token") || "";
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `field-radar-jobs-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success(`Exported ${selected.size} jobs to CSV`);
      })
      .catch(() => toast.error("CSV export failed"));
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-8">
        <AdminJobsToolbar
          jobs={jobs}
          statusFilter={statusFilter}
          legacyFilter={legacyFilter}
          STATUS_LABEL={STATUS_LABEL}
          query={query}
          setQuery={setQuery}
          isPM={isPM}
          setSearchParams={setSearchParams}
          onNewJob={() => setOpen(true)}
          onImportCsv={() => setCsvOpen(true)}
          onBulkImport={() => setBulkOpen(true)}
          onAiClassify={aiClassifyAll}
          onFixMap={backfillGeocode}
        />
        <VendorChipBar className="mt-3"/>
        {staleFilter && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5" data-testid="stale-filter-banner">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] font-black uppercase tracking-widest text-amber-800 whitespace-nowrap">Kraaft drill-in</span>
              <span className="text-xs text-amber-900 truncate">
                Open WOs synced from AppFolio before the newest import — Kraaft may have already closed these.
              </span>
            </div>
            <button
              onClick={() => { const n = new URLSearchParams(searchParams); n.delete("stale"); setSearchParams(n); }}
              data-testid="stale-filter-clear"
              className="text-[10px] font-black uppercase tracking-widest text-amber-800 hover:text-amber-950 whitespace-nowrap"
            >
              Clear ×
            </button>
          </div>
        )}
        {/* Legacy title + button row hidden (replaced by AdminJobsToolbar) */}
        <div className="hidden">
          <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
            {statusFilter && (
              <button onClick={() => setSearchParams({})} data-testid="clear-filter-btn"
                className="tactile-btn bg-white text-[#0f172a] h-12 px-3 font-black uppercase tracking-wide inline-flex items-center justify-center gap-1 border-2 border-[#0f172a]">
                <X weight="bold" size={16}/> Clear
              </button>
            )}
            <button onClick={() => setSearchParams({ status: "needs_docs" })}
              data-testid="needs-docs-filter-btn"
              className={`tactile-btn h-12 px-3 sm:px-4 font-black uppercase tracking-wide inline-flex items-center justify-center gap-1.5 border-2 border-[#0f172a] ${statusFilter === "needs_docs" ? "bg-amber-500 text-white" : "bg-white text-[#0f172a] hover:bg-amber-50"}`}
              title="Jobs quick-closed that still need photos/notes">
              <Notepad weight="bold" size={16}/> <span className="hidden sm:inline">Needs </span>Docs
            </button>
            <Link to="/admin/needs-docs/batch"
              data-testid="needs-docs-batch-btn"
              className="tactile-btn h-12 px-3 sm:px-4 font-black uppercase tracking-wide inline-flex items-center justify-center gap-1.5 border-2 border-[#0f172a] bg-white text-cyan-700 hover:bg-cyan-50"
              title="Drop 20 phone photos and let AI match each to the right WO">
              <Sparkle weight="bold" size={16}/> <span className="hidden sm:inline">Batch </span>Photos
            </Link>
            {isPM && <button onClick={() => setSearchParams({ status: "life_safety" })}
              data-testid="life-safety-filter-btn"
              className={`tactile-btn h-12 px-3 sm:px-4 font-black uppercase tracking-wide inline-flex items-center justify-center gap-1.5 border-2 border-[#0f172a] ${statusFilter === "life_safety" ? "bg-red-600 text-white" : "bg-white text-red-700 hover:bg-red-50"}`}
              title="Life-safety issues only (leaks, gas, no heat, etc.)">
              <Warning weight="bold" size={16}/> <span className="hidden sm:inline">Life </span>Safety
            </button>}
            {isPM && <button onClick={() => setSearchParams({ status: "habitability" })}
              data-testid="habitability-filter-btn"
              className={`tactile-btn h-12 px-3 sm:px-4 font-black uppercase tracking-wide inline-flex items-center justify-center gap-1.5 border-2 border-[#0f172a] ${statusFilter === "habitability" ? "bg-orange-600 text-white" : "bg-white text-orange-700 hover:bg-orange-50"}`}
              title="Habitability / Philadelphia Safe Healthy Homes Act (30-day rule)">
              <FilePdf weight="bold" size={16}/> <span className="hidden sm:inline">Habitability</span><span className="sm:hidden">Hab</span>
            </button>}
            {isPM && <Link to="/admin/safety-board"
              data-testid="safety-board-link"
              className="tactile-btn h-12 px-3 sm:px-4 font-black uppercase tracking-wide inline-flex items-center justify-center gap-1.5 border-2 border-red-700 bg-red-700 text-white hover:bg-red-800"
              title="War-room view of every open life-safety job">
              <Fire weight="fill" size={16}/> <span className="hidden sm:inline">Safety </span>Board
            </Link>}
            <Link to="/admin/duplicates"
              data-testid="duplicates-queue-link"
              className="tactile-btn h-12 px-3 sm:px-4 font-black uppercase tracking-wide inline-flex items-center justify-center gap-1.5 border-2 border-[#0f172a] bg-white text-[#0f172a] hover:bg-fuchsia-50"
              title="Groups of duplicate open work orders">
              <Copy weight="bold" size={16}/> <span className="hidden sm:inline">Dupes</span>
            </Link>
            <button onClick={() => setCsvOpen(true)} data-testid="import-csv-btn"
              className="tactile-btn bg-white text-[#0f172a] h-12 px-4 sm:px-5 font-black uppercase tracking-wide inline-flex items-center justify-center gap-2 flex-1 sm:flex-initial">
              <UploadSimple weight="bold" size={18}/> <span className="hidden xs:inline">Import </span>CSV
            </button>
            <button onClick={() => setBulkOpen(true)} data-testid="bulk-import-btn"
              className="tactile-btn h-12 px-4 sm:px-5 font-black uppercase tracking-wide inline-flex items-center justify-center gap-2 flex-1 sm:flex-initial bg-gradient-to-r from-[#0f172a] to-[#00C4CC] text-white">
              <Sparkle weight="fill" size={18}/> <span className="hidden sm:inline">AI </span>Bulk
            </button>
            <button
              onClick={async () => {
                if (!window.confirm("Run AI classification on every un-tagged job? This uses AI credits.")) return;
                const t = toast.loading("Classifying jobs with AI…");
                try {
                  const r = await api.post("/jobs/backfill-categories");
                  toast.success(`Tagged ${r.data.updated} jobs · ${r.data.skipped} skipped`, { id: t });
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e) || "Failed", { id: t });
                }
              }}
              data-testid="ai-classify-btn"
              className="tactile-btn bg-white text-[#0f172a] h-12 px-4 sm:px-5 font-black uppercase tracking-wide inline-flex items-center justify-center gap-2 flex-1 sm:flex-initial border-2 border-[#00C4CC]">
              <Sparkle weight="fill" size={18}/> <span className="hidden xs:inline">AI </span>Tag
            </button>
            <button
              onClick={async () => {
                const force = window.confirm("Re-geocode every job (even ones already on the map)?\n\nOK = Force re-run everything (fixes jobs pinned in wrong cities).\nCancel = Only fill in missing coords.");
                const t = toast.loading("Re-geocoding addresses…");
                let total = 0, failedAll = 0;
                try {
                  for (let skip = 0; skip < 400; skip += 40) {
                    const r = await api.post(`/jobs/backfill-geocode?force=${force}&limit=40&skip=${skip}`);
                    total += r.data.updated || 0;
                    failedAll += r.data.skipped || 0;
                    if ((r.data.updated || 0) + (r.data.skipped || 0) < 40) break;
                    toast.loading(`Re-geocoding… ${total} fixed so far`, { id: t });
                  }
                  toast.success(`Fixed ${total} jobs · ${failedAll} unresolvable`, { id: t });
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e) || "Failed", { id: t });
                }
              }}
              data-testid="backfill-geo-btn"
              className="tactile-btn bg-white text-[#0f172a] h-12 px-4 sm:px-5 font-black uppercase tracking-wide inline-flex items-center justify-center gap-2 flex-1 sm:flex-initial border-2 border-fuchsia-500">
              <MapPin weight="fill" size={18}/> <span className="hidden xs:inline">Fix </span>Map
            </button>
            <button onClick={() => setOpen(true)} data-testid="new-job-btn"
              className="tactile-btn bg-[#00C4CC] text-white h-12 px-4 sm:px-5 font-black uppercase tracking-wide inline-flex items-center justify-center gap-2 flex-1 sm:flex-initial">
              <Plus weight="bold" size={18}/> New Job
            </button>
          </div>
        </div>

        {/* Bulk action bar (search now in AdminJobsToolbar) */}
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1"></div>
          <button
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (groupByZip) next.delete("group"); else next.set("group", "zip");
              setSearchParams(next);
            }}
            data-testid="group-by-zip-toggle"
            className={`tactile-btn h-12 px-4 font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 border-2 border-[#0f172a] ${groupByZip ? "bg-[#00C4CC] text-white" : "bg-white text-[#0f172a] hover:bg-slate-50"}`}>
            <MapPin weight="bold" size={14}/> {groupByZip ? "Grouped by ZIP" : "Group by ZIP"}
          </button>
          {selected.size > 0 && (
            <div className="flex items-center gap-2 rugged-card p-2 bg-red-50 border-red-500" data-testid="bulk-action-bar">
              <span className="text-xs font-black uppercase tracking-wider text-red-700 px-2">
                {selected.size} selected
              </span>
              <button onClick={clearSelection} data-testid="bulk-clear-btn"
                className="h-9 px-3 border-2 border-[#0f172a] text-[#0f172a] font-black uppercase text-xs tracking-wider hover:bg-[#0f172a] hover:text-white transition-colors">
                Clear
              </button>
              <button onClick={() => setBulkAssignOpen(true)} disabled={bulkBusy} data-testid="bulk-assign-btn"
                title="Reassign every selected WO to a worker or sub in one hit"
                className="h-9 px-3 bg-cyan-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-cyan-700 disabled:opacity-60">
                <Users weight="bold" size={14}/> Assign
              </button>
              <button onClick={() => setCombineOpen(true)} disabled={bulkBusy || selected.size < 2} data-testid="bulk-combine-btn"
                title="Combine selected WOs into one parent with each ticket as its own checklist line. All selected must be at the same property."
                className="h-9 px-3 bg-fuchsia-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-fuchsia-700 disabled:opacity-60">
                🔗 Combine on one WO
              </button>
              <button onClick={bulkStatus} disabled={bulkBusy} data-testid="bulk-status-btn"
                title="Set an arbitrary status on every selected WO"
                className="h-9 px-3 bg-blue-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-blue-700 disabled:opacity-60">
                <ArrowsClockwise weight="bold" size={14}/> Status
              </button>
              <button onClick={bulkExportCsv} disabled={bulkBusy} data-testid="bulk-export-csv-btn"
                title="Download a spreadsheet of every selected WO"
                className="h-9 px-3 bg-amber-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-amber-700 disabled:opacity-60">
                <Download weight="bold" size={14}/> CSV
              </button>
              <button onClick={() => bulkQuickClose("completed")} disabled={bulkBusy} data-testid="bulk-quick-close-btn"
                title="Mark selected jobs Completed and drop them in Needs Docs for photos/notes later"
                className="h-9 px-3 bg-emerald-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-emerald-700 disabled:opacity-60">
                <CheckCircle weight="bold" size={14}/> Quick-Close · Needs Docs
              </button>
              <button onClick={bulkDownloadPdfs} disabled={bulkBusy} data-testid="bulk-pdf-download-btn"
                title="Download every selected work order as a zip of PDFs"
                className="h-9 px-3 bg-slate-800 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-slate-900 disabled:opacity-60">
                <FilePdf weight="bold" size={14}/> Download PDFs
              </button>
              <button onClick={bulkSyncClickUp} disabled={bulkBusy} data-testid="bulk-sync-clickup-btn"
                title="Pull real status + address from ClickUp for the selected WOs"
                className="h-9 px-3 bg-[#0b1c2d] text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-slate-800 disabled:opacity-60">
                <ArrowsClockwise weight="bold" size={14}/> Sync CU
              </button>
              <button onClick={bulkCombinedReport} disabled={bulkBusy} data-testid="bulk-combined-report-btn"
                title="One clean PDF grouped by unit — perfect for property manager updates"
                className="h-9 px-3 bg-purple-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-purple-700 disabled:opacity-60">
                <FilePdf weight="bold" size={14}/> Combined Report
              </button>
              <button onClick={bulkEmailPdfs} disabled={bulkBusy} data-testid="bulk-pdf-email-btn"
                title="Email every selected work order PDF to one address"
                className="h-9 px-3 bg-indigo-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-indigo-700 disabled:opacity-60">
                <Envelope weight="bold" size={14}/> Email PDFs
              </button>
              <button onClick={() => bulkQuickClose("canceled")} disabled={bulkBusy} data-testid="bulk-quick-cancel-btn"
                title="Cancel selected jobs and drop them in Needs Docs"
                className="h-9 px-3 bg-slate-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-slate-700 disabled:opacity-60">
                <XCircle weight="bold" size={14}/> Cancel Selected
              </button>
              <button onClick={bulkDelete} disabled={bulkBusy} data-testid="bulk-delete-btn"
                className="h-9 px-3 bg-red-500 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-red-600 disabled:opacity-60">
                <Trash weight="bold" size={14}/> Delete Selected
              </button>
            </div>
          )}
          {bulkAssignOpen && (
            <BulkAssignModal
              jobIds={Array.from(selected)}
              onClose={() => setBulkAssignOpen(false)}
              onDone={() => { clearSelection(); load(); }}
            />
          )}
          {combineOpen && (
            <CombineWosModal
              selectedJobs={jobs.filter(j => selected.has(j.id))}
              onClose={() => setCombineOpen(false)}
              onSaved={(parentId) => {
                setCombineOpen(false);
                clearSelection();
                if (parentId) nav(`/admin/jobs/${parentId}`);
                else load();
              }}
            />
          )}
        </div>

        {/* Sort + filter chips row */}
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="jobs-sort-filter-row">
          {/* Sort dropdown */}
          <div className="inline-flex items-center gap-1 h-9 border-2 border-slate-300 bg-white pl-3 pr-1"
            data-testid="jobs-sort-wrapper">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Sort</span>
            <select
              value={sortBy}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                if (e.target.value === "newest") next.delete("sort"); else next.set("sort", e.target.value);
                setSearchParams(next);
              }}
              data-testid="jobs-sort-select"
              className="h-8 pl-1 pr-2 text-xs font-black bg-transparent focus:outline-none">
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="address">Address (A-Z)</option>
              <option value="title">Title (A-Z)</option>
              <option value="status">Status</option>
              <option value="tech">Tech (A-Z)</option>
            </select>
          </div>

          {/* Age filter chips */}
          <div className="inline-flex border-2 border-slate-300 bg-white p-0.5" data-testid="jobs-age-filter">
            {[
              { key: "", label: "Any age" },
              { key: "today", label: "Today" },
              { key: "week", label: "7d" },
              { key: "month", label: "30d" },
              { key: "older", label: "30d+" },
            ].map(b => (
              <button
                key={b.key}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  if (b.key) next.set("age", b.key); else next.delete("age");
                  setSearchParams(next);
                }}
                data-testid={`jobs-age-${b.key || "any"}`}
                className={`h-7 px-2 text-[11px] font-black uppercase tracking-widest ${
                  ageBucket === b.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
                {b.label}
              </button>
            ))}
          </div>

          {/* Tech filter — MULTI-select (Ciarra ask). Tap a chip to add/remove.
              +N More opens a picker sheet for the long tail. Empty = "any tech". */}
          {workers.length > 0 && (
            <TechChipRow
              workers={workers}
              selected={techFilterSet}
              onToggle={(name) => {
                const next = new URLSearchParams(searchParams);
                const set = new Set(techFilterSet);
                const key = (name || "").toLowerCase();
                if (set.has(key)) set.delete(key); else set.add(key);
                if (set.size === 0) next.delete("tech");
                else next.set("tech", Array.from(set).join(","));
                setSearchParams(next);
              }}
              onClear={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("tech");
                setSearchParams(next);
              }}
            />
          )}

          {/* Clear-all when any filter/sort is active */}
          {(sortBy !== "newest" || techFilter || ageBucket) && (
            <button
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("sort"); next.delete("tech"); next.delete("age");
                setSearchParams(next);
              }}
              data-testid="jobs-clear-filters"
              className="h-9 px-3 border-2 border-slate-300 bg-white text-slate-700 font-black uppercase text-[10px] tracking-widest hover:bg-slate-100 inline-flex items-center gap-1">
              <X size={12}/> Clear filters
            </button>
          )}

          <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500"
            data-testid="jobs-visible-count">
            {visibleJobs.length} of {jobs.length}
          </div>
        </div>

        <div className="mt-4 space-y-2" data-testid="jobs-card-list">
          {/* Bulk-select header */}
          <div className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-200 rounded-lg">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
              onChange={toggleAllVisible}
              data-testid="bulk-select-all"
              aria-label="Select all visible jobs"
              className="w-4 h-4 accent-cyan-600 cursor-pointer"
            />
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">
              Select all · {visibleJobs.length} shown
            </span>
            {groupByZip && (
              <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-cyan-700 bg-cyan-50 px-2 py-1 rounded">
                Grouped by ZIP
              </span>
            )}
          </div>

          {visibleJobs.map(j => (
            <JobCard
              key={j.id}
              job={j}
              selected={selected.has(j.id)}
              onToggle={toggleOne}
              onQuickClose={quickClose}
              onWoFixed={load}
            />
          ))}

          {visibleJobs.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500 font-bold" data-testid="jobs-empty-state">
              {jobs.length === 0
                ? "No jobs. Click \"New Job\" to create one."
                : (q ? `No jobs match "${query}".` : "No jobs match this filter.")}
            </div>
          )}
        </div>

        {open && <NewJobModal workers={workers} initialProperty={searchParams.get("property") || ""} onClose={() => setOpen(false)} onCreated={(j) => { setOpen(false); load(); nav(`/admin/jobs/${j.id}`); }}/>}
        {csvOpen && <CsvImportModal onClose={() => setCsvOpen(false)} onImported={() => { setCsvOpen(false); load(); }}/>}
        {bulkOpen && <BulkImportModal workers={workers} onClose={() => setBulkOpen(false)} onCreated={() => { setBulkOpen(false); load(); }}/>}
      </div>
    </AdminShell>
  );
}

function VendorSuggestions({ text }) {
  const [suggs, setSuggs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!text || text.trim().length < 4) { setSuggs([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/vendors/suggest?text=${encodeURIComponent(text)}`);
        setSuggs(r.data?.vendors || []);
      } catch { setSuggs([]); }
      finally { setLoading(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [text]);

  if (dismissed || (!loading && suggs.length === 0)) return null;

  const SPEC_COLORS = {
    "HVAC": "bg-cyan-600", "Plumbing": "bg-blue-600", "Drain Cleaning": "bg-blue-500",
    "Service Line Insurance": "bg-indigo-600", "Water Heater": "bg-sky-600",
    "Electrical": "bg-amber-500", "Roofing": "bg-orange-600", "Garage Door": "bg-yellow-600",
    "Fire Safety": "bg-red-700", "Emergency Response": "bg-red-600",
    "Pest Control": "bg-lime-700", "Landscaping": "bg-green-700",
    "Appliance": "bg-fuchsia-600", "Cleaning": "bg-teal-600",
    "Concrete / Masonry": "bg-stone-600", "Property Mgmt": "bg-purple-600",
    "General Contractor": "bg-slate-700", "In-House Crew": "bg-emerald-600",
    "Painting": "bg-pink-600", "Locksmith": "bg-neutral-700",
  };

  return (
    <div className="p-3 border-2 border-[#00C4CC] bg-cyan-50" data-testid="vendor-suggestions">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-black uppercase tracking-widest text-[#0e7490] inline-flex items-center gap-1.5">
          💡 Suggested Vendors <span className="text-slate-500 font-medium normal-case tracking-normal">(from history — pick manually)</span>
        </div>
        <button type="button" onClick={() => setDismissed(true)} data-testid="vendor-suggestions-dismiss"
          className="text-[10px] font-black uppercase text-slate-500 hover:text-[#0f172a]">Hide</button>
      </div>
      {loading && suggs.length === 0 ? (
        <div className="mt-2 text-xs text-slate-500 font-medium">Scanning history…</div>
      ) : (
        <div className="mt-2 space-y-1.5">
          {suggs.map(v => (
            <div key={v.vendor} className="flex items-start gap-3 p-2 bg-white border border-slate-300 rounded-sm"
                 data-testid={`vendor-sugg-${v.vendor.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-black text-sm truncate">{v.vendor}</span>
                  <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-white rounded-sm ${SPEC_COLORS[v.specialty] || "bg-violet-500"}`}>
                    {v.specialty}
                  </span>
                  <span className={`text-[10px] font-mono font-black ${v.completion_rate >= 80 ? "text-emerald-700" : v.completion_rate >= 50 ? "text-amber-600" : "text-red-600"}`}>
                    {v.completion_rate}% · {v.total} WO
                  </span>
                </div>
                {v.hint && (
                  <div className="mt-1 text-[11px] font-medium text-indigo-700 bg-indigo-50 border-l-4 border-indigo-500 px-2 py-1 rounded-sm">
                    <span className="font-black uppercase text-[9px] tracking-widest">Insurance Tip:</span> {v.hint}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewJobModal({ workers, onClose, onCreated, initialProperty = "" }) {
  const { tenant } = useTenantCtx();
  const turnsEnabled = tenant?.turns_enabled !== false;
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/sub-contractors").then(r => setSubs(r.data)).catch(() => {}); }, []);
  const [f, setF] = useState({ title: "", description: "", property_address: initialProperty, customer_name: "", customer_phone: "",
                                assigned_to: "", helper_id: "", assigned_sub_id: "", priority: "normal", entry_code: "", lockbox_code: "", entry_notes: "", scheduled_for: "", work_order_number: "",
                                is_turn: false, turn_flat_price: "", turn_price_tbd: false,
                                rc_bedrooms: "", rc_bathrooms: "", rc_half_baths: "",
                                has_kitchen: true, has_living: true, has_dining: false,
                                has_hallway: false, has_stairs: false,
                                has_basement: false, has_garage: false, has_yard: false,
                                has_deck: false, has_porch: false,
                                has_laundry: false, has_utility: false, has_office: false,
                                has_foyer: false, has_attic: false, has_pantry: false, has_closet: false });
  const [seedAppliances, setSeedAppliances] = useState(new Set(["Refrigerator", "Range / Stove", "Dishwasher"]));
  const [checklist, setChecklist] = useState([""]);
  const [propertyMemoryHit, setPropertyMemoryHit] = useState(false);

  // AI Parse a work order (paste text or upload up to 4 screenshots)
  const [parseOpen, setParseOpen] = useState(false);
  const [parseText, setParseText] = useState("");
  const [parseFiles, setParseFiles] = useState([]); // array of File
  const [parseBusy, setParseBusy] = useState(false);
  const [parseConfidence, setParseConfidence] = useState(null);
  const parseFileInputRef = useRef(null);

  const runParse = async () => {
    if (!parseText.trim() && parseFiles.length === 0) {
      toast.error("Paste the work order or attach at least one screenshot first.");
      return;
    }
    setParseBusy(true);
    const t = toast.loading("Reading the work order…");
    try {
      const fd = new FormData();
      if (parseText.trim()) fd.append("text", parseText.trim());
      parseFiles.forEach(f => fd.append("images", f));
      const r = await api.post("/jobs/parse-work-order", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const p = r.data?.parsed || {};
      setF(prev => ({
        ...prev,
        title: p.title || prev.title,
        description: p.description || prev.description,
        property_address: p.property_address || prev.property_address,
        unit: p.unit || prev.unit,
        customer_name: p.customer_name || prev.customer_name,
        customer_phone: p.customer_phone || prev.customer_phone,
        work_order_number: p.work_order_number || prev.work_order_number,
        priority: p.priority || prev.priority,
        entry_code: p.entry_code || prev.entry_code,
        lockbox_code: p.lockbox_code || prev.lockbox_code,
        entry_notes: p.entry_notes || prev.entry_notes,
      }));
      // AI-suggested checklist merges with any existing entries (drops empty rows first)
      if (Array.isArray(p.checklist) && p.checklist.length > 0) {
        setChecklist(prev => {
          const existing = (prev || []).map(s => (s || "").trim()).filter(Boolean);
          return [...existing, ...p.checklist];
        });
      }
      setParseConfidence(p.confidence || "medium");
      toast.success("Fields filled — review below and edit anything that's off.", { id: t });
      setParseOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI couldn't read that work order.", { id: t });
    } finally {
      setParseBusy(false);
    }
  };

  // Property memory: when Turn is on + we have an address, load the saved room config
  useEffect(() => {
    if (!f.is_turn || !(f.property_address || "").trim()) { setPropertyMemoryHit(false); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/property-config?address=${encodeURIComponent(f.property_address)}`);
        if (r.data?.found && r.data?.room_config) {
          const rc = r.data.room_config;setF(prev => ({ ...prev,
            rc_bedrooms: rc.bedrooms ?? "", rc_bathrooms: rc.bathrooms ?? "", rc_half_baths: rc.half_baths ?? "",
            has_kitchen: !!rc.has_kitchen, has_living: !!rc.has_living, has_dining: !!rc.has_dining,
            has_hallway: !!rc.has_hallway, has_stairs: !!rc.has_stairs,
            has_basement: !!rc.has_basement, has_garage: !!rc.has_garage, has_yard: !!rc.has_yard,
            has_deck: !!rc.has_deck, has_porch: !!rc.has_porch,
            has_laundry: !!rc.has_laundry, has_utility: !!rc.has_utility, has_office: !!rc.has_office,
            has_foyer: !!rc.has_foyer, has_attic: !!rc.has_attic, has_pantry: !!rc.has_pantry, has_closet: !!rc.has_closet,
          }));
          setPropertyMemoryHit(true);
        } else setPropertyMemoryHit(false);
      } catch { setPropertyMemoryHit(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [f.is_turn, f.property_address]);

  // When "This is a Turn" flips on, auto-populate checklist from template (if empty)
  useEffect(() => {
    if (f.is_turn && checklist.filter(x => x && x.trim()).length === 0) {
      setChecklist([
        "Walk-through — document damage with photos",
        "Paint walls",
        "Paint ceilings",
        "Drywall repair / patch holes",
        "Full unit deep clean",
        "Kitchen — appliances, cabinets, counters",
        "Bathrooms — tub, toilet, vanity, tile grout",
        "Blinds — clean or replace",
        "Carpet — clean or replace",
        "Replace HVAC filter",
        "Smoke alarm batteries + test",
        "Change locks / re-key",
        "Final walk-through with photos",
      ]);
    }
  }, [f.is_turn]);
  const [busy, setBusy] = useState(false);
  const [estOpen, setEstOpen] = useState(false);
  const [rec, setRec] = useState({ enabled: false, cadence: "weekly", weekday: 1, day_of_month: 1 });
  const [history, setHistory] = useState({ count: 0, jobs: [] });
  const historyReq = useRef(0);

  // Prefill from Office AI Help ("Turn thread into WO")
  const [prefilledFrom, setPrefilledFrom] = useState("");
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("fr_new_job_prefill");
      if (raw) {
        const p = JSON.parse(raw);
        setF(prev => ({
          ...prev,
          title: p.title || prev.title,
          description: p.description || prev.description,
          category: p.category || prev.category,
          priority: p.priority || prev.priority,
        }));
        setPrefilledFrom(p.source_thread_id || "ai-help");
        sessionStorage.removeItem("fr_new_job_prefill");
        toast.success("Pre-filled from Office AI Help thread");
      }
    } catch { /* silent */ }
    // eslint-disable-next-line
  }, []);

  // AUTO-TRIAGE on first photo drop — new-job form only
  const [photos, setPhotos] = useState([]);              // File[]
  const [triage, setTriage] = useState(null);            // pre-triage envelope
  const [triageBusy, setTriageBusy] = useState(false);
  const [triageApplied, setTriageApplied] = useState(false);
  const photoInputRef = useRef(null);

  const runPreTriage = async (filesForFetch) => {
    setTriageBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", f.title || "");
      fd.append("description", f.description || "");
      fd.append("property_address", f.property_address || "");
      (filesForFetch || []).slice(0, 6).forEach(fl => fd.append("files", fl));
      const r = await api.post("/jobs/pre-triage", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setTriage(r.data);
      setTriageApplied(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI triage failed");
    } finally { setTriageBusy(false); }
  };

  const addPhotos = (newFiles) => {
    const picks = Array.from(newFiles || []).filter(x => x.type?.startsWith("image/"));
    if (!picks.length) return;
    const merged = [...photos];
    const seen = new Set(merged.map(x => x.name + x.size));
    for (const p of picks) {
      const k = p.name + p.size;
      if (!seen.has(k)) { seen.add(k); merged.push(p); }
    }
    setPhotos(merged);
    // Fire triage on the FIRST photo added (or when adding photos in a batch)
    if (photos.length === 0 || !triage) {
      runPreTriage(merged);
    }
  };

  const removePhoto = (idx) => setPhotos(photos.filter((_, i) => i !== idx));

  const applyTriage = () => {
    if (!triage) return;
    const urgencyToPriority = { normal: "normal", same_day: "high", emergency: "urgent" };
    setF(prev => ({
      ...prev,
      title: prev.title.trim() || (triage.summary || "Maintenance request").slice(0, 120),
      description: prev.description.trim()
        ? prev.description
        : `${triage.summary}${triage.prep_notes ? "\n\n" + triage.prep_notes : ""}`.trim(),
      priority: urgencyToPriority[triage.urgency] || "normal",
    }));
    setTriageApplied(true);
    toast.success(`Applied · ${triage.category} · ${triage.urgency.replace("_", "-")}`);
  };

  useEffect(() => {
    const addr = f.property_address?.trim();
    if (!addr || addr.length < 5) { setHistory({ count: 0, jobs: [] }); return; }
    const seq = ++historyReq.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/history/at-address?address=${encodeURIComponent(addr)}`);
        if (seq === historyReq.current) setHistory(data);
      } catch { /* ignore */ }
    }, 600);
    return () => clearTimeout(t);
  }, [f.property_address]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...f, checklist: checklist.map(s => s.trim()).filter(Boolean) };
      payload.turn_flat_price = Number(payload.turn_flat_price) || 0;
      if (payload.is_turn) {
        payload.room_config = {
          bedrooms: Number(payload.rc_bedrooms) || 0,
          bathrooms: Number(payload.rc_bathrooms) || 0,
          half_baths: Number(payload.rc_half_baths) || 0,
          has_kitchen: !!payload.has_kitchen, has_living: !!payload.has_living,
          has_dining: !!payload.has_dining,
          has_hallway: !!payload.has_hallway, has_stairs: !!payload.has_stairs,
          has_basement: !!payload.has_basement,
          has_garage: !!payload.has_garage, has_yard: !!payload.has_yard,
          has_deck: !!payload.has_deck, has_porch: !!payload.has_porch,
          has_laundry: !!payload.has_laundry, has_utility: !!payload.has_utility,
          has_office: !!payload.has_office, has_foyer: !!payload.has_foyer,
          has_attic: !!payload.has_attic, has_pantry: !!payload.has_pantry,
          has_closet: !!payload.has_closet,
          custom_rooms: [],
        };
        payload.seed_appliances = Array.from(seedAppliances);
      }
      ["rc_bedrooms", "rc_bathrooms", "rc_half_baths", "has_kitchen", "has_living", "has_dining",
       "has_hallway", "has_stairs",
       "has_basement", "has_garage", "has_yard", "has_deck", "has_porch",
       "has_laundry", "has_utility", "has_office", "has_foyer", "has_attic", "has_pantry", "has_closet"]
       .forEach(k => delete payload[k]);
      delete payload._picked_coord;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.helper_id) delete payload.helper_id;
      if (payload.helper_id && payload.helper_id === payload.assigned_to) {
        toast.error("Lead and helper can't be the same person");
        setBusy(false); return;
      }
      if (rec.enabled) {
        const r = { cadence: rec.cadence, active: true };
        if (rec.cadence === "weekly" || rec.cadence === "biweekly") r.weekday = Number(rec.weekday);
        if (rec.cadence === "monthly") r.day_of_month = Number(rec.day_of_month);
        payload.recurrence = r;
      }
      const { data } = await api.post("/jobs", payload);
      // If user picked an autocomplete result, set the geocoded coord immediately
      if (f._picked_coord && data.id) {
        try {
          await api.post(`/jobs/${data.id}/set-location?lat=${f._picked_coord.lat}&lng=${f._picked_coord.lng}`);
        } catch (err) { console.error("set-location failed after job create", err); }
      }
      // Upload any attached photos (from New Job form), then persist the AI triage.
      if (photos.length > 0 && data.id) {
        toast.loading(`Uploading ${photos.length} photo${photos.length === 1 ? "" : "s"}…`, { id: "photo-upload" });
        for (const p of photos) {
          try {
            const fd = new FormData();
            fd.append("file", p);
            await api.post(`/jobs/${data.id}/photos?kind=before`, fd, { headers: { "Content-Type": "multipart/form-data" }});
          } catch (err) { console.error("photo upload failed", err); }
        }
        toast.dismiss("photo-upload");
        // Fire the persistent /ai-triage now that photos are on the actual job.
        // Fire-and-forget — we don't block the redirect on Claude.
        api.post(`/jobs/${data.id}/ai-triage`).catch(() => { /* silent, user can re-run from JobDetail */ });
      }
      toast.success("Job created");
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6 my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <h3 className="font-display font-black text-2xl">New Job</h3>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            <button type="button" onClick={() => setParseOpen(v => !v)} data-testid="ai-parse-toggle-btn"
              className="h-8 px-2.5 bg-gradient-to-r from-[#0f172a] to-[#00C4CC] text-white font-black uppercase text-[10px] sm:text-[11px] tracking-wider inline-flex items-center gap-1 hover:opacity-90 whitespace-nowrap">
              <Sparkle weight="fill" size={12}/> <span className="sm:hidden">Parse</span><span className="hidden sm:inline">AI Parse WO</span>
            </button>
            <button type="button" onClick={() => setEstOpen(true)} data-testid="new-job-estimate-btn"
              className="h-8 px-2.5 bg-gradient-to-r from-violet-600 to-[#00C4CC] text-white font-black uppercase text-[10px] sm:text-[11px] tracking-wider inline-flex items-center gap-1 hover:opacity-90 whitespace-nowrap">
              <Sparkle weight="fill" size={12}/> <span className="sm:hidden">Est</span><span className="hidden sm:inline">AI Estimate</span>
            </button>
            <button type="button" onClick={onClose} data-testid="new-job-close-btn" className="p-1"><X weight="bold" size={22}/></button>
          </div>
        </div>
        {parseOpen && (
          <div className="mt-4 rugged-card p-4 bg-cyan-50 border-[#00C4CC]" data-testid="ai-parse-panel">
            <div className="flex items-center gap-2 text-[#0e7490]">
              <Sparkle weight="fill" size={16}/>
              <span className="text-xs font-black uppercase tracking-widest">AI Parse a work order</span>
            </div>
            <p className="mt-1 text-xs text-slate-700 font-medium">
              Paste the WO text (email / SMS / AppFolio) and/or drop a screenshot. AI fills in title, address, customer, WO#, and priority — you review before creating.
            </p>
            <textarea
              rows={4}
              value={parseText}
              onChange={(e) => setParseText(e.target.value)}
              data-testid="ai-parse-text"
              placeholder="Paste the work order here — email body, tenant SMS, whatever you got…"
              className="mt-2 w-full px-2 py-1.5 border-2 border-[#0f172a] text-sm font-medium resize-none bg-white"
            />
            <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                ref={parseFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const picks = Array.from(e.target.files || []);
                  setParseFiles(prev => {
                    const seen = new Set(prev.map(f => f.name + f.size));
                    const merged = [...prev];
                    for (const p of picks) {
                      const k = p.name + p.size;
                      if (!seen.has(k) && merged.length < 4) { seen.add(k); merged.push(p); }
                    }
                    return merged;
                  });
                  if (parseFileInputRef.current) parseFileInputRef.current.value = "";
                }}
                data-testid="ai-parse-file"
                className="hidden"
              />
              <button type="button" onClick={() => parseFileInputRef.current?.click()}
                data-testid="ai-parse-attach-btn"
                className="h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white hover:bg-[#0f172a] hover:text-white transition-colors inline-flex items-center gap-2">
                <Camera weight="bold" size={14}/>
                {parseFiles.length === 0 ? "Attach screenshots" : `Add more (${parseFiles.length}/4)`}
              </button>
              <div className="flex-1"/>
              <button type="button" onClick={runParse} disabled={parseBusy}
                data-testid="ai-parse-run-btn"
                className="h-10 px-4 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-2 hover:bg-[#00a8af] disabled:opacity-60">
                <Sparkle weight="fill" size={14}/> {parseBusy ? "Reading…" : "Parse & fill form"}
              </button>
            </div>
            {parseFiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="ai-parse-file-list">
                {parseFiles.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 h-7 px-2 bg-white border-2 border-[#0f172a] text-xs font-bold" data-testid={`ai-parse-file-chip-${i}`}>
                    <Camera weight="bold" size={12}/>
                    <span className="max-w-[140px] truncate">{f.name}</span>
                    <button type="button" onClick={() => setParseFiles(fs => fs.filter((_, j) => j !== i))}
                      aria-label="Remove screenshot"
                      className="ml-1 text-slate-500 hover:text-red-500">
                      <X weight="bold" size={12}/>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {parseConfidence && !parseOpen && (
          <div className="mt-3 rugged-card p-2.5 bg-cyan-50 border-[#00C4CC] flex items-center gap-2" data-testid="ai-parse-confidence">
            <Sparkle weight="fill" size={14} className="text-[#0e7490]"/>
            <span className="text-[11px] font-black uppercase tracking-widest text-[#0e7490]">
              AI filled fields · confidence: {parseConfidence}
            </span>
            <span className="text-[11px] text-slate-600 font-medium">Review anything highlighted and edit before creating.</span>
          </div>
        )}
        {estOpen && (
          <EstimatorModal
            initialTitle={f.title}
            initialDescription={f.description}
            jobId={null}
            onClose={() => setEstOpen(false)}
          />
        )}
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="new-job-form">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Title" required><input required value={f.title} onChange={upd("title")} data-testid="job-title" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
            </div>
            <Field label="Work Order #"><input value={f.work_order_number} onChange={upd("work_order_number")} placeholder="WO-1234"
              data-testid="job-wo-number" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono text-sm"/></Field>
          </div>
          <Field label="Property Address" required>
            <AddressAutocomplete
              value={f.property_address}
              onChange={(v) => setF(prev => ({ ...prev, property_address: v }))}
              onPick={(r) => setF(prev => ({ ...prev, property_address: r.display_name, _picked_coord: r.lat != null ? { lat: r.lat, lng: r.lng } : prev._picked_coord }))}
              testId="job-address"
            />
          </Field>
          <Field label="Unit / Apt (multi-family)">
            <input value={f.unit} onChange={upd("unit")} placeholder="A · 3B · 101 · leave blank if single-family"
              data-testid="job-unit" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
          </Field>
          {history.count > 0 && (
            <div className="rugged-card p-3 bg-yellow-50 border-yellow-500" data-testid="job-history-warning">
              <div className="flex items-center gap-2">
                <Warning weight="fill" size={20} className="text-yellow-600 flex-shrink-0"/>
                <div className="font-black uppercase text-xs tracking-wider text-yellow-800">
                  Repeat property — {history.count} prior job{history.count === 1 ? "" : "s"} at this address
                </div>
              </div>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {history.jobs.slice(0, 5).map(h => (
                  <div key={h.id} className="text-xs flex items-center justify-between gap-2 py-1 border-t border-yellow-200 first:border-t-0">
                    <span className="font-bold truncate">{h.title}</span>
                    <span className="text-slate-500 font-mono uppercase text-[10px] flex-shrink-0">
                      {(h.status || "").replace("_", " ")} · {h.created_at ? new Date(h.created_at).toLocaleDateString() : "?"}
                    </span>
                  </div>
                ))}
              </div>
              {history.count >= 3 && (
                <div className="mt-2 text-[11px] font-bold text-yellow-800 italic">
                  💡 Recurring pattern — consider quoting a full inspection / maintenance contract.
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer Name"><input value={f.customer_name} onChange={upd("customer_name")} data-testid="job-customer" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
            <Field label="Customer Phone"><input value={f.customer_phone} onChange={upd("customer_phone")} data-testid="job-phone" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lead Tech">
              <select value={f.assigned_to} onChange={upd("assigned_to")} data-testid="job-assign" className="w-full h-11 px-2 border-2 border-[#0f172a] bg-white">
                <option value="">— Unassigned —</option>
                {workers.map(w => <option key={w.id} value={w.id}>{w.name}{w.role === "admin" ? " (office)" : ""}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select value={f.priority} onChange={upd("priority")} data-testid="job-priority" className="w-full h-11 px-2 border-2 border-[#0f172a] bg-white">
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </Field>
          </div>
          <Field label="Helper (optional — 2-man crew)">
            <select value={f.helper_id} onChange={upd("helper_id")} data-testid="job-helper" className="w-full h-11 px-2 border-2 border-[#0f172a] bg-white">
              <option value="">— No helper —</option>
              {workers.filter(w => w.id !== f.assigned_to).map(w => <option key={w.id} value={w.id}>{w.name}{w.role === "admin" ? " (office)" : ""}</option>)}
            </select>
          </Field>
          {subs.length > 0 && (
            <div className="rugged-card p-3 bg-cyan-50 border-[#00C4CC]" data-testid="sub-assign-block">
              <div className="text-[10px] font-black uppercase tracking-widest text-[#0e7490] mb-1">Or hand it off to a sub-contractor</div>
              <select value={f.assigned_sub_id} onChange={upd("assigned_sub_id")} data-testid="job-assign-sub"
                className="w-full h-11 px-2 border-2 border-[#0f172a] bg-white font-bold">
                <option value="">— No sub — use my crew above</option>
                {subs.map(s => <option key={s.id} value={s.id}>{s.name}{s.trade ? ` · ${s.trade}` : ""}</option>)}
              </select>
              {f.assigned_sub_id && (
                <div className="mt-2 text-xs text-slate-700 font-medium leading-snug">
                  When you save, we'll email this sub the job details + a link to their private portal. Timer & GPS won't apply.
                </div>
              )}
            </div>
          )}
          <Field label="Description"><textarea value={f.description} onChange={upd("description")} rows={3} data-testid="job-desc" className="w-full px-3 py-2 border-2 border-[#0f172a]"/></Field>
          <VendorSuggestions text={`${f.title || ""} ${f.description || ""}`}/>
          {turnsEnabled && (
            <div className={`p-3 border-2 ${f.is_turn ? "border-[#0f172a] bg-amber-50" : "border-slate-300 bg-white"} rounded-lg`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!f.is_turn} data-testid="job-is-turn"
                  onChange={e => setF(prev => ({ ...prev, is_turn: e.target.checked }))}
                  className="h-5 w-5 accent-[#0f172a]"/>
                <span className="font-black uppercase text-sm tracking-wider">This is a Turn</span>
                <span className="text-xs text-slate-500 font-medium">(unit turnover — deep clean, paint, repairs)</span>
              </label>
              {f.is_turn && (
                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="job-turn-price-tbd-label">
                    <input type="checkbox" checked={!!f.turn_price_tbd} data-testid="job-turn-price-tbd"
                      onChange={e => setF(prev => ({ ...prev, turn_price_tbd: e.target.checked, turn_flat_price: e.target.checked ? "" : prev.turn_flat_price }))}
                      className="h-4 w-4 accent-[#0f172a]"/>
                    <span className="text-xs font-black uppercase tracking-widest">Price N/A — sub will quote</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Flat Price ($)">
                      <input type="number" step="0.01" min="0" value={f.turn_flat_price}
                        disabled={!!f.turn_price_tbd}
                        onChange={upd("turn_flat_price")} placeholder={f.turn_price_tbd ? "TBD" : "1500.00"} data-testid="job-turn-price"
                        className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono disabled:bg-slate-100 disabled:text-slate-400"/>
                    </Field>
                    <div className="text-xs text-slate-600 font-medium self-center">
                      Only office/sub sees the price. A default Turn checklist auto-populates below.
                    </div>
                  </div>
                </div>
              )}
              {f.is_turn && (
                <div className="mt-4 pt-4 border-t-2 border-amber-200">
                  <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-2">Unit Layout</div>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Bedrooms">
                      <input type="number" min="0" max="10" value={f.rc_bedrooms ?? ""} data-testid="job-rc-bedrooms"
                        onChange={e => setF(prev => ({ ...prev, rc_bedrooms: e.target.value === "" ? "" : Number(e.target.value) }))}
                        placeholder="3" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
                    </Field>
                    <Field label="Full Baths">
                      <input type="number" min="0" max="10" value={f.rc_bathrooms ?? ""} data-testid="job-rc-bathrooms"
                        onChange={e => setF(prev => ({ ...prev, rc_bathrooms: e.target.value === "" ? "" : Number(e.target.value) }))}
                        placeholder="2" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
                    </Field>
                    <Field label={<span title="Toilet + vanity, no shower">Half Baths</span>}>
                      <input type="number" min="0" max="10" value={f.rc_half_baths ?? ""} data-testid="job-rc-half-baths"
                        onChange={e => setF(prev => ({ ...prev, rc_half_baths: e.target.value === "" ? "" : Number(e.target.value) }))}
                        placeholder="0" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
                    </Field>
                  </div>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5 text-sm">
                    {[["has_kitchen", "Kitchen"], ["has_living", "Living Room"], ["has_dining", "Dining Room"],
                      ["has_hallway", "Hallway"], ["has_stairs", "Stairs"],
                      ["has_basement", "Basement"], ["has_garage", "Garage"], ["has_yard", "Yard"],
                      ["has_deck", "Deck"], ["has_porch", "Porch"],
                      ["has_laundry", "Laundry Room"], ["has_utility", "Utility / Mudroom"],
                      ["has_office", "Office"], ["has_foyer", "Foyer / Entry"],
                      ["has_attic", "Attic"], ["has_pantry", "Pantry"], ["has_closet", "Closet"]].map(([k, lbl]) => (
                      <label key={k} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!f[k]}
                          onChange={e => {
                            const on = e.target.checked;
                            setF(prev => ({ ...prev, [k]: on }));
                            const areaAppl = {
                              has_kitchen: ["Refrigerator", "Range / Stove", "Dishwasher", "Microwave", "Garbage Disposal"],
                              has_basement: ["Water Heater", "Furnace", "AC Condenser", "HVAC Air Handler"],
                              has_laundry: ["Washer", "Dryer"],
                              has_utility: ["Water Heater", "Furnace"],
                            }[k];
                            if (on && areaAppl) {
                              setSeedAppliances(prev => {
                                const next = new Set(prev);
                                areaAppl.forEach(a => next.add(a));
                                return next;
                              });
                            }
                          }}
                          data-testid={`job-rc-${k}`} className="h-4 w-4 accent-[#0f172a]"/>
                        <span className="font-medium text-xs">{lbl}</span>
                      </label>
                    ))}
                  </div>
                  {propertyMemoryHit && (
                    <div className="mt-2 text-xs text-emerald-700 font-black uppercase tracking-widest">
                      ✓ Loaded from previous turn at this address
                    </div>
                  )}

                  <div className="mt-4 p-3 border-2 border-[#00C4CC] bg-cyan-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] font-black uppercase tracking-widest text-slate-800">
                        Appliances Present ({seedAppliances.size})
                      </div>
                      <div className="text-[10px] text-slate-500 font-medium">Seeded — data-plate scan on-site</div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-sm">
                      {["Refrigerator", "Range / Stove", "Oven", "Microwave", "Dishwasher",
                        "Washer", "Dryer", "Water Heater", "Furnace", "AC Condenser",
                        "HVAC Air Handler", "Garbage Disposal"].map(t => {
                        const on = seedAppliances.has(t);
                        return (
                          <button key={t} type="button"
                            onClick={() => setSeedAppliances(prev => {
                              const next = new Set(prev);
                              if (next.has(t)) next.delete(t); else next.add(t);
                              return next;
                            })}
                            data-testid={`job-seed-appliance-${t.replace(/\W+/g, "-").toLowerCase()}`}
                            className={`h-9 px-2 border-2 font-black uppercase text-[10px] tracking-wider transition-all ${
                              on ? "border-[#00C4CC] bg-[#00C4CC] text-white shadow-[2px_2px_0px_#0f172a]"
                                 : "border-[#0f172a] bg-white text-[#0f172a] hover:bg-cyan-50"
                            }`}>
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Entry Code"><input value={f.entry_code || ""} onChange={upd("entry_code")} placeholder="1234#"
              data-testid="job-entry-code" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/></Field>
            <Field label="Lockbox Code"><input value={f.lockbox_code || ""} onChange={upd("lockbox_code")} placeholder="0000"
              data-testid="job-lockbox-code" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/></Field>
          </div>
          <Field label="Entry Notes"><input value={f.entry_notes || ""} onChange={upd("entry_notes")} placeholder="Front door key under mat, dog in backyard, etc."
            data-testid="job-entry-notes" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          <Field label="Scheduled Date">
            <input type="date" value={f.scheduled_for || ""} onChange={upd("scheduled_for")} data-testid="job-scheduled"
              className="w-full h-11 px-3 border-2 border-[#0f172a]"/>
          </Field>
          <Field label="Issues Checklist (each worker can check off as they fix)">
            <div className="space-y-2">
              {checklist.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <input value={c} onChange={e => { const n = [...checklist]; n[i] = e.target.value; setChecklist(n); }}
                    placeholder={`Issue #${i+1} (e.g., Leaky kitchen faucet)`}
                    data-testid={`checklist-input-${i}`}
                    className="flex-1 h-10 px-3 border-2 border-[#0f172a] text-sm"/>
                  {checklist.length > 1 && (
                    <button type="button" onClick={() => setChecklist(checklist.filter((_, idx) => idx !== i))}
                      className="px-3 border-2 border-red-500 text-red-500 font-bold text-sm">×</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setChecklist([...checklist, ""])} data-testid="add-checklist-row"
                className="text-xs font-black uppercase tracking-wider text-[#00C4CC] underline">+ Add another issue</button>
            </div>
          </Field>

          {/* Recurrence */}
          <div className="border-2 border-[#0f172a] p-3 bg-slate-50" data-testid="recurrence-section">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={rec.enabled}
                onChange={e => setRec({ ...rec, enabled: e.target.checked })}
                data-testid="recurrence-enabled"
                className="h-5 w-5 border-2 border-[#0f172a] accent-[#00C4CC]"/>
              <span className="font-black uppercase text-sm tracking-wider flex items-center gap-1">
                <Repeat weight="bold" size={16}/> Repeat this job
              </span>
            </label>
            {rec.enabled && (
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <select value={rec.cadence} onChange={e => setRec({ ...rec, cadence: e.target.value })}
                    data-testid="recurrence-cadence"
                    className="h-10 px-2 border-2 border-[#0f172a] text-sm font-medium bg-white">
                    <option value="daily">Every day</option>
                    <option value="weekly">Every week</option>
                    <option value="biweekly">Every 2 weeks</option>
                    <option value="monthly">Every month</option>
                  </select>
                  {(rec.cadence === "weekly" || rec.cadence === "biweekly") && (
                    <select value={rec.weekday} onChange={e => setRec({ ...rec, weekday: e.target.value })}
                      data-testid="recurrence-weekday"
                      className="h-10 px-2 border-2 border-[#0f172a] text-sm font-medium bg-white">
                      {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => (
                        <option key={i} value={i}>on {d}</option>
                      ))}
                    </select>
                  )}
                  {rec.cadence === "monthly" && (
                    <select value={rec.day_of_month} onChange={e => setRec({ ...rec, day_of_month: e.target.value })}
                      data-testid="recurrence-dom"
                      className="h-10 px-2 border-2 border-[#0f172a] text-sm font-medium bg-white">
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                        <option key={d} value={d}>on day {d}</option>
                      ))}
                    </select>
                  )}
                </div>
                <p className="text-[11px] font-medium text-slate-500">
                  A fresh copy of this job (title, address, checklist, entry info) will be created automatically on the next occurrence.
                </p>
              </div>
            )}
          </div>

          <button type="submit" disabled={busy} data-testid="job-submit" className="tactile-btn w-full h-12 bg-[#00C4CC] text-white font-black uppercase tracking-wide">{busy ? "Creating..." : "Create Job"}</button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-black uppercase tracking-wider">{label}{required && <span className="text-red-500"> *</span>}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function AddressAutocomplete({ value, onChange, onPick, testId }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const known = useKnownAddresses();
  const knownHits = matchKnownAddresses(known, q);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!q || q.length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geocode/search?q=${encodeURIComponent(q)}`);
        setResults(data);
      } catch (_) { setResults([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <input autoComplete="off"
        value={q}
        onChange={e => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        data-testid={testId}
        placeholder="Start typing an address…"
        className="w-full h-11 px-3 border-2 border-[#0f172a]"
      />
      {open && (results.length > 0 || knownHits.length > 0) && (
        <ul className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border-2 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] max-h-60 overflow-y-auto">
          {knownHits.map((a, i) => (
            <li key={`k${i}`} data-testid={`${testId}-known-${i}`}
              onMouseDown={() => { setQ(a); onChange(a); onPick({ display_name: a, known: true }); setOpen(false); }}
              className="px-3 py-2 hover:bg-emerald-50 bg-emerald-50/40 cursor-pointer text-sm font-bold border-b border-slate-200 flex items-center gap-2">
              <span className="flex-1">{a}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-700">Your property</span>
            </li>
          ))}
          {results.map((r, i) => (
            <li key={i} onMouseDown={() => { setQ(r.display_name); onPick(r); setOpen(false); }}
              className="px-3 py-2 hover:bg-cyan-50 cursor-pointer text-sm border-b border-slate-200">
              {r.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CsvImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/jobs/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setResult(data);
      const summary = [];
      if (data.created)       summary.push(`${data.created} new`);
      if (data.updated)       summary.push(`${data.updated} updated`);
      if (data.closed)        summary.push(`${data.closed} closed`);
      if (data.skipped_dupes) summary.push(`${data.skipped_dupes} skipped`);
      toast.success(summary.length ? `Sync complete · ${summary.join(" · ")}` : "Nothing to import");
      if ((data.errors || []).length === 0) setTimeout(onImported, 1500);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const downloadTemplate = () => {
    const csv = "title,description,property_address,customer_name,customer_phone,assigned_to_email,priority,scheduled_for\n" +
      "Leaky Faucet Repair,Kitchen sink drips,123 Main St,John Doe,555-1234,worker@fieldworkpro.com,normal,2026-08-01\n" +
      "AC Not Cooling,Upstairs bedroom unit warm,456 Oak Ave,Jane Smith,555-5678,,high,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "fieldwork-jobs-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-2xl">Import Jobs from CSV</h3>
          <button onClick={onClose}><X weight="bold" size={22}/></button>
        </div>
        <p className="mt-2 text-sm text-zinc-600 font-medium">
          Idempotent sync from AppFolio / Kraaft. Re-uploading is safe — WOs are matched by
          <code className="text-[11px] font-mono bg-zinc-100 px-1 py-0.5 rounded mx-1">Work Order Number</code>
          and updated in place. Rows marked <strong>Completed / Closed / Cancelled</strong> auto-close their Field Radar counterpart.
        </p>
        <div className="mt-3 p-3 bg-zinc-50 border-2 border-dashed border-[#0f172a] text-xs">
          <div className="font-black uppercase tracking-wider">AppFolio columns (auto-detected)</div>
          <code className="block mt-1 text-[11px]">Work Order Number, Property Name, Unit, Work Order Issue, Job Description, Status, Priority, Scheduled Start, Primary Resident, Vendor</code>
          <div className="mt-2 font-black uppercase tracking-wider">Or use the Field Radar template</div>
          <code className="block mt-1 text-[11px]">title, property_address, description, customer_name, customer_phone, assigned_to_email, priority, scheduled_for</code>
          <button onClick={downloadTemplate} data-testid="download-template-btn"
            className="mt-3 text-[#00C4CC] font-black uppercase text-xs underline">Download Template CSV</button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0])} data-testid="csv-file-input"
            className="w-full text-sm file:mr-3 file:px-4 file:py-2 file:border-2 file:border-[#0f172a] file:bg-white file:font-black file:uppercase file:text-xs file:tracking-wider file:cursor-pointer"/>
          {result && (
            <div className="p-3 border-2 border-[#0f172a] bg-[#f0fdf4]" data-testid="csv-result">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <div className="text-2xl font-black text-emerald-700">{result.created || 0}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">New</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-cyan-700">{result.updated || 0}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Updated</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-violet-700">{result.closed || 0}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Closed</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-zinc-500">{result.skipped_dupes || 0}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Same</div>
                </div>
              </div>
              {(result.errors || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-300 text-sm">
                  <div className="font-bold text-red-600">{result.errors.length} rows skipped:</div>
                  <ul className="mt-1 list-disc pl-4 text-xs">
                    {result.errors.slice(0, 5).map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
                    {result.errors.length > 5 && <li>… and {result.errors.length - 5} more</li>}
                  </ul>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-zinc-300">
                <Link to="/admin/csv-sync-history" data-testid="view-sync-history-link"
                  className="text-[#00C4CC] font-black uppercase text-xs tracking-wider underline">
                  View full sync history →
                </Link>
              </div>
            </div>
          )}
          <button type="submit" disabled={busy || !file} data-testid="csv-submit-btn"
            className="tactile-btn w-full h-12 bg-[#00C4CC] text-white font-black uppercase tracking-wider disabled:opacity-50">
            {busy ? "Importing..." : "Import Jobs"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ------ Recurrence manager (Admin Job Detail sidebar) ------
function RecurrenceManagerPanel({ job, onReload }) {
  const rec = job.recurrence;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    cadence: rec?.cadence || "weekly",
    weekday: rec?.weekday ?? 1,
    day_of_month: rec?.day_of_month ?? 1,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (rec) setForm({
      cadence: rec.cadence || "weekly",
      weekday: rec.weekday ?? 1,
      day_of_month: rec.day_of_month ?? 1,
    });
  }, [rec?.cadence, rec?.weekday, rec?.day_of_month]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { cadence: form.cadence, active: rec?.active ?? true };
      if (form.cadence === "weekly" || form.cadence === "biweekly") payload.weekday = Number(form.weekday);
      if (form.cadence === "monthly") payload.day_of_month = Number(form.day_of_month);
      await api.patch(`/jobs/${job.id}/recurrence`, payload);
      toast.success(rec ? "Schedule updated" : "Recurring schedule created");
      setEditing(false);
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save schedule"); }
    finally { setBusy(false); }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/recurrence/toggle`);
      toast.success(r.data.recurrence?.active ? "Recurring schedule resumed" : "Recurring schedule paused");
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the recurring schedule? Future occurrences will stop.")) return;
    setBusy(true);
    try {
      await api.delete(`/jobs/${job.id}/recurrence`);
      toast.success("Recurring schedule cleared");
      setEditing(false);
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const weekdayLabel = (w) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][w] || "";
  const summarize = (r) => {
    if (!r) return "";
    if (r.cadence === "daily") return "Every day";
    if (r.cadence === "weekly") return `Every week on ${weekdayLabel(r.weekday)}`;
    if (r.cadence === "biweekly") return `Every 2 weeks on ${weekdayLabel(r.weekday)}`;
    if (r.cadence === "monthly") return `Every month on day ${r.day_of_month}`;
    return r.cadence;
  };
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const EditForm = (
    <div className="mt-3 space-y-2" data-testid="recurrence-edit-form">
      <select value={form.cadence} onChange={e => setForm({ ...form, cadence: e.target.value })}
        data-testid="rec-mgr-cadence"
        className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-medium bg-white">
        <option value="daily">Every day</option>
        <option value="weekly">Every week</option>
        <option value="biweekly">Every 2 weeks</option>
        <option value="monthly">Every month</option>
      </select>
      {(form.cadence === "weekly" || form.cadence === "biweekly") && (
        <select value={form.weekday} onChange={e => setForm({ ...form, weekday: e.target.value })}
          data-testid="rec-mgr-weekday"
          className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-medium bg-white">
          {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => (
            <option key={i} value={i}>on {d}</option>
          ))}
        </select>
      )}
      {form.cadence === "monthly" && (
        <select value={form.day_of_month} onChange={e => setForm({ ...form, day_of_month: e.target.value })}
          data-testid="rec-mgr-dom"
          className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-medium bg-white">
          {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
            <option key={d} value={d}>on day {d}</option>
          ))}
        </select>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => { setEditing(false); }} disabled={busy}
          data-testid="rec-mgr-cancel-btn"
          className="h-10 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white disabled:opacity-50">
          Cancel
        </button>
        <button onClick={save} disabled={busy}
          data-testid="rec-mgr-save-btn"
          className="tactile-btn h-10 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider disabled:opacity-50">
          {busy ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );

  if (!rec) {
    return (
      <div className="mt-4" data-testid="recurrence-manager-empty">
        {editing ? (
          <div className="p-3 border-2 border-[#0f172a] bg-slate-50">
            <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider">
              <Repeat weight="bold" size={14}/> New Repeat Schedule
            </div>
            {EditForm}
          </div>
        ) : (
          <button onClick={() => setEditing(true)} data-testid="rec-mgr-setup-btn"
            className="w-full h-10 border-2 border-dashed border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white hover:bg-slate-50 inline-flex items-center justify-center gap-1.5">
            <Repeat weight="bold" size={14}/> Set Up Recurring Schedule
          </button>
        )}
      </div>
    );
  }

  const active = rec.active !== false;
  return (
    <div className="mt-4 p-3 border-2 border-[#0f172a] bg-slate-50" data-testid="recurrence-manager-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Repeat weight="bold" size={16} className={active ? "text-[#00C4CC]" : "text-slate-400"}/>
          <span className="text-xs font-black uppercase tracking-wider">Recurring</span>
          <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 ${
            active ? "bg-[#00C4CC] text-white" : "bg-slate-300 text-slate-700"
          }`} data-testid="recurrence-status-badge">
            {active ? "Active" : "Paused"}
          </span>
        </div>
      </div>

      {!editing ? (
        <>
          <div className="mt-2 text-sm font-bold" data-testid="recurrence-summary">{summarize(rec)}</div>
          <div className="mt-2 text-xs text-slate-600 space-y-0.5">
            <div><b>Next run:</b> <span data-testid="recurrence-next-run">{fmt(rec.next_run_at)}</span></div>
            {rec.last_spawned_at && <div><b>Last spawned:</b> {fmt(rec.last_spawned_at)}</div>}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            <button onClick={toggle} disabled={busy} data-testid="rec-mgr-toggle-btn"
              className="h-9 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-wider bg-white disabled:opacity-50">
              {active ? "Pause" : "Resume"}
            </button>
            <button onClick={() => setEditing(true)} disabled={busy} data-testid="rec-mgr-edit-btn"
              className="h-9 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-wider bg-white disabled:opacity-50">
              Edit
            </button>
            <button onClick={clear} disabled={busy} data-testid="rec-mgr-clear-btn"
              className="h-9 border-2 border-red-500 text-red-600 font-black uppercase text-[10px] tracking-wider bg-white disabled:opacity-50 hover:bg-red-500 hover:text-white transition-colors">
              Clear
            </button>
          </div>
        </>
      ) : EditForm}
    </div>
  );
}

// ------ Route completed / in-flight job to a sub for labor invoicing (Wyalusing-style fix)
function RouteToSubPanel({ job, onReload }) {
  const [subs, setSubs] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return;
    api.get("/sub-contractors").then(r => setSubs(r.data || [])).catch(() => {});
  }, [expanded]);
  const send = async () => {
    if (!pick) return toast.error("Pick a sub first");
    setBusy(true);
    const t = toast.loading("Routing to sub…");
    try {
      const r = await api.post(`/jobs/${job.id}/route-to-sub`, { sub_id: pick });
      toast.success(`Sent to ${r.data?.sub_name || "sub"} — they'll see it in their Ready-to-Invoice`, { id: t });
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Route failed", { id: t });
    } finally { setBusy(false); }
  };
  if (!expanded) {
    return (
      <button onClick={() => setExpanded(true)} data-testid="route-to-sub-open"
        className="mt-4 tactile-btn h-11 px-4 border-2 border-[#00C4CC] bg-white text-[#0f172a] font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-[#ecfeff]">
        <Wrench weight="bold" size={14}/> Send to Sub for Labor Invoice
      </button>
    );
  }
  return (
    <div className="mt-4 rugged-card p-3 bg-cyan-50 border-[#00C4CC]" data-testid="route-to-sub-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="h-6 px-2 bg-[#0f172a] text-[#00C4CC] font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Wrench weight="fill" size={12}/> Route to Sub
        </span>
        <span className="text-xs font-medium text-slate-600">Sub will see it in their portal → Ready-to-Invoice.</span>
      </div>
      <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)}
          data-testid="route-to-sub-select"
          className="flex-1 h-10 px-2 border-2 border-[#0f172a] bg-white text-sm font-bold">
          <option value="">— Pick a sub —</option>
          {subs.map(s => <option key={s.id} value={s.id}>{s.name}{s.company ? ` (${s.company})` : ""}{s.trade ? ` — ${s.trade}` : ""}</option>)}
        </select>
        <button onClick={send} disabled={busy || !pick} data-testid="route-to-sub-send"
          className="h-10 px-3 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-1 hover:bg-[#00a8af] disabled:opacity-60">
          <Envelope weight="fill" size={14}/> {busy ? "Sending…" : "Send"}
        </button>
        <button onClick={() => setExpanded(false)} data-testid="route-to-sub-cancel"
          className="h-10 px-2 text-xs font-black uppercase text-slate-500 hover:text-[#0f172a]">Cancel</button>
      </div>
    </div>
  );
}

// ------ Request Quote from a Sub for a move-out inspection / turn
function RequestSubQuotePanel({ job, onReload }) {
  const [subs, setSubs] = useState([]);
  const [pick, setPick] = useState("");
  const [summary, setSummary] = useState("");
  const [sizeLine, setSizeLine] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!expanded) return;
    api.get("/sub-contractors").then(r => setSubs(r.data || [])).catch(() => {});
  }, [expanded]);
  const draft = async () => {
    setDrafting(true);
    const t = toast.loading("AI drafting scope of work…");
    try {
      const r = await api.post(`/jobs/${job.id}/draft-sub-quote`);
      setSummary(r.data?.summary || "");
      setSizeLine(r.data?.size_line || "");
      toast.success(r.data?.used_ai ? "Draft ready — edit before sending" : "Draft ready (deterministic — no AI key)", { id: t });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Draft failed", { id: t });
    } finally { setDrafting(false); }
  };
  const send = async () => {
    if (!pick) return toast.error("Pick a sub first");
    if (!summary.trim()) return toast.error("Draft or type a summary first");
    setBusy(true);
    const t = toast.loading("Sending quote request…");
    try {
      const r = await api.post(`/jobs/${job.id}/request-sub-quote`, { sub_id: pick, summary });
      toast.success(`Quote request sent to ${r.data?.sub_name || "sub"}`, { id: t });
      setExpanded(false);
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed", { id: t });
    } finally { setBusy(false); }
  };
  if (!expanded) {
    return (
      <button onClick={() => setExpanded(true)} data-testid="request-sub-quote-open"
        className="mt-3 tactile-btn h-11 px-4 border-2 border-fuchsia-600 bg-white text-fuchsia-700 font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-fuchsia-50">
        <Sparkle weight="fill" size={14}/> Request Sub Quote (Move-Out Scope)
      </button>
    );
  }
  return (
    <div className="mt-4 rugged-card p-4 bg-fuchsia-50 border-fuchsia-500" data-testid="request-sub-quote-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="h-6 px-2 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Sparkle weight="fill" size={12}/> Request Sub Quote
        </span>
        <span className="text-xs font-medium text-slate-700">Sends inspection scope + property size to sub's portal + email.</span>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)}
          data-testid="rsq-sub-select"
          className="h-10 px-2 border-2 border-[#0f172a] bg-white text-sm font-bold">
          <option value="">— Pick a sub —</option>
          {subs.map(s => <option key={s.id} value={s.id}>{s.name}{s.company ? ` (${s.company})` : ""}{s.trade ? ` — ${s.trade}` : ""}</option>)}
        </select>
        <button onClick={draft} disabled={drafting} data-testid="rsq-draft"
          className="h-10 px-3 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-1 hover:bg-slate-800 disabled:opacity-60">
          <Sparkle weight="fill" size={14} className={drafting ? "animate-pulse text-[#00C4CC]" : ""}/> {drafting ? "Drafting…" : "AI Draft"}
        </button>
      </div>
      {sizeLine && (
        <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-600" data-testid="rsq-size-line">
          Property: {sizeLine}
        </div>
      )}
      <textarea value={summary} onChange={(e) => setSummary(e.target.value)}
        placeholder="Scope of work goes here — click AI Draft to populate from the inspection, then edit."
        rows={10} data-testid="rsq-summary"
        className="mt-2 w-full border-2 border-[#0f172a] p-2 text-sm font-medium bg-white focus:outline-none focus:ring-4 focus:ring-fuchsia-500/30"/>
      <div className="mt-3 flex gap-2">
        <button onClick={() => setExpanded(false)} data-testid="rsq-cancel"
          className="h-10 px-3 border-2 border-[#0f172a] bg-white font-black uppercase text-xs tracking-widest">Cancel</button>
        <button onClick={send} disabled={busy || !pick || !summary.trim()} data-testid="rsq-send"
          className="flex-1 h-10 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-60">
          <Envelope weight="fill" size={14}/> {busy ? "Sending…" : "Send Quote Request"}
        </button>
      </div>
    </div>
  );
}

// ------ Nearby jobs by ZIP — dispatch clustering helper
function NearbyJobsPanel({ jobId }) {
  const [state, setState] = useState({ loading: true, zip: null, street: null, jobs: [] });
  useEffect(() => {
    let alive = true;
    api.get(`/jobs/${jobId}/nearby`).then(r => { if (alive) setState({ loading: false, ...r.data }); })
      .catch(() => { if (alive) setState({ loading: false, zip: null, street: null, jobs: [] }); });
    return () => { alive = false; };
  }, [jobId]);
  if (state.loading) return null;
  const label = state.zip ? `ZIP ${state.zip}` : (state.street ? `${state.street.toUpperCase()} St` : null);
  if (!label) return null;
  if (state.jobs.length === 0) {
    return (
      <div className="mt-6 rugged-card p-4" data-testid="nearby-jobs-empty">
        <h3 className="font-display font-black text-lg uppercase inline-flex items-center gap-1.5">
          <MapPin weight="bold" size={16}/> Nearby · {label}
        </h3>
        <p className="mt-1 text-xs text-slate-500 font-medium">No other open jobs {state.zip ? "in this ZIP" : "on this street"} right now.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 rugged-card p-4" data-testid="nearby-jobs-panel">
      <h3 className="font-display font-black text-lg uppercase inline-flex items-center gap-1.5">
        <MapPin weight="bold" size={16}/> Nearby · {label}
        <span className="text-xs font-black uppercase tracking-widest text-slate-500">· {state.jobs.length}</span>
      </h3>
      <p className="mt-1 text-xs text-slate-500 font-medium">Open / assigned jobs {state.zip ? "in the same ZIP" : "on the same street"} — cluster the day.</p>
      <div className="mt-3 space-y-1.5">
        {state.jobs.map(nj => (
          <Link to={`/admin/jobs/${nj.id}`} key={nj.id} data-testid={`nearby-job-${nj.id}`}
            className="block p-2 border-2 border-transparent hover:border-[#00C4CC] hover:bg-[#ecfeff] transition-colors">
            <div className="text-xs font-black uppercase text-[#0f172a] flex items-center gap-1.5 flex-wrap">
              {nj.work_order_number && (
                <span className="h-4 px-1 bg-[#0f172a] text-white text-[9px] font-black uppercase tracking-widest font-mono">
                  WO# {nj.work_order_number}
                </span>
              )}
              <span className="truncate">{nj.title}</span>
              <StatusPill status={nj.status}/>
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5 truncate">
              {nj.property_address}{nj.unit ? ` · #${nj.unit}` : ""}
              {nj.assigned_to_name && <span className="text-slate-400"> · {nj.assigned_to_name}</span>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ------ Sub-Contractor banner shown on admin job detail when job.assigned_sub_id is set
function SubContractorBanner({ job, onReload }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    if (!window.confirm(`Re-send the job assignment email to ${job.assigned_sub_name}?`)) return;
    setBusy(true);
    const t = toast.loading("Sending email…");
    try {
      await api.post(`/jobs/${job.id}/resend-sub-email`);
      toast.success(`Email sent to ${job.assigned_sub_name}`, { id: t });
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed", { id: t });
    } finally { setBusy(false); }
  };
  const sentAt = job.sub_email_last_sent_at ? new Date(job.sub_email_last_sent_at) : null;
  return (
    <div className="mt-4 rugged-card p-3 bg-cyan-50 border-[#00C4CC]" data-testid="sub-banner">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="h-6 px-2 bg-[#0f172a] text-[#00C4CC] font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Wrench weight="fill" size={12}/> Sub-Contractor
        </span>
        <span className="font-display font-black text-lg">{job.assigned_sub_name}</span>
        <span className="text-xs font-medium text-slate-600">· Timer &amp; GPS not tracked for sub jobs</span>
      </div>
      <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="text-xs font-medium text-slate-700 flex-1" data-testid="sub-email-status">
          {sentAt ? (
            <>Emailed <span className="font-black">{job.assigned_sub_name}</span> on{" "}
            <span className="font-black">{sentAt.toLocaleString([], {month:"short", day:"numeric", hour:"2-digit", minute:"2-digit"})}</span></>
          ) : (
            <span className="text-amber-700 font-black">Email not yet sent</span>
          )}
        </div>
        <button onClick={resend} disabled={busy} data-testid="sub-resend-email-btn"
          className="h-9 px-3 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-[#00a8af] disabled:opacity-60">
          <Envelope weight="fill" size={14}/> {busy ? "Sending…" : "Resend Email"}
        </button>
      </div>
    </div>
  );
}

// ------ Job detail (admin) ------
export function AdminJobDetail() {
  return (
    <AdminShell>
      <AdminJobDetailBody/>
    </AdminShell>
  );
}

// Extracted body — reused by AdminJobDetailV2 which wraps this in a
// BrandedHero. Renders WITHOUT its own AdminShell.
export function AdminJobDetailBody({ v2 = false }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = useRef(false);
  const [job, setJob] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [assignSel, setAssignSel] = useState("");
  const [smsOpen, setSmsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [estimatorOpen, setEstimatorOpen] = useState(false);
  const [inspectionPhase, setInspectionPhase] = useState(null);
  const [retagOpen, setRetagOpen] = useState(false);
  const [bundleOpen, setBundleOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [openARs, setOpenARs] = useState([]);
  const [resolveTargetId, setResolveTargetId] = useState(null);
  const [resolveText, setResolveText] = useState("");
  const [resolving, setResolving] = useState(false);
  const [ownerNoticeOpen, setOwnerNoticeOpen] = useState(false);
  const [landlordQuoteOpen, setLandlordQuoteOpen] = useState(false);
  const [clickupInspect, setClickupInspect] = useState(null);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchSettings, setDispatchSettings] = useState({ enabled: false, mode: "suggest" });

  useEffect(() => {
    api.get("/settings").then(r => setDispatchSettings({
      enabled: !!r.data.auto_dispatch_enabled,
      mode: r.data.auto_dispatch_mode || "suggest",
      requireDx: !!r.data.auto_dispatch_require_diagnosis,
    })).catch(() => {});
  }, []);

  const loadARs = async () => {
    try {
      const { data } = await api.get(`/jobs/${id}/access-requests`);
      setOpenARs((data || []).filter(a => a.status === "open"));
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadARs(); const t = setInterval(loadARs, 15000); return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const resolveAR = async () => {
    if (!resolveTargetId) return;
    setResolving(true);
    try {
      await api.post(`/access-requests/${resolveTargetId}/resolve`, { resolution: resolveText });
      toast.success("Access request resolved");
      setResolveTargetId(null); setResolveText("");
      await loadARs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resolve");
    } finally { setResolving(false); }
  };

  const promoteToTurn = async () => {
    if (!confirm("Create a Turn job from this Move-Out inspection?")) return;
    setPromoting(true);
    try {
      const r = await api.post(`/jobs/${job.id}/promote-to-turn`);
      toast.success("Turn created");
      await load();
      if (r.data?.turn_id) window.location.href = `/admin/jobs/${r.data.turn_id}`;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not promote");
    } finally { setPromoting(false); }
  };
  const load = () => api.get(`/jobs/${id}`).then(r => { setJob(r.data); setAssignSel(r.data.assigned_to || ""); });
  useEffect(() => { load(); api.get("/assignable-users").then(r => setWorkers(r.data)); }, [id]);

  // Auto-open the simplified inspection flow on mount for pure inspection
  // jobs so techs land straight in the walk (no scrolling job detail).
  // Skipped when `?walk=0` is in the URL (so office can view the full detail).
  useEffect(() => {
    if (autoOpenedRef.current || !job) return;
    if (job.status === "completed") return;
    if (job.is_turn) return;
    const t = String(job.inspection_type || "").toLowerCase();
    const phase = t === "move_out" ? "move_out"
                : t === "move_in"  ? "move_in"
                : t === "periodic" ? "periodic"
                : null;
    if (!phase) return;
    if (searchParams.get("walk") === "0") return;
    autoOpenedRef.current = true;
    setInspectionPhase(phase);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status, job?.inspection_type, job?.is_turn]);

  if (!job) return <div className="p-8 font-bold">Loading...</div>;

  const reassign = async (worker_id) => {
    try {
      await api.patch(`/jobs/${id}`, { assigned_to: worker_id || "" });
      await load();
      toast.success(worker_id ? "Worker assigned" : "Returned to pool");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    nav("/admin/jobs");
  };

  return (
    <>
      <div className="p-8">
        <button onClick={() => nav(-1)} className="text-sm font-black uppercase tracking-wider text-zinc-600">← Back</button>
        {/* Pinned WO Answer Sheet — Complaint / Human Notes / What was done /
            Who-Status-Address. Shown at the top of every WO detail render.
            Hidden when embedded inside V2 (v2 prop) since V2 already renders
            the sheet above this body in its own layout. */}
        {!v2 && (
          <div className="mt-4">
            <WoAnswerSheet job={job} onRefresh={load}/>
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rugged-card p-6">
            {/* ── AppFolio-style Summary Header: status + WO# + title + Edit button (top-right) ── */}
            <div className="flex items-start justify-between gap-3 flex-wrap md:flex-nowrap" data-testid="jobdetail-header-bar">
              <div className="w-full md:min-w-0 md:flex-1 order-2 md:order-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill status={job.status}/>
                  <span className="px-2 py-0.5 border-2 border-slate-300 text-[10px] font-black uppercase tracking-widest text-slate-600 rounded-sm">
                    {job.priority || "normal"}
                  </span>
                  {job.portfolio_name && (
                    <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white rounded-sm"
                          style={{ background: job.portfolio_color || "#0f172a" }} data-testid="admin-header-portfolio">
                      {job.portfolio_name}
                    </span>
                  )}
                  {job.permission_to_enter === "no" && (
                    <span className="px-2 py-0.5 border-2 border-rose-400 bg-rose-50 text-[10px] font-black uppercase tracking-widest text-rose-700 rounded-sm"
                          data-testid="admin-header-pte">
                      Permission to enter: No
                    </span>
                  )}
                  {job.permission_to_enter === "yes" && (
                    <span className="px-2 py-0.5 border-2 border-emerald-400 bg-emerald-50 text-[10px] font-black uppercase tracking-widest text-emerald-700 rounded-sm"
                          data-testid="admin-header-pte">
                      Permission to enter: Yes
                    </span>
                  )}
                  {(job.portfolio_slug === "jscre" || job.partner_handled) && <PartnerHandledToggle job={job} onDone={load}/>}
                  {job.appfolio_link && (
                    <a href={job.appfolio_link} target="_blank" rel="noreferrer" data-testid="admin-header-appfolio-link"
                       className="px-2 py-0.5 border-2 border-sky-300 bg-sky-50 text-[10px] font-black uppercase tracking-widest text-sky-700 rounded-sm hover:bg-sky-100">
                      Open in AppFolio ↗
                    </a>
                  )}
                </div>
                {/* Address = big hero (was buried in the Details card below).
                    Responsive font: shrinks on narrow phones so long
                    "4259 Main St, Philadelphia, PA 19127-1513" doesn't wrap
                    letter-by-letter when a Reopen button is next to it. */}
                {job.property_address && (
                  <h1 className="mt-2 font-display font-black text-xl sm:text-2xl md:text-3xl leading-tight break-words" data-testid="jobdetail-address-hero">
                    <Link to={`/admin/properties/${encodeURIComponent(job.property_address)}`}
                          className="hover:text-[#00C4CC] decoration-dotted underline-offset-4">
                      {job.property_address}
                    </Link>
                    {job.unit && <span className="ml-2 align-middle inline-block px-2 py-0.5 bg-fuchsia-600 text-white font-black uppercase text-xs tracking-widest rounded-sm">Apt {formatUnit(job.unit)}</span>}
                  </h1>
                )}
                {/* WO# = small chip below the address */}
                {job.work_order_number && (
                  <button
                    type="button"
                    onClick={() => { try { navigator.clipboard.writeText(job.work_order_number); toast.success(`Copied WO#${job.work_order_number}`); } catch (_) {} }}
                    data-testid="admin-header-wo"
                    className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-black uppercase tracking-widest font-mono cursor-pointer rounded-sm"
                    title="Click to copy WO number"
                  >
                    WO# {job.work_order_number} <span className="opacity-60 text-[9px]">⧉</span>
                  </button>
                )}
                <InlineWoFix jobId={job.id || job._id} currentWo={job.work_order_number || ""} onSaved={load} variant="row"/>
                <h2 className="mt-1 font-display font-bold text-base text-slate-800 leading-snug" data-testid="jobdetail-title">{job.title}</h2>
              </div>
              <div className="shrink-0 flex items-center gap-2 order-1 md:order-2 self-end md:self-auto">
                {job.status === "completed" ? (
                  <ReopenAndPinButton jobId={id} workers={workers} onDone={load}/>
                ) : (
                  <PinToSweeperButton jobIds={[id]} workers={workers} onPinned={load}/>
                )}
                <button onClick={() => setEditOpen(true)} data-testid="edit-job-top-btn"
                  className="tactile-btn h-10 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-2 hover:bg-slate-800 shadow-[3px_3px_0_#00C4CC]">
                  <PencilSimple weight="bold" size={14}/> Edit
                </button>
              </div>
            </div>

            {/* ── AppFolio-style Details Card: consolidated summary of key fields ── */}
            <div className="mt-4 border-2 border-slate-200 bg-slate-50/60 rounded-md p-4" data-testid="jobdetail-details-card">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                Details
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Property</div>
                  {job.property_address ? (
                    <Link
                      to={`/admin/properties/${encodeURIComponent(job.property_address)}`}
                      data-testid="admin-header-address-link"
                      className="font-bold text-slate-900 hover:text-[#00C4CC] hover:underline decoration-dotted flex items-center gap-1"
                    >
                      <MapPin weight="bold" size={13} className="text-slate-500"/>
                      <span className="truncate">{job.property_address}</span>
                    </Link>
                  ) : <span className="text-slate-400 text-xs">—</span>}
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Unit</div>
                  <div className="font-bold text-slate-900">
                    {job.unit ? <span className="px-1.5 py-0.5 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm">Apt {formatUnit(job.unit)}</span> : <span className="text-slate-400 text-xs">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Assigned Tech</div>
                  <div className="font-bold text-slate-900 truncate">
                    {job.assigned_to_name || <span className="text-slate-400 text-xs font-normal">Unassigned</span>}
                    {job.helper_name && <span className="ml-1 text-xs text-slate-500 font-medium">+ {job.helper_name}</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Vendor / Sub</div>
                  <div className="font-bold text-slate-900 truncate">
                    {job.assigned_sub_name || <span className="text-slate-400 text-xs font-normal">In-house</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Customer</div>
                  <div className="font-bold text-slate-900 truncate">
                    {job.customer_name || <span className="text-slate-400 text-xs font-normal">—</span>}
                    {job.customer_phone && <span className="block text-[11px] text-slate-500 font-medium">{job.customer_phone}</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Scheduled</div>
                  <div className="font-bold text-slate-900">
                    {job.scheduled_for ? displayLocalDate(job.scheduled_for) : <span className="text-slate-400 text-xs font-normal">Not scheduled</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Created</div>
                  <div className="font-bold text-slate-900 text-xs">
                    {job.created_at ? new Date(job.created_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                {job.completed_at && (
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Completed</div>
                    <div className="font-bold text-emerald-900 text-xs">
                      {new Date(job.completed_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
              {job.property_address && (
                <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-2">
                  <VendorSpendPill address={job.property_address} unit={job.unit || undefined}/>
                  <QuoteConfidenceChip jobId={job.id}/>
                </div>
              )}
            </div>

            {/* Work Order description — pulled up so admin sees WHAT the
                job actually needs before wading through the banner stack. */}
            {job.description && (
              <div className="mt-3 rugged-card p-4 bg-amber-50 border-l-4 border-amber-400"
                data-testid="jobdetail-description">
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-900 mb-1.5">
                  Work Order
                </div>
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {job.description}
                </p>
              </div>
            )}

            {/* ── Prominent Start Walk chip — pinned near the top so techs never scroll to find it ── */}
            {(() => {
              const t = String(job.inspection_type || "").toLowerCase();
              const autoPhase = job.is_turn ? null
                              : t === "move_out" ? "move_out"
                              : t === "move_in"  ? "move_in"
                              : t === "periodic" ? "periodic"
                              : null;
              const isInspectionJob = !!autoPhase || !!job.is_turn;
              if (!isInspectionJob || job.status === "completed") return null;
              return (
                <div className="mt-3 rugged-card p-3 bg-gradient-to-br from-[#0f172a] to-slate-800 text-white border-4 border-[#00C4CC] flex items-center gap-3 flex-wrap"
                  data-testid="jobdetail-start-walk-strip">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">
                      {job.is_turn ? "Turn Inspection" : (t === "move_out" ? "Move-Out" : t === "periodic" ? "Periodic Sweep" : "Move-In")} · ready
                    </div>
                    <div className="font-display font-black text-base leading-tight">
                      Walk the unit room-by-room
                    </div>
                  </div>
                  {autoPhase ? (
                    <button onClick={() => setInspectionPhase(autoPhase)}
                      data-testid="jobdetail-start-walk-btn"
                      className="h-12 px-5 bg-[#00C4CC] hover:bg-[#00d4dd] text-[#0f172a] font-black uppercase tracking-widest text-sm inline-flex items-center gap-2 shadow-[4px_4px_0_rgba(0,196,204,0.35)]">
                      <PlayCircle weight="fill" size={16}/> Start Walk
                    </button>
                  ) : (
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button onClick={() => setInspectionPhase("move_in")}
                        data-testid="jobdetail-start-move-in-btn"
                        className="flex-1 h-12 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1">
                        <PlayCircle weight="fill" size={14}/> Move-In
                      </button>
                      <button onClick={() => setInspectionPhase("move_out")}
                        data-testid="jobdetail-start-move-out-btn"
                        className="flex-1 h-12 px-4 bg-red-500 hover:bg-red-600 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1">
                        <PlayCircle weight="fill" size={14}/> Move-Out
                      </button>
                    </div>
                  )}
                  {/* One-tap rescue: bulk-retag mis-tagged photos so the PDF renders correctly. */}
                  {(((job.before_photos || []).length + (job.after_photos || []).length) > 0) && (
                    <button onClick={() => setRetagOpen(true)}
                      data-testid="jobdetail-fix-photos-btn"
                      title="Move mis-tagged photos to the correct room"
                      className="h-12 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5 shadow-[3px_3px_0_rgba(0,0,0,0.35)]">
                      <Sparkle weight="fill" size={13}/> Fix Photos
                    </button>
                  )}
                  {/* Cross-env sync: package the WO folder to move between preview↔production */}
                  <button onClick={() => setBundleOpen(true)}
                    data-testid="jobdetail-bundle-btn"
                    title="Export/Import this WO's folder to another environment"
                    className="h-12 px-3 bg-gradient-to-r from-teal-600 to-emerald-700 text-white font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5 shadow-[3px_3px_0_rgba(0,0,0,0.35)]">
                    <Sparkle weight="fill" size={13}/> Copy Folder
                  </button>
                  <button type="button"
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      next.set("walk", "0");
                      setSearchParams(next, { replace: true });
                      autoOpenedRef.current = true;
                      setInspectionPhase(null);
                    }}
                    data-testid="jobdetail-view-details-btn"
                    className="h-8 px-2 text-[10px] font-black uppercase tracking-widest text-white/60 hover:text-white underline">
                    Job details ↓
                  </button>
                </div>
              );
            })()}
            {/* Central Files Tab — AppFolio-parity unified attachments (photos,
                receipts, ClickUp invoices, habitability PDFs) filterable by kind. */}
            <WoFilesTab jobId={job.id}/>

            {/* Owner Approval Threshold — request one-click approve/reject email
                from the landlord before dispatching. Hidden if tenant toggle is off. */}
            <OwnerApprovalCard job={job} onReload={load}/>

            {/* Field-Day Tools — collapsed by default for office/admin so the
                huge "I'M HERE" tech panel doesn't hog the screen. Techs on
                mobile see the full card via /worker/jobs/:id (JobDetail.jsx). */}
            <details className="mt-4 border-2 border-slate-300 bg-white shadow-[3px_3px_0_#0f172a] rounded-sm" data-testid="admin-field-day-toggle">
              <summary className="cursor-pointer px-3 py-2 flex items-center gap-2 hover:bg-slate-50">
                <Truck weight="fill" size={16} className="text-cyan-600"/>
                <span className="font-display font-black uppercase tracking-wider text-xs text-slate-800">
                  Field-Day Tools
                </span>
                <span className="text-[10px] text-slate-500 font-medium normal-case tracking-normal">
                  · tech entry, tap-to-navigate, "I'm here"
                </span>
                {job.en_route_at && (
                  <span className="px-2 py-0.5 bg-emerald-600 text-white font-black uppercase text-[9px] tracking-widest rounded-sm">
                    tenant notified ✓
                  </span>
                )}
                <span className="ml-auto text-[10px] font-bold text-slate-500">tap to expand</span>
              </summary>
              <div className="p-2 pt-0">
                <ShowUpCard job={job} onReload={() => window.location.reload()}/>
              </div>
            </details>
            {/* Legal Protocol banner + Tag-as-Legal button (works retroactively on any WO, incl. completed ones) */}
            <LegalProtocolPanel jobId={id}/>
            <FundingGuardBanner job={job}/>
            {job.needs_docs && !job.docs_completed_at && <NeedsDocsBanner job={job} onDone={load}/>}
            <DuplicatesBanner job={job} role="admin" onMerged={load}/>
            {/* Awaiting AF Invoice chip — bridge between FR-completed and AF-billed */}
            {job.status === "completed" && job.completed_at && (
              (() => {
                const daysAgo = Math.max(0, Math.floor((Date.now() - new Date(job.completed_at).getTime()) / 86400000));
                if (daysAgo > 60) return null;
                return (
                  <div className="mt-3 p-2 border-2 border-emerald-500 bg-emerald-50 rounded flex items-center gap-2" data-testid="awaiting-af-invoice-chip">
                    <span className="inline-flex items-center h-6 px-2 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest rounded">
                      💰 Awaiting AF Invoice
                    </span>
                    <span className="text-[11px] text-emerald-900">
                      Marked done <b>{daysAgo === 0 ? "today" : `${daysAgo}d ago`}</b> — verify AppFolio has an invoice logged.
                    </span>
                    <a href="/admin/ready-to-bill" data-testid="awaiting-af-view-all"
                       className="ml-auto text-[10px] font-black uppercase tracking-widest text-emerald-700 hover:underline">
                      View all →
                    </a>
                  </div>
                );
              })()
            )}
            {/* ClickUp Sync + Dedupe Description — admin-only manual overrides */}
            <div className="mt-3 flex gap-2 flex-wrap" data-testid="admin-clickup-tools">
              <button
                type="button"
                data-testid="admin-job-sync-clickup-btn"
                onClick={async () => {
                  try {
                    const r = await api.post(`/clickup/jobs/${job.id}/sync-now`);
                    const parts = [`ClickUp: ${r.data.cu_status}`, `FR: ${r.data.fr_status}`];
                    if (r.data.photos_pulled) parts.push(`+${r.data.photos_pulled} photos`);
                    if (r.data.attachments_pulled) parts.push(`+${r.data.attachments_pulled} files`);
                    if (r.data.completion_notes_pulled) parts.push("notes ✓");
                    toast.success(parts.join(" · "));
                    load();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No ClickUp task found");
                  }
                }}
                className="h-9 px-3 border-2 border-cyan-500 bg-cyan-50 hover:bg-cyan-100 text-cyan-900 font-black uppercase tracking-widest text-[10px] rounded-md flex items-center gap-1">
                🔄 Sync from ClickUp
              </button>
              <button
                type="button"
                data-testid="admin-job-dedupe-desc-btn"
                onClick={async () => {
                  if (!window.confirm("Collapse duplicate 'Merged from…' blocks in the description?")) return;
                  try {
                    const r = await api.post(`/clickup/jobs/${job.id}/deduplicate-description`);
                    toast.success(r.data.changed ? `Deduped: ${r.data.old_length}→${r.data.new_length} chars` : "Nothing to dedupe");
                    if (r.data.changed) load();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Dedupe failed");
                  }
                }}
                className="h-9 px-3 border-2 border-slate-400 bg-white hover:bg-slate-50 text-slate-700 font-black uppercase tracking-widest text-[10px] rounded-md flex items-center gap-1">
                🧹 Dedupe Description
              </button>
              <button
                type="button"
                data-testid="admin-job-inspect-clickup-btn"
                onClick={async () => {
                  try {
                    const r = await api.get(`/clickup/jobs/${job.id}/inspect`);
                    setClickupInspect(r.data);
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Inspect failed");
                  }
                }}
                className="h-9 px-3 border-2 border-indigo-500 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 font-black uppercase tracking-widest text-[10px] rounded-md flex items-center gap-1">
                🔍 Inspect ClickUp
              </button>
            </div>
            <AutoDiagnoseBanner job={job} onReload={load}/>
            <TriageLinkCard job={job} onMinted={load}/>
            <WOPermitPanel job={job}/>
            <PublicStatusLinkCard jobId={job.id} wo_number={job.work_order_number}/>
            <PhotoAlbumCard jobId={job.id} job={job}/>
            <QuotePanel job={job}/>
            <ChangeOrderPanel job={job}/>
            {(job.is_turn || ["move_out", "move_in", "periodic"].includes(String(job.inspection_type || "").toLowerCase())) && (
              <InspectionReportShareCard jobId={job.id} job={job}/>
            )}
            <ESignPanel
              parentType="work_order"
              parentId={job.id}
              defaultSubject={`Please sign — ${job.title || `WO #${job.work_order_number || ""}`.trim()}`}
              defaultSigners={[
                ...(job.customer_email ? [{ role: "Tenant", name: job.customer_name || "", email_address: job.customer_email }] : []),
                ...(job.landlord_email ? [{ role: "Owner", name: job.landlord_name || "", email_address: job.landlord_email }] : []),
              ]}
              compact={true}
            />
            <TenantThreadPanel job={job} onReload={load} role="admin"/>
            {dispatchSettings.enabled && !job.assigned_to && !job.assigned_to_name && (
              <button onClick={() => setDispatchOpen(true)} data-testid="admin-auto-dispatch-btn"
                className="mt-3 w-full h-11 border-2 border-fuchsia-600 bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-900 font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2">
                <Compass weight="fill" size={16}/> Auto-Dispatch · Suggest Best Worker
              </button>
            )}
            {dispatchOpen && (
              <AutoDispatchSuggestModal
                job={job}
                mode={dispatchSettings.mode}
                onClose={() => setDispatchOpen(false)}
                onAssigned={load}
              />
            )}
            <TenantHistoryPanel job={job} role="admin"/>
            <KraaftJobPanel jobId={job.id}/>
            <div className="mt-4 rugged-card p-3 flex items-center justify-between gap-3 bg-white" data-testid="habitability-toggle-card">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center gap-1">
                  <FilePdf weight="fill" size={12} className={job.habitability ? "text-orange-600" : "text-slate-400"}/>
                  Habitability / Code (Philly Safe Healthy Homes Act)
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {job.habitability ? "This job is flagged as a habitability issue — 30-day repair clock applies." : "Not flagged. Toggle on for code / habitability issues (leaks, no heat, mold, pests, gas)."}
                  {job.habitability_manual && <span className="ml-1 font-black text-orange-700">· LOCKED by office</span>}
                </div>
              </div>
              <button
                data-testid="habitability-toggle-btn"
                onClick={async () => {
                  try {
                    await api.post(`/jobs/${job.id}/toggle-habitability`, { value: !job.habitability });
                    toast.success(job.habitability ? "Habitability flag cleared" : "Flagged as habitability issue");
                    await load();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update flag");
                  }
                }}
                className={`shrink-0 tactile-btn h-10 px-4 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${job.habitability ? "bg-orange-600 text-white hover:bg-orange-700" : "bg-white text-[#0f172a] hover:bg-orange-50"}`}>
                {job.habitability ? "ON" : "OFF"}
              </button>
              <a
                href={`${API}/jobs/${job.id}/compliance-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                target="_blank" rel="noreferrer"
                data-testid="compliance-pdf-btn"
                className="shrink-0 tactile-btn h-10 px-4 font-black uppercase tracking-wider text-xs border-2 border-orange-700 bg-orange-600 text-white hover:bg-orange-700 inline-flex items-center gap-1.5"
                title="Export Philly Safe Healthy Homes Act compliance packet">
                <FilePdf weight="fill" size={14}/> PDF
              </a>
            </div>
            {job.habitability && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span data-testid="habitability-badge"
                  title={job.habitability_manual ? "Flagged as habitability by office" : "Auto-detected habitability"}
                  className="inline-flex items-center gap-1 h-8 px-3 bg-orange-600 text-white text-xs font-black uppercase tracking-widest rounded-md">
                  <FilePdf weight="fill" size={14}/> Habitability · 30-Day Rule
                </span>
                <HabitabilityCountdown startAt={job.created_at} size="lg" testId="habitability-countdown-detail"/>
              </div>
            )}
            {(job.status === "completed" || job.status === "canceled") && (
              <div className="mt-3">
                <a
                  href={`${API}/jobs/${job.id}/compliance-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                  target="_blank" rel="noreferrer"
                  data-testid="download-compliance-pdf-btn"
                  title="Philadelphia Safe Healthy Homes Act compliance packet"
                  className="tactile-btn h-11 px-5 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2">
                  <FilePdf weight="fill" size={16}/> Compliance PDF
                </a>
              </div>
            )}
            {job.assigned_sub_id && <SubContractorBanner job={job} onReload={load}/>}
            {!job.assigned_sub_id && <RouteToSubPanel job={job} onReload={load}/>}
            {(job.is_turn || job.inspection_type === "move_out") && !job.needs_quote && (
              <RequestSubQuotePanel job={job} onReload={load}/>
            )}

            {/* Quick Update — one-shot card so admins don't have to hunt
                through tabs to drop a photo/file, add a note, or nudge status. */}
            <QuickUpdateCard job={job} onReload={load}/>

            {/* Owner Authorization Notice — manual send to landlord */}
            <div className="mt-4 rugged-card p-4 bg-slate-50 border-2 border-slate-300 flex items-center justify-between gap-3 flex-wrap" data-testid="owner-notice-card">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-widest text-slate-600 flex items-center gap-1.5">
                  <Envelope weight="fill" size={14} className="text-slate-500"/> Owner Authorization Notice
                </div>
                <div className="text-[12px] text-slate-600 mt-1 max-w-xl">
                  {job.owner_notice?.sent_at
                    ? <>Sent {new Date(job.owner_notice.sent_at).toLocaleString()} to <span className="font-bold">{job.owner_notice.to_email}</span> — from <span className="font-mono">{job.owner_notice.from_name}</span></>
                    : "Preview and email the landlord a polished, PM-branded dispatch notice with the $ cap policy. Nothing fires automatically — you're in control."}
                </div>
              </div>
              <button data-testid="send-owner-notice-btn" onClick={() => setOwnerNoticeOpen(true)}
                className="tactile-btn h-10 px-4 bg-[#0f172a] text-white font-black uppercase tracking-wider text-xs hover:bg-slate-800 inline-flex items-center gap-1.5">
                <Envelope weight="fill" size={14}/> {job.owner_notice?.sent_at ? "Send Again" : "Send Owner Notice"}
              </button>
            </div>

            {/* Landlord Quote & Authorization — one-tap "send quote for approval" flow */}
            <div className="mt-4 rugged-card p-4 bg-yellow-50 border-2 border-yellow-500 flex items-center justify-between gap-3 flex-wrap" data-testid="landlord-quote-card">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-widest text-yellow-800 flex items-center gap-1.5 flex-wrap">
                  <FilePdf weight="fill" size={14} className="text-yellow-700"/> Repair Quote &amp; Authorization
                  {job.landlord_quote?.status && job.landlord_quote.status !== "draft" && (
                    <span className="px-2 py-0.5 bg-yellow-500 text-[#0f172a] font-black text-[10px] uppercase tracking-widest">
                      {job.landlord_quote.status}
                    </span>
                  )}
                  {job.landlord_quote?.status === "signed" && !job.landlord_quote?.funded_at && (
                    <span className="px-2 py-0.5 bg-orange-500 text-white font-black text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                          data-testid="awaiting-funding-chip">
                      ⚠ Awaiting Funding
                    </span>
                  )}
                  {job.landlord_quote?.funded_at && (
                    <span className="px-2 py-0.5 bg-emerald-600 text-white font-black text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                          data-testid="funded-chip">
                      ✓ Funded
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-slate-700 mt-1 max-w-xl">
                  {job.landlord_quote?.funded_at
                    ? <>Funded {new Date(job.landlord_quote.funded_at).toLocaleString()} · {job.landlord_quote.funded_method || "appfolio"}{job.landlord_quote.funded_reference ? ` · ref ${job.landlord_quote.funded_reference}` : ""} · Total <b>${((job.landlord_quote.landlord_total_cents||0)/100).toFixed(2)}</b></>
                    : job.landlord_quote?.signed_at
                      ? <>Signed by <b>{job.landlord_quote.signed_by_name}</b> at {new Date(job.landlord_quote.signed_at).toLocaleString()} · Total <b>${((job.landlord_quote.landlord_total_cents||0)/100).toFixed(2)}</b></>
                      : job.landlord_quote?.sent_at
                        ? <>Sent {new Date(job.landlord_quote.sent_at).toLocaleString()} to <b>{job.landlord_quote.sent_to}</b> · Total <b>${((job.landlord_quote.landlord_total_cents||0)/100).toFixed(2)}</b></>
                        : "Build a marked-up quote PDF from the vendor's price. Claude drafts the scope, landlord signs online, AppFolio funding baked in."}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {job.landlord_quote?.status === "signed" && !job.landlord_quote?.funded_at && (
                  <MarkFundedButton jobId={job.id} onDone={load}/>
                )}
                <button data-testid="open-landlord-quote-btn" onClick={() => setLandlordQuoteOpen(true)}
                  className="tactile-btn h-10 px-4 bg-yellow-500 hover:bg-yellow-600 text-[#0f172a] font-black uppercase tracking-wider text-xs inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
                  <FilePdf weight="fill" size={14}/> {job.landlord_quote?.token ? "Manage Quote" : "Send Quote for Approval"}
                </button>
              </div>
            </div>
            {landlordQuoteOpen && (
              <LandlordQuoteModal job={job} onClose={() => setLandlordQuoteOpen(false)} onSaved={load}/>
            )}

            {openARs.length > 0 && (
              <div className="mt-4 p-4 rounded-xl bg-red-50 border-l-4 border-red-600 shadow-sm" data-testid="admin-access-banner">
                <div className="flex items-center gap-2 mb-1">
                  <Warning weight="fill" size={20} className="text-red-600"/>
                  <div className="font-display font-black text-lg text-red-800 uppercase tracking-wide">
                    🚨 Can't Get In — {openARs[0].worker_name}
                  </div>
                </div>
                <div className="text-sm text-red-900 font-bold">
                  {openARs[0].reason_label}{openARs[0].notes ? ` · ${openARs[0].notes}` : ""}
                </div>
                <div className="text-xs text-red-700 mt-1 font-medium">
                  Filed {new Date(openARs[0].created_at).toLocaleTimeString()} · Stage: {openARs[0].notified_stage}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {job.customer_phone && (
                    <a href={`tel:${job.customer_phone}`} data-testid="ar-call-tenant"
                      className="h-9 px-3 border-2 border-red-600 bg-white text-red-700 font-black uppercase text-[11px] tracking-widest rounded inline-flex items-center gap-1">
                      📞 Call Tenant
                    </a>
                  )}
                  <button onClick={() => setResolveTargetId(openARs[0].id)}
                    data-testid="ar-resolve-btn"
                    className="h-9 px-3 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[11px] tracking-widest rounded">
                    Mark Resolved
                  </button>
                </div>
              </div>
            )}

            {job.property_address && (
              <div className="mt-4">
                <PropertyHistoryPanel address={job.property_address} currentJobId={job.id} adminLink={true}/>
              </div>
            )}

            {/* Evidence Timeline — every photo + AI event stitched into one
                chronological "evidence locker" for demos and dispute-proofing. */}
            <details className="mt-4 border-2 border-[#0f172a] bg-white shadow-[3px_3px_0_#0f172a]" data-testid="admin-evidence-timeline">
              <summary className="cursor-pointer px-4 py-3 font-display font-black uppercase tracking-wider text-sm bg-[#0f172a] text-white flex items-center gap-2">
                <Clock weight="fill" size={16}/> Evidence Timeline
                <span className="ml-auto text-[10px] font-bold opacity-70">tap to expand</span>
              </summary>
              <div className="p-4">
                <JobHistoryTimeline jobId={job.id}/>
              </div>
            </details>

            {(() => {
              // Smart labels: for a single-phase inspection (periodic / move_in / move_out)
              // show ONE grid with a meaningful heading; for a turn, keep Before / After;
              // for regular repair jobs, keep default Before / After.
              const t = String(job.inspection_type || "").toLowerCase();
              const isSinglePhase = !job.is_turn && ["move_out", "move_in", "periodic"].includes(t);
              if (isSinglePhase) {
                const label = t === "periodic" ? "Periodic Walk Photos"
                            : t === "move_in"  ? "Move-In Photos"
                            :                    "Move-Out Photos";
                // For periodic all photos land in after_photos; for move_out in before_photos
                const items = t === "move_out"
                  ? (job.before_photos || [])
                  : [...(job.after_photos || []), ...(job.before_photos || [])];
                return <PhotoGrid title={label} items={items} jobId={job.id} onDelete={load}/>;
              }
              return (
                <>
                  <PhotoGrid title={job.is_turn ? "Move-Out Photos" : "Before"} items={job.before_photos} jobId={job.id} onDelete={load}/>
                  <PhotoGrid title={job.is_turn ? "Move-In Photos"  : "After"}  items={job.after_photos}  jobId={job.id} onDelete={load}/>
                </>
              );
            })()}
            <PhotoGrid title="Receipts" items={job.receipts} jobId={job.id} onDelete={load}/>
            <ClickUpAttachSummary job={job}/>

            {/* Rescue tool: bulk-retag mis-tagged inspection photos so
                periodic / turn reports render correctly on next PDF open. */}
            {(job.is_turn || ["move_out", "move_in", "periodic"].includes(String(job.inspection_type || "").toLowerCase())) &&
              (((job.before_photos || []).length + (job.after_photos || []).length) > 0) && (
              <div className="mt-4">
                <button onClick={() => setRetagOpen(true)}
                  data-testid="admin-photo-retag-btn"
                  className="h-11 px-4 bg-gradient-to-r from-fuchsia-600 to-red-600 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-transform">
                  <Sparkle weight="fill" size={14}/> Fix Photo Rooms
                  <span className="text-[9px] opacity-80 font-medium normal-case tracking-normal">
                    · drag mis-tagged photos to the right room
                  </span>
                </button>
              </div>
            )}

            <MaterialsEditor job={job} onReload={load}/>

            {(job.checklist || []).length > 0 && (
              <div className="mt-6">
                <h3 className="font-display font-black text-lg uppercase">Issues Checklist ({job.checklist.filter(c => c.done).length}/{job.checklist.length})</h3>
                <ul className="mt-3 space-y-2">
                  {job.checklist.map(c => (
                    <li key={c.id} className={`flex items-center gap-3 p-2 border-2 ${c.done ? "border-green-500 bg-green-50" : "border-slate-300"}`}>
                      <span className={`h-5 w-5 border-2 flex items-center justify-center ${c.done ? "bg-[#00C4CC] border-[#00C4CC] text-white" : "border-slate-400"}`}>
                        {c.done ? "✓" : ""}
                      </span>
                      <span className={c.done ? "line-through text-slate-500" : "font-medium"}>{c.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {job.completion_notes && (
              <div className="mt-6 p-4 bg-zinc-50 border-l-4 border-[#00C4CC]">
                <div className="text-xs font-black uppercase tracking-wider text-zinc-500">Worker Notes / Write-up</div>
                <p className="mt-1 whitespace-pre-wrap">{job.completion_notes}</p>
              </div>
            )}

            {/* ClickUp-mirrored files + completion notes (invoice PDFs etc.) */}
            <ClickUpMirrorSection job={job}/>

            {/* Closing Summary → habitability PDF (pinned to WO + property record) */}
            <HabitabilityCard jobId={job.id} initialSummary={job.closing_summary || ""}/>

            <AdminWritePanel job={job} onReload={load}/>
            {!job.completion_notes && (job.work_performed_polished || job.work_performed) && (
              <div className="mt-6 p-4 bg-[#fff7ed] border-l-4 border-[#00C4CC]">
                <div className="text-xs font-black uppercase tracking-wider text-[#c2410c]">Work Performed (in progress)</div>
                <p className="mt-1 whitespace-pre-wrap">{job.work_performed_polished || job.work_performed}</p>
              </div>
            )}

            {(job.is_turn || job.inspection_type) && (
              <div className="mt-6 rugged-card p-4 bg-amber-50 border-l-4 border-amber-500" data-testid="admin-turn-inspection-launcher">
                <div className="font-display font-black text-sm uppercase tracking-wider text-amber-800">
                  {job.inspection_type === "move_out" ? "Move-Out Inspection" :
                   job.inspection_type === "move_in" ? "Move-In Inspection" :
                   job.inspection_type === "periodic" ? "Periodic Inspection" : "Turn Inspections"}
                </div>
                <div className="mt-1 text-xs text-slate-600 font-medium">
                  Walk room-by-room. Rate condition, snap photos, mark complete. Data feeds the Chargeback report.
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(job.is_turn || job.inspection_type === "move_out" || job.inspection_type === "periodic") && (
                    <button onClick={() => setInspectionPhase("move_out")} data-testid="admin-start-move-out-btn"
                      disabled={(job.rooms || []).length === 0}
                      className="tactile-btn h-14 bg-red-600 hover:bg-red-700 text-white font-display font-black uppercase text-sm tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-50">
                      <PlayCircle weight="fill" size={20}/> Start {job.inspection_type === "periodic" ? "Walk" : "Move-Out"}
                    </button>
                  )}
                  {(job.is_turn || job.inspection_type === "move_in") && (
                    <button onClick={() => setInspectionPhase("move_in")} data-testid="admin-start-move-in-btn"
                      disabled={(job.rooms || []).length === 0}
                      className="tactile-btn h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-display font-black uppercase text-sm tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-50">
                      <PlayCircle weight="fill" size={20}/> Start Move-In
                    </button>
                  )}
                </div>
                {(job.rooms || []).length === 0 && (
                  <div className="mt-2 text-xs text-red-600 font-bold">
                    ⚠ No rooms configured yet — click "Edit Job" and set bedrooms/bathrooms first.
                  </div>
                )}
                {job.inspection_type === "move_out" && !job.promoted_to_turn_id && (
                  <button onClick={promoteToTurn} disabled={promoting}
                    data-testid="promote-to-turn-btn"
                    className="mt-3 tactile-btn w-full h-11 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    <Repeat weight="bold" size={14}/> {promoting ? "Creating Turn…" : "Send to Turns Board"}
                  </button>
                )}
                {job.promoted_to_turn_id && (
                  <div className="mt-3 p-2 border-2 border-amber-500 bg-white text-xs font-bold flex items-center justify-between gap-2">
                    <span>✓ Promoted to Turn</span>
                    <Link to={`/admin/jobs/${job.promoted_to_turn_id}`} data-testid="view-promoted-turn-link"
                      className="text-[#00C4CC] hover:underline">View turn →</Link>
                  </div>
                )}
                {job.source_inspection_id && (
                  <div className="mt-3 p-2 border-2 border-slate-300 bg-white text-xs font-medium flex items-center justify-between gap-2">
                    <span>Promoted from Move-Out Inspection</span>
                    <Link to={`/admin/jobs/${job.source_inspection_id}`} data-testid="view-source-inspection-link"
                      className="text-[#00C4CC] hover:underline">View inspection →</Link>
                  </div>
                )}
                {job.auto_generated_move_in_id && (
                  <div className="mt-3 p-2 border-2 border-emerald-500 bg-emerald-50 text-xs font-bold flex items-center justify-between gap-2">
                    <span>✓ Move-In Inspection auto-created</span>
                    <Link to={`/admin/jobs/${job.auto_generated_move_in_id}`} data-testid="view-auto-movein-link"
                      className="text-[#00C4CC] hover:underline">View →</Link>
                  </div>
                )}
              </div>
            )}

            {job.is_turn && (
              <div className="mt-6" data-testid="admin-chargeback-section">
                <ChargebackPanel jobId={job.id} isAdmin={true} onJobReload={load}/>
              </div>
            )}

            {/* Nearby Jobs — collapsed by default so it doesn't crowd the WO view.
                Click to expand and route-plan on-the-fly. */}
            <details className="mt-6 border-2 border-slate-300 bg-white shadow-[3px_3px_0_#0f172a]" data-testid="admin-nearby-jobs-toggle">
              <summary className="cursor-pointer px-4 py-3 font-display font-black uppercase tracking-wider text-sm bg-slate-100 hover:bg-slate-200 flex items-center gap-2">
                <MapPin weight="fill" size={16} className="text-[#00C4CC]"/> Nearby Jobs
                <span className="ml-auto text-[10px] font-bold opacity-60">tap to expand</span>
              </summary>
              <div className="p-2">
                <NearbyJobsPanel jobId={job.id}/>
              </div>
            </details>
          </div>

          <div className="rugged-card p-6 h-fit">
            <h3 className="font-display font-black text-lg uppercase">Assignment</h3>
            <PropertyDetailsCard address={job.property_address} compact/>
            <WoLeaseContextChip address={job.property_address} unit={job.unit}/>
            <div className="mt-3">
              <label className="text-xs font-black uppercase tracking-wider">Worker</label>
              <select value={assignSel} onChange={e => setAssignSel(e.target.value)} data-testid="reassign-select"
                className="mt-1 w-full h-11 px-2 border-2 border-[#0f172a] bg-white">
                <option value="">— Unassigned (pool) —</option>
                {workers.map(w => <option key={w.id} value={w.id}>{w.name}{w.role === "admin" ? " (office)" : ""}</option>)}
              </select>
              {assignSel !== (job.assigned_to || "") && (
                <button onClick={() => reassign(assignSel)} data-testid="save-assign-btn"
                  className="tactile-btn mt-2 w-full h-10 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider">
                  Save Assignment
                </button>
              )}
            </div>
            {(job.entry_code || job.lockbox_code || job.entry_notes) && (
              <div className="mt-4 p-3 border-2 border-dashed border-[#00C4CC] bg-cyan-50" data-testid="entry-info-card">
                <div className="text-xs font-black uppercase tracking-wider text-[#0e7490]">Entry / Access</div>
                <div className="mt-1 font-mono font-bold text-sm">
                  {job.entry_code && <div>Code: {job.entry_code}</div>}
                  {job.lockbox_code && <div>Lockbox: {job.lockbox_code}</div>}
                </div>
                {job.entry_notes && <div className="mt-1 text-xs text-slate-600">{job.entry_notes}</div>}
              </div>
            )}
            {job.customer_phone && (
              <button onClick={() => setSmsOpen(true)} data-testid="sms-open-btn"
                className="mt-4 w-full h-10 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white hover:bg-slate-50">
                Send SMS to Customer
              </button>
            )}

            <button onClick={() => setEditOpen(true)} data-testid="edit-job-btn"
              className="mt-4 tactile-btn w-full h-11 bg-[#0f172a] text-white font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-2 hover:bg-slate-800">
              <PencilSimple weight="bold" size={14}/> Edit Job
            </button>

            <AIGatedButton onGo={() => setEstimatorOpen(true)} data-testid="job-detail-estimate-btn"
              className="mt-2 tactile-btn w-full h-11 bg-gradient-to-r from-violet-600 to-[#00C4CC] text-white font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-2">
              <Sparkle weight="fill" size={14}/> AI Estimate & Add Parts
            </AIGatedButton>

            <ClosePanel job={job} onDone={load}/>

            <RecurrenceManagerPanel job={job} onReload={load}/>

            <div className="mt-4 space-y-2 text-sm" data-testid="admin-job-times">
              <div><b>Created:</b> {new Date(job.created_at).toLocaleString()}</div>
              {job.clock_in_at && <div><b>Clock-in:</b> {new Date(job.clock_in_at).toLocaleString()}</div>}
              {job.clock_out_at && <div><b>Clock-out:</b> {new Date(job.clock_out_at).toLocaleString()}</div>}
              {job.completed_at && <div><b>Completed:</b> {new Date(job.completed_at).toLocaleString()}</div>}
              {(job.time_entries || []).slice(1).length > 0 && (
                <div className="mt-1 pt-1 border-t border-dashed border-slate-300">
                  <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Extra time entries</div>
                  {(job.time_entries || []).slice(1).map((e, i) => (
                    <div key={i} className="text-xs mt-0.5" data-testid={`extra-time-${i}`}>
                      <b>{e.user_name || "—"}:</b> {e.clock_in_at ? new Date(e.clock_in_at).toLocaleString() : "—"} → {e.clock_out_at ? new Date(e.clock_out_at).toLocaleString() : "still open"}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setTimeOpen(true)} data-testid="adjust-time-btn"
              className="mt-2 w-full h-10 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-amber-50 hover:bg-amber-100 inline-flex items-center justify-center gap-1">
              <Clock weight="bold" size={14}/> Adjust Times / Back-Fill Hours
            </button>
            <button onClick={del} data-testid="delete-job-btn" className="mt-6 w-full h-11 border-2 border-red-600 text-red-600 font-black uppercase tracking-wider text-sm hover:bg-red-600 hover:text-white transition-colors">
              <Trash weight="bold" size={14} className="inline mr-1"/> Delete Job
            </button>
            <a
              href={`${API}/jobs/${job.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
              target="_blank" rel="noreferrer"
              data-testid="admin-download-pdf-btn"
              className="mt-3 tactile-btn w-full h-11 bg-[#00C4CC] text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2">
              <FilePdf weight="fill" size={14}/> Internal Work Order PDF
            </a>
            <a
              href={`${API}/jobs/${job.id}/pdf?customer=true&auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
              target="_blank" rel="noreferrer"
              data-testid="admin-download-customer-pdf-btn"
              className="mt-2 tactile-btn w-full h-11 bg-white text-[#0f172a] border-2 border-[#0f172a] font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 hover:bg-slate-50">
              <FilePdf weight="fill" size={14}/> Customer PDF (No Pricing)
            </a>
            {job.is_turn && (
              <a
                href={`${API}/jobs/${job.id}/damage-report-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                target="_blank" rel="noreferrer"
                data-testid="admin-damage-report-pdf-btn"
                className="mt-2 tactile-btn w-full h-11 bg-red-600 text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 hover:bg-red-700">
                <FilePdf weight="fill" size={14}/> Owner Damage Report (AI)
              </a>
            )}
          </div>
        </div>

        {/* Field ↔ Office chat thread */}
        <JobMessages jobId={id} isAdmin={true} onAccessResolveClick={(arId) => setResolveTargetId(arId)}/>

        {smsOpen && (
          <SmsModal
            to={job.customer_phone}
            defaultText={`Hi ${job.customer_name || "there"}, this is ${job.assigned_to_name || "your Field Radar tech"} — on my way to ${job.property_address}.`}
            onClose={() => setSmsOpen(false)}
          />
        )}
        {editOpen && (
          <EditJobModal job={job} onClose={() => setEditOpen(false)} onSaved={async () => { setEditOpen(false); await load(); }}/>
        )}
        {clickupInspect && (
          <ClickUpInspectModal data={clickupInspect} onClose={() => setClickupInspect(null)}/>
        )}
        {retagOpen && (
          <PhotoRetagModal
            jobId={job.id}
            onClose={() => setRetagOpen(false)}
            onDone={load}
          />
        )}
        {bundleOpen && (
          <WOBundleModal
            jobId={job.id}
            wo={job}
            onClose={() => setBundleOpen(false)}
          />
        )}
        {inspectionPhase && (
          (() => {
            const savedMode = (typeof localStorage !== "undefined" && localStorage.getItem("insp_flow_mode")) || "simple";
            if (savedMode === "classic") {
              return (
                <InspectionWizard
                  jobId={job.id}
                  phase={inspectionPhase}
                  onClose={() => setInspectionPhase(null)}
                  onJobReload={load}
                />
              );
            }
            return (
              <InspectionFlowSimple
                jobId={job.id}
                phase={inspectionPhase}
                onClose={() => setInspectionPhase(null)}
                onJobReload={load}
                onSwitchClassic={() => {
                  try { localStorage.setItem("insp_flow_mode", "classic"); } catch {}
                  window.location.reload();
                }}
              />
            );
          })()
        )}
        {timeOpen && (
          <TimeAdjustModal
            job={job}
            workers={workers}
            onClose={() => setTimeOpen(false)}
            onSaved={async () => { setTimeOpen(false); await load(); }}
          />
        )}
        {estimatorOpen && (
          <EstimatorModal
            initialTitle={job.title}
            initialDescription={job.description}
            jobId={job.id}
            onClose={() => setEstimatorOpen(false)}
            onApplied={load}
          />
        )}

        {ownerNoticeOpen && (
          <OwnerNoticeModal
            jobId={job.id}
            onClose={() => setOwnerNoticeOpen(false)}
            onSent={load}
          />
        )}

        {resolveTargetId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white w-full max-w-md rounded-2xl border-4 border-red-600 shadow-2xl overflow-hidden" data-testid="ar-resolve-modal">
              <div className="p-4 border-b-2 border-red-100 bg-red-50">
                <div className="text-[10px] font-black uppercase tracking-widest text-red-700">Access Request</div>
                <div className="font-display font-black text-xl text-red-800">Mark Resolved</div>
              </div>
              <div className="p-4 space-y-3">
                <label className="block">
                  <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">
                    What did you do? (worker will see this)
                  </span>
                  <textarea value={resolveText} onChange={e => setResolveText(e.target.value)}
                    data-testid="ar-resolve-text" rows={3} maxLength={500}
                    placeholder="Sent updated code 5588. Called tenant, they're on their way."
                    className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] resize-none text-sm"/>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => { setResolveTargetId(null); setResolveText(""); }}
                    data-testid="ar-resolve-cancel"
                    className="h-11 border-2 border-[#0f172a] bg-white font-black uppercase text-xs tracking-wider">
                    Cancel
                  </button>
                  <button onClick={resolveAR} disabled={resolving} data-testid="ar-resolve-confirm"
                    className="tactile-btn h-11 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-xs tracking-wider disabled:opacity-50">
                    {resolving ? "Resolving…" : "Send & Resolve"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ---- Admin/Office Close Job Panel ----
function ClosePanel({ job, onDone }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(job.completion_notes || "");
  const [busy, setBusy] = useState(false);
  const done = job.status === "completed";
  const isFollowUp = job.status === "follow_up";

  const submit = async (status) => {
    if (!notes.trim()) { toast.error("Add a note before closing the job"); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/complete`, { status, notes: notes.trim() });
      toast.success(status === "completed" ? "Job closed" : "Marked follow-up");
      setOpen(false);
      if (onDone) await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update job");
    } finally { setBusy(false); }
  };

  const reopen = async () => {
    if (!window.confirm("Reopen this job? Status goes back to In Progress and clears the completion.")) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/reopen`);
      toast.success("Reopened — back to work");
      if (onDone) await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reopen");
    } finally { setBusy(false); }
  };

  if (done || isFollowUp) {
    return (
      <div className="mt-4 p-3 border-2 border-green-600 bg-green-50" data-testid="close-panel-done">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-black uppercase tracking-widest text-green-800">
            {done ? "Job Closed" : "Follow-up Needed"}
          </div>
          {job.completed_at && <div className="text-[10px] font-bold text-green-700">{new Date(job.completed_at).toLocaleDateString()}</div>}
        </div>
        {job.completion_notes && <div className="mt-2 text-sm whitespace-pre-wrap">{job.completion_notes}</div>}
        <button
          type="button"
          onClick={reopen}
          disabled={busy}
          data-testid="admin-reopen-job-btn"
          className="mt-3 w-full h-10 bg-slate-700 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-xs disabled:opacity-50 border-2 border-[#0f172a]"
        >
          ↺ Reopen Job
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="close-job-btn"
        className="mt-2 tactile-btn w-full h-11 bg-green-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-2 hover:bg-green-700">
        <CheckCircle weight="fill" size={14}/> Close / Complete Job
      </button>
    );
  }

  return (
    <div className="mt-3 p-3 border-2 border-[#0f172a] bg-slate-50" data-testid="close-panel-open">
      <div className="text-xs font-black uppercase tracking-widest">Closing Notes <span className="text-red-500">*</span></div>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
        data-testid="close-notes-textarea"
        placeholder="Summary of work performed, parts used, next steps, etc."
        className="mt-2 w-full px-3 py-2 border-2 border-[#0f172a] text-sm font-medium resize-none"/>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <button onClick={() => setOpen(false)} disabled={busy} data-testid="close-cancel-btn"
          className="h-10 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-wider bg-white disabled:opacity-50">
          Cancel
        </button>
        <button onClick={() => submit("follow_up")} disabled={busy} data-testid="close-followup-btn"
          className="h-10 bg-amber-500 text-white font-black uppercase text-[10px] tracking-wider disabled:opacity-50">
          Follow-up
        </button>
        <button onClick={() => submit("completed")} disabled={busy} data-testid="close-complete-btn"
          className="h-10 bg-green-600 text-white font-black uppercase text-[10px] tracking-wider disabled:opacity-50">
          {busy ? "…" : "Complete"}
        </button>
      </div>
    </div>
  );
}

// ------ Time Adjust Modal (Admin) ------
function TimeAdjustModal({ job, workers, onClose, onSaved }) {
  // datetime-local <input> shows LOCAL time. To edit → convert stored UTC iso
  // to a local wall-clock string. On save, do the reverse.
  const toLocal = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (_) { return ""; }
  };
  // Bug fix Sep 2026 — was sending raw "YYYY-MM-DDTHH:MM" (no TZ), backend
  // treated it as UTC → 3:07 PM EDT saved as 15:07 UTC → reads back as
  // 11:07 AM EDT. Now round-trip through Date() so local input becomes
  // proper UTC ISO like "2026-09-21T19:07:00.000Z".
  const toUtcIso = (localStr) => {
    if (!localStr) return null;
    const d = new Date(localStr); // browser reads local wall-clock time
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };
  const [f, setF] = useState({
    clock_in_at: toLocal(job.clock_in_at),
    clock_out_at: toLocal(job.clock_out_at),
  });
  const [extras, setExtras] = useState([]);
  const [busy, setBusy] = useState(false);

  const addRow = () => setExtras(prev => [...prev, { user_id: "", clock_in_at: "", clock_out_at: "" }]);
  const setRow = (i, k, v) => setExtras(prev => prev.map((r, ix) => ix === i ? { ...r, [k]: v } : r));
  const rmRow = (i) => setExtras(prev => prev.filter((_, ix) => ix !== i));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        clear_clock_in: !f.clock_in_at,
        clear_clock_out: !f.clock_out_at,
      };
      if (f.clock_in_at) payload.clock_in_at = toUtcIso(f.clock_in_at);
      if (f.clock_out_at) payload.clock_out_at = toUtcIso(f.clock_out_at);
      const validExtras = extras.filter(e => e.clock_in_at);
      if (validExtras.length) {
        payload.additional_entries = validExtras.map(e => ({
          user_id: e.user_id || null,
          user_name: e.user_id ? (workers.find(w => w.id === e.user_id)?.name || "") : "",
          clock_in_at: toUtcIso(e.clock_in_at),
          clock_out_at: toUtcIso(e.clock_out_at),
        }));
      }
      await api.patch(`/jobs/${job.id}/time`, payload);
      toast.success("Times updated");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update times");
    } finally { setBusy(false); }
  };

  const delEntry = async (idx) => {
    if (!confirm("Remove this time entry?")) return;
    try {
      await api.delete(`/jobs/${job.id}/time-entry/${idx}`);
      toast.success("Entry removed");
      await onSaved();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const existingExtras = (job.time_entries || []).slice(1);
  const hoursFrom = (a, b) => {
    if (!a || !b) return "—";
    try {
      const diff = (new Date(b) - new Date(a)) / 3600000;
      return `${diff.toFixed(2)}h`;
    } catch (_) { return "—"; }
  };
  const primaryHours = hoursFrom(f.clock_in_at, f.clock_out_at);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="w-full max-w-lg bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6 my-8"
        data-testid="time-adjust-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-2xl">Adjust Times</h3>
          <button type="button" onClick={onClose} className="h-8 w-8 border-2 border-[#0f172a] font-black">×</button>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-medium">
          Fix a missed clock-in / out, or back-fill hours for an old job.
        </p>

        <div className="mt-4 p-3 border-2 border-[#0f172a] bg-slate-50 space-y-3">
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-600">
            Primary — {job.assigned_to_name || "Unassigned"}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Clock-In</span>
              <input type="datetime-local" value={f.clock_in_at} data-testid="time-primary-in"
                onChange={e => setF({ ...f, clock_in_at: e.target.value })}
                className="mt-1 w-full h-11 px-2 border-2 border-[#0f172a] font-mono text-sm"/>
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Clock-Out</span>
              <input type="datetime-local" value={f.clock_out_at} data-testid="time-primary-out"
                onChange={e => setF({ ...f, clock_out_at: e.target.value })}
                className="mt-1 w-full h-11 px-2 border-2 border-[#0f172a] font-mono text-sm"/>
            </label>
          </div>
          <div className="text-[11px] font-bold text-emerald-700">Duration: {primaryHours}</div>
        </div>

        {existingExtras.length > 0 && (
          <div className="mt-3 p-3 border-2 border-slate-300 bg-white">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">
              Existing Extra Entries · {existingExtras.length}
            </div>
            <ul className="space-y-1">
              {existingExtras.map((e, i) => (
                <li key={i} className="flex items-center justify-between gap-2 text-xs" data-testid={`existing-time-${i}`}>
                  <div className="flex-1 min-w-0 truncate">
                    <b>{e.user_name || "—"}:</b> {hoursFrom(e.clock_in_at, e.clock_out_at)}
                    <span className="text-slate-500 ml-1">({e.clock_in_at ? new Date(e.clock_in_at).toLocaleString() : "—"})</span>
                  </div>
                  <button type="button" onClick={() => delEntry(i + 1)} data-testid={`del-extra-time-${i}`}
                    className="h-7 w-7 border-2 border-red-400 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center">
                    <Trash weight="bold" size={12}/>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-3 p-3 border-2 border-dashed border-slate-400 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-600">
              Add Extra Time Entries · {extras.length}
            </div>
            <button type="button" onClick={addRow} data-testid="add-extra-time-btn"
              className="h-8 px-2 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-wider bg-white hover:bg-slate-100 inline-flex items-center gap-1">
              <Plus weight="bold" size={12}/> Add
            </button>
          </div>
          <div className="mt-1 text-[10px] text-slate-500 font-medium">
            For helpers, second-day visits, or back-filling old jobs.
          </div>
          {extras.map((r, i) => (
            <div key={i} className="mt-2 p-2 border border-slate-300 bg-white space-y-2" data-testid={`extra-row-${i}`}>
              <div className="flex items-center gap-2">
                <select value={r.user_id} onChange={e => setRow(i, "user_id", e.target.value)}
                  data-testid={`extra-user-${i}`}
                  className="flex-1 h-9 px-2 border-2 border-[#0f172a] bg-white text-xs">
                  <option value="">— Pick tech —</option>
                  {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <button type="button" onClick={() => rmRow(i)} data-testid={`rm-extra-${i}`}
                  className="h-9 w-9 border-2 border-red-400 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center">
                  <Trash weight="bold" size={12}/>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="datetime-local" value={r.clock_in_at} data-testid={`extra-in-${i}`}
                  onChange={e => setRow(i, "clock_in_at", e.target.value)}
                  className="h-9 px-2 border-2 border-[#0f172a] font-mono text-xs"/>
                <input type="datetime-local" value={r.clock_out_at} data-testid={`extra-out-${i}`}
                  onChange={e => setRow(i, "clock_out_at", e.target.value)}
                  className="h-9 px-2 border-2 border-[#0f172a] font-mono text-xs"/>
              </div>
              <div className="text-[10px] font-bold text-emerald-700">{hoursFrom(r.clock_in_at, r.clock_out_at)}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={busy} data-testid="time-adjust-save-btn"
            className="tactile-btn flex-1 h-11 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider disabled:opacity-60">
            {busy ? "Saving…" : "Save Times"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ------ Edit Job Modal (Admin) ------
function MaterialsEditor({ job, onReload }) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [cost, setCost] = useState("");
  const [store, setStore] = useState("");
  const [busy, setBusy] = useState(false);
  const items = job.materials || [];
  const total = items.reduce((s, m) => s + (m.quantity || 0) * (m.unit_cost || 0), 0);
  const add = async () => {
    if (!name.trim()) { toast.error("Item name required"); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/materials`, {
        name: name.trim(), quantity: Number(qty) || 1,
        unit_cost: Number(cost) || 0, store: store.trim(),
      });
      setName(""); setQty("1"); setCost(""); setStore("");
      await onReload();
      toast.success("Material added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this material?")) return;
    try {
      await api.delete(`/jobs/${job.id}/materials/${id}`);
      await onReload();
      toast.success("Removed");
    } catch (e) { toast.error("Could not remove"); }
  };
  return (
    <div className="mt-6" data-testid="admin-materials-panel">
      <h3 className="font-display font-black text-lg uppercase">Materials</h3>
      {items.length > 0 && (
        <table className="mt-3 w-full text-sm">
          <thead><tr className="text-left text-xs font-black uppercase text-zinc-500"><th>Item</th><th>Qty</th><th>Unit $</th><th>Store</th><th className="text-right">Total</th><th></th></tr></thead>
          <tbody>
            {items.map(m => (
              <tr key={m.id} className="border-t border-zinc-200">
                <td className="py-2 font-bold">{m.name}</td>
                <td>{m.quantity}</td><td>${(m.unit_cost || 0).toFixed(2)}</td><td>{m.store}</td>
                <td className="text-right font-bold">${((m.quantity || 0) * (m.unit_cost || 0)).toFixed(2)}</td>
                <td className="text-right">
                  <button onClick={() => remove(m.id)} data-testid={`del-mat-${m.id}`}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash weight="bold" size={14}/></button>
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-[#0f172a] font-black">
              <td className="py-2" colSpan={4}>TOTAL</td>
              <td className="text-right">${total.toFixed(2)}</td><td/>
            </tr>
          </tbody>
        </table>
      )}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-5 gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Item name"
          data-testid="mat-name" className="h-10 px-2 border-2 border-[#0f172a] text-sm sm:col-span-2"/>
        <input value={qty} onChange={e => setQty(e.target.value)} placeholder="Qty" type="number" step="0.1"
          data-testid="mat-qty" className="h-10 px-2 border-2 border-[#0f172a] text-sm font-mono"/>
        <input value={cost} onChange={e => setCost(e.target.value)} placeholder="Unit $" type="number" step="0.01"
          data-testid="mat-cost" className="h-10 px-2 border-2 border-[#0f172a] text-sm font-mono"/>
        <input value={store} onChange={e => setStore(e.target.value)} placeholder="Store"
          data-testid="mat-store" className="h-10 px-2 border-2 border-[#0f172a] text-sm"/>
      </div>
      <button onClick={add} disabled={busy || !name.trim()} data-testid="mat-add-btn"
        className="tactile-btn mt-2 h-10 px-4 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-widest disabled:opacity-50">
        {busy ? "Adding…" : "+ Add Material"}
      </button>
    </div>
  );
}

function AdminWritePanel({ job, onReload }) {
  const [text, setText] = useState(job.work_performed || job.completion_notes || "");
  const [polished, setPolished] = useState(job.work_performed_polished || "");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/jobs/${job.id}/work-performed`, { text });
      toast.success("Write-up saved");
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };
  const polish = async () => {
    if (!text.trim()) { toast.error("Type rough notes first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/polish", { text, job_id: job.id });
      setPolished(data.polished);
      toast.success("AI polished");
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Polish failed"); }
    finally { setBusy(false); }
  };
  const saveAsFinal = async () => {
    setSaving(true);
    try {
      // Push the polished write-up back to work_performed so it becomes the
      // official record on the job. Never touches status — the "Complete"
      // action lives in its own button. Fixes the "Input should be 'completed'
      // or 'follow_up'" error when the job is in_progress / assigned.
      const finalText = (polished || text || "").trim();
      if (!finalText) { toast.error("Nothing to push"); setSaving(false); return; }
      await api.post(`/jobs/${job.id}/work-performed`, { text: finalText });
      toast.success("Finish notes saved to job");
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-6 rugged-card p-4" data-testid="admin-write-panel">
      <div className="flex items-center gap-2">
        <Sparkle weight="bold" size={18}/>
        <h3 className="font-display font-black text-lg uppercase">Office Write-Up / Finish Notes</h3>
      </div>
      <p className="mt-1 text-xs text-slate-500 font-medium">
        Type rough notes → tap AI Polish → optionally push into the job's Finish Notes.
      </p>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
        data-testid="admin-write-input"
        placeholder="Replaced garbage disposal, tested twice, no leaks. Owner notified."
        className="mt-2 w-full px-3 py-2 border-2 border-[#0f172a] text-sm"/>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button onClick={save} disabled={saving || !text.trim()} data-testid="admin-write-save"
          className="h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest disabled:opacity-50">
          {saving ? "Saving…" : "Save Notes"}
        </button>
        <button onClick={polish} disabled={busy || !text.trim()} data-testid="admin-write-polish"
          className="tactile-btn h-11 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Sparkle weight="fill" size={14}/> {busy ? "Polishing…" : "AI Polish"}
        </button>
      </div>
      {polished && (
        <div className="mt-3 p-3 bg-[#ecfeff] border-2 border-[#00C4CC]" data-testid="admin-polished">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#0e7490]">AI Write-Up</div>
          <p className="mt-1 whitespace-pre-wrap text-sm">{polished}</p>
          <button onClick={saveAsFinal} disabled={saving} data-testid="admin-save-as-finish"
            className="mt-2 tactile-btn h-9 px-3 bg-[#00C4CC] text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50">
            Push into Finish Notes
          </button>
        </div>
      )}
    </div>
  );
}

function ClickUpMirrorSection({ job }) {
  const notes = (job?.notes || []).filter(n => n?.kind === "clickup_attachment" || n?.kind === "clickup_completion_notes");
  if (notes.length === 0) return null;
  const files = notes.filter(n => n.kind === "clickup_attachment");
  const completionNotes = notes.filter(n => n.kind === "clickup_completion_notes");
  return (
    <div className="mt-6 p-4 border-2 border-fuchsia-500 bg-fuchsia-50 rounded" data-testid="clickup-mirror-section">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-black uppercase tracking-widest bg-fuchsia-600 text-white px-2 py-0.5 rounded">
          ClickUp Mirror
        </span>
        <span className="text-xs text-fuchsia-800">
          {files.length ? `${files.length} file${files.length === 1 ? "" : "s"}` : ""}
          {files.length && completionNotes.length ? " · " : ""}
          {completionNotes.length ? `${completionNotes.length} note${completionNotes.length === 1 ? "" : "s"}` : ""}
        </span>
      </div>
      {completionNotes.length > 0 && (
        <div className="mt-3 space-y-2">
          {completionNotes.map((n, i) => (
            <div key={i} className="p-2 bg-white border border-fuchsia-300 rounded" data-testid={`clickup-completion-note-${i}`}>
              <div className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700">Completion Notes (from ClickUp)</div>
              <p className="mt-1 text-sm whitespace-pre-wrap">
                {(n.text || "").replace(/^\[ClickUp · Completion Notes\]\s*/, "").trim()}
              </p>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {files.map((n, i) => {
            const stored = n.cu_attachment_url || "";
            // Same double-/api/ guard as the photo grid — `API` already ends
            // in `/api`, so a stored URL like `/api/clickup/…` needs the
            // BACKEND ROOT prepended (not `API`) to avoid /api/api/… 404s.
            const href = stored
              ? (stored.startsWith("http") ? stored
                 : stored.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${stored}`
                 : `${API}${stored.startsWith("/") ? "" : "/"}${stored}`)
              : "";
            const title = n.cu_attachment_title || n.cu_attachment_ext || "file";
            const ext = (n.cu_attachment_ext || "").toLowerCase();
            const isPdf = ext === "pdf";
            return (
              <a key={i} href={href} target="_blank" rel="noreferrer"
                 data-testid={`clickup-file-${i}`}
                 className="p-2 bg-white border-2 border-fuchsia-300 hover:border-fuchsia-600 rounded flex items-center gap-2 transition-colors">
                <span className="inline-flex items-center justify-center w-8 h-8 bg-fuchsia-100 text-fuchsia-800 font-black text-[10px] uppercase tracking-widest rounded flex-shrink-0">
                  {isPdf ? "PDF" : (ext || "?").slice(0, 3).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-xs truncate">{title}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {n.at ? new Date(n.at).toLocaleDateString() : ""} · tap to open
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ClickUpInspectModal({ data, onClose }) {
  const cf = data.task_attachments_sample || [];
  const comments = data.comment_attachments_sample || [];
  const raw = JSON.stringify(data, null, 2);
  const copyRaw = async () => {
    try { await navigator.clipboard.writeText(raw); toast.success("Copied raw JSON"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={onClose}
         data-testid="clickup-inspect-modal">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-lg border-2 border-slate-900" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-700">ClickUp Debug</div>
            <div className="font-black text-lg">Task: {data.task_name || "?"}</div>
            <div className="text-xs text-slate-500 font-mono">{data.task_id || "no id"} · status={data.status || "?"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={copyRaw} data-testid="cu-inspect-copy" className="h-8 px-2 border-2 border-slate-300 hover:bg-slate-50 text-[10px] font-black uppercase tracking-widest rounded">Copy JSON</button>
            <button onClick={onClose} data-testid="cu-inspect-close" className="h-8 px-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded">Close</button>
          </div>
        </div>
        {!data.ok ? (
          <div className="p-4 text-red-700 font-black">{data.message || "No ClickUp task found"}</div>
        ) : (
          <div className="p-4 space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-slate-50 border-2 border-slate-200 rounded">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Task Files</div>
                <div className="text-2xl font-black">{data.task_attachments_count ?? 0}</div>
              </div>
              <div className="p-3 bg-slate-50 border-2 border-slate-200 rounded">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Comments</div>
                <div className="text-2xl font-black">{data.comments_count ?? 0}</div>
              </div>
              <div className="p-3 bg-slate-50 border-2 border-slate-200 rounded">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Custom Fields</div>
                <div className="text-2xl font-black">{data.custom_fields_count ?? 0}</div>
              </div>
            </div>
            {/* FR-side view — what's actually stored on this WO */}
            <div className="border-2 border-emerald-500 bg-emerald-50 rounded p-3 space-y-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">
                Stored in FR on this WO
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                <span>after_photos: <b>{data.fr_after_photos_count || 0}</b></span>
                <span>clickup_attachment notes: <b>{data.fr_clickup_attachment_notes_count || 0}</b></span>
              </div>
              {(data.fr_after_photos_sample || []).length > 0 && (
                <div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-600 mt-1">Photo URLs (tap to test)</div>
                  {(data.fr_after_photos_sample || []).map((p, i) => (
                    <div key={i} className="mt-1 flex items-center gap-2">
                      <a href={p.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-700 underline truncate max-w-full">{p.url}</a>
                      <img src={p.url} alt="test" className="w-8 h-8 object-cover border border-slate-300" onError={(e) => { e.target.style.border = '2px solid red'; }}/>
                    </div>
                  ))}
                </div>
              )}
              {(data.fr_clickup_attachment_notes_sample || []).length > 0 && (
                <div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-600 mt-1">File notes (tap to open)</div>
                  {(data.fr_clickup_attachment_notes_sample || []).map((n, i) => (
                    <div key={i} className="mt-1">
                      <a href={n.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-700 underline">{n.title || n.ext || "file"} — {n.url}</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cf.length > 0 && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Task-level attachment sample</div>
                <pre className="text-[10px] p-3 bg-slate-900 text-emerald-300 rounded overflow-x-auto max-h-40">
{JSON.stringify(cf, null, 2)}
                </pre>
              </div>
            )}
            {comments.length > 0 && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Comments · files sample</div>
                <pre className="text-[10px] p-3 bg-slate-900 text-cyan-300 rounded overflow-x-auto max-h-52">
{JSON.stringify(comments, null, 2)}
                </pre>
              </div>
            )}
            {(data.custom_fields_summary || []).length > 0 && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Custom Fields <span className="text-slate-400 font-medium">· where AppFolio-synced tasks store invoices + photos</span></div>
                <div className="border-2 border-slate-200 rounded overflow-hidden">
                  <table className="w-full text-[10px]">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-black uppercase tracking-widest">Name</th>
                        <th className="text-left px-2 py-1.5 font-black uppercase tracking-widest">Type</th>
                        <th className="text-left px-2 py-1.5 font-black uppercase tracking-widest">Value?</th>
                        <th className="text-left px-2 py-1.5 font-black uppercase tracking-widest">Sample</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.custom_fields_summary || []).map((cf, i) => (
                        <tr key={cf.id || i} className={cf.has_value ? "bg-emerald-50" : "opacity-60"}>
                          <td className="px-2 py-1 font-black">{cf.name || "—"}</td>
                          <td className="px-2 py-1 font-mono">{cf.type}</td>
                          <td className="px-2 py-1">{cf.has_value ? "✓" : "—"}</td>
                          <td className="px-2 py-1 font-mono text-slate-600 max-w-[300px] truncate">
                            {cf.value_sample ? (Array.isArray(cf.value_sample) ? JSON.stringify(cf.value_sample) : String(cf.value_sample)) : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Full raw response</div>
              <pre className="text-[9px] p-3 bg-slate-900 text-slate-100 rounded overflow-x-auto max-h-80">
{raw}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function EditJobModal({ job, onClose, onSaved }) {
  const RoomConfigFields = ({ f, setF, testidPrefix }) => (
    <>
      <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-2">Unit Layout · Inspection rooms</div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Bedrooms">
          <input type="number" min="0" max="10" value={f.rc_bedrooms} data-testid={`${testidPrefix}-bedrooms`}
            onChange={e => setF({ ...f, rc_bedrooms: e.target.value === "" ? "" : Number(e.target.value) })}
            placeholder="3" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
        </Field>
        <Field label="Full Baths">
          <input type="number" min="0" max="10" value={f.rc_bathrooms} data-testid={`${testidPrefix}-bathrooms`}
            onChange={e => setF({ ...f, rc_bathrooms: e.target.value === "" ? "" : Number(e.target.value) })}
            placeholder="2" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
        </Field>
        <Field label={<span title="Toilet + vanity, no shower">Half Baths</span>}>
          <input type="number" min="0" max="10" value={f.rc_half_baths ?? ""} data-testid={`${testidPrefix}-half-baths`}
            onChange={e => setF({ ...f, rc_half_baths: e.target.value === "" ? "" : Number(e.target.value) })}
            placeholder="0" className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
        </Field>
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5 text-sm">
        {[["has_kitchen", "Kitchen"], ["has_living", "Living Room"], ["has_dining", "Dining Room"],
          ["has_hallway", "Hallway"], ["has_stairs", "Stairs"],
          ["has_basement", "Basement"], ["has_garage", "Garage"], ["has_yard", "Yard"],
          ["has_deck", "Deck"], ["has_porch", "Porch"],
          ["has_laundry", "Laundry Room"], ["has_utility", "Utility / Mudroom"],
          ["has_office", "Office"], ["has_foyer", "Foyer / Entry"],
          ["has_attic", "Attic"], ["has_pantry", "Pantry"], ["has_closet", "Closet"]].map(([k, lbl]) => (
          <label key={k} className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!f[k]} onChange={e => setF({ ...f, [k]: e.target.checked })}
              data-testid={`${testidPrefix}-${k}`} className="h-4 w-4 accent-[#0f172a]"/>
            <span className="font-medium text-xs">{lbl}</span>
          </label>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-slate-500 font-medium italic">
        Add or remove rooms anytime. Existing inspection data on kept rooms is preserved.
      </div>
    </>
  );
  const { tenant } = useTenantCtx();
  const turnsEnabled = tenant?.turns_enabled !== false;
  const prevRc = job.room_config || {};
  const [f, setF] = useState({
    title: job.title || "",
    description: job.description || "",
    property_address: job.property_address || "",
    customer_name: job.customer_name || "",
    customer_phone: job.customer_phone || "",
    priority: job.priority || "normal",
    scheduled_for: job.scheduled_for || "",
    entry_code: job.entry_code || "",
    lockbox_code: job.lockbox_code || "",
    entry_notes: job.entry_notes || "",
    work_order_number: job.work_order_number || "",
    category: job.category || "",
    is_turn: !!job.is_turn,
    turn_flat_price: job.turn_flat_price ?? 0,
    turn_price_tbd: (job.is_turn && !job.turn_flat_price) || false,
    rc_bedrooms: prevRc.bedrooms ?? "",
    rc_bathrooms: prevRc.bathrooms ?? "",
    rc_half_baths: prevRc.half_baths ?? "",
    has_kitchen: prevRc.has_kitchen ?? true,
    has_living: prevRc.has_living ?? true,
    has_dining: prevRc.has_dining ?? false,
    has_hallway: prevRc.has_hallway ?? false,
    has_stairs: prevRc.has_stairs ?? false,
    has_basement: prevRc.has_basement ?? false,
    has_garage: prevRc.has_garage ?? false,
    has_yard: prevRc.has_yard ?? false,
    has_deck: prevRc.has_deck ?? false,
    has_porch: prevRc.has_porch ?? false,
    has_laundry: prevRc.has_laundry ?? false,
    has_utility: prevRc.has_utility ?? false,
    has_office: prevRc.has_office ?? false,
    has_foyer: prevRc.has_foyer ?? false,
    has_attic: prevRc.has_attic ?? false,
    has_pantry: prevRc.has_pantry ?? false,
    has_closet: prevRc.has_closet ?? false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      const orig = {
        ...job,
        is_turn: !!job.is_turn,
        turn_flat_price: job.turn_flat_price ?? 0,
      };
      const rcKeys = ["rc_bedrooms", "rc_bathrooms", "rc_half_baths", "has_kitchen", "has_living", "has_dining",
                      "has_hallway", "has_stairs",
                      "has_basement", "has_garage", "has_yard", "has_deck", "has_porch",
                      "has_laundry", "has_utility", "has_office", "has_foyer", "has_attic", "has_pantry", "has_closet"];
      Object.keys(f).forEach(k => {
        if (k === "turn_price_tbd") return;
        if (rcKeys.includes(k)) return;
        if (k === "is_turn") {
          if (Boolean(f[k]) !== Boolean(orig[k])) payload[k] = f[k];
          return;
        }
        if (k === "turn_flat_price") {
          const val = f.turn_price_tbd ? 0 : (Number(f[k]) || 0);
          if (val !== (Number(orig[k]) || 0)) payload[k] = val;
          return;
        }
        if ((f[k] || "") !== (orig[k] || "")) payload[k] = f[k];
      });
      if (payload.is_turn === true && payload.turn_flat_price === undefined) {
        payload.turn_flat_price = f.turn_price_tbd ? 0 : (Number(f.turn_flat_price) || 0);
      }
      // Send room_config when Turn active OR when this is an inspection_type job (decoupled inspections still need editable rooms)
      const isInspectionJob = !!job.inspection_type;
      const turnActive = f.is_turn;
      const rcChanged = rcKeys.some(k => {
        const cur = f[k];
        const prev = k === "rc_bedrooms" ? (prevRc.bedrooms ?? "") :
                     k === "rc_bathrooms" ? (prevRc.bathrooms ?? "") :
                     k === "rc_half_baths" ? (prevRc.half_baths ?? "") :
                     (prevRc[k.replace("has_", "has_")] ?? (k === "has_kitchen" || k === "has_living"));
        return cur !== prev;
      });
      if ((turnActive || isInspectionJob) && (payload.is_turn === true || rcChanged || !job.room_config)) {
        payload.room_config = {
          bedrooms: Number(f.rc_bedrooms) || 0,
          bathrooms: Number(f.rc_bathrooms) || 0,
          half_baths: Number(f.rc_half_baths) || 0,
          has_kitchen: !!f.has_kitchen, has_living: !!f.has_living,
          has_dining: !!f.has_dining,
          has_hallway: !!f.has_hallway, has_stairs: !!f.has_stairs,
          has_basement: !!f.has_basement,
          has_garage: !!f.has_garage, has_yard: !!f.has_yard,
          has_deck: !!f.has_deck, has_porch: !!f.has_porch,
          has_laundry: !!f.has_laundry, has_utility: !!f.has_utility,
          has_office: !!f.has_office, has_foyer: !!f.has_foyer,
          has_attic: !!f.has_attic, has_pantry: !!f.has_pantry,
          has_closet: !!f.has_closet,
          custom_rooms: [],
        };
      }
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        onClose();
        return;
      }
      await api.patch(`/jobs/${job.id}`, payload);
      toast.success(payload.is_turn === true && !orig.is_turn ? "Converted to Turn — rooms ready" : "Job updated");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update job");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="edit-job-modal">
      <div className="w-full max-w-2xl rugged-card p-6 bg-white my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-2xl">Edit Job</h2>
          <button onClick={onClose} data-testid="edit-job-close" className="text-slate-500 hover:text-[#0f172a]">
            <X weight="bold" size={22}/>
          </button>
        </div>

        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Title *">
                <input required value={f.title} onChange={e => setF({ ...f, title: e.target.value })}
                  data-testid="edit-title" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
              </Field>
            </div>
            <Field label="Work Order #">
              <input value={f.work_order_number} onChange={e => setF({ ...f, work_order_number: e.target.value })}
                data-testid="edit-wo-number" placeholder="WO-1234"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-mono"/>
            </Field>
          </div>
          <Field label="Property Address *">
            <input required value={f.property_address} onChange={e => setF({ ...f, property_address: e.target.value })}
              data-testid="edit-address" placeholder="1234 Main St" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Field label="Unit">
              <input value={f.unit || ""} onChange={e => setF({ ...f, unit: e.target.value })}
                data-testid="edit-unit" placeholder="Apt / #" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
            </Field>
            <Field label="City">
              <input value={f.city || ""} onChange={e => setF({ ...f, city: e.target.value })}
                data-testid="edit-city" placeholder="Philadelphia" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
            </Field>
            <Field label="State">
              <input value={f.state || ""} onChange={e => setF({ ...f, state: e.target.value.toUpperCase().slice(0, 2) })}
                maxLength={2} data-testid="edit-state" placeholder="PA" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium uppercase"/>
            </Field>
            <Field label="Zip">
              <input value={f.zip_code || ""} onChange={e => setF({ ...f, zip_code: e.target.value.replace(/[^0-9\-]/g, "").slice(0, 10) })}
                inputMode="numeric" data-testid="edit-zip" placeholder="19125" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium font-mono"/>
            </Field>
          </div>
          <Field label="Description / Work Details">
            <textarea rows={3} value={f.description} onChange={e => setF({ ...f, description: e.target.value })}
              data-testid="edit-description" className="w-full px-3 py-2 border-2 border-[#0f172a] text-sm font-medium resize-none"/>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Customer Name">
              <input value={f.customer_name} onChange={e => setF({ ...f, customer_name: e.target.value })}
                data-testid="edit-customer-name" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
            </Field>
            <Field label="Customer Phone">
              <input value={f.customer_phone} onChange={e => setF({ ...f, customer_phone: e.target.value })}
                data-testid="edit-customer-phone" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Priority">
              <select value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })}
                data-testid="edit-priority" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium bg-white">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </Field>
            <Field label="Category">
              <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })}
                data-testid="edit-category" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium bg-white">
                <option value="">— Uncategorized —</option>
                {Object.keys(CATEGORY_COLORS).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Scheduled Date">
              <input type="date" value={f.scheduled_for ? f.scheduled_for.slice(0, 10) : ""}
                onChange={e => setF({ ...f, scheduled_for: e.target.value })}
                data-testid="edit-scheduled" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Gate / Entry Code">
              <input value={f.entry_code} onChange={e => setF({ ...f, entry_code: e.target.value })}
                data-testid="edit-entry-code" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
            </Field>
            <Field label="Lockbox Code">
              <input value={f.lockbox_code} onChange={e => setF({ ...f, lockbox_code: e.target.value })}
                data-testid="edit-lockbox" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
            </Field>
          </div>
          <Field label="Entry Notes">
            <textarea rows={2} value={f.entry_notes} onChange={e => setF({ ...f, entry_notes: e.target.value })}
              data-testid="edit-entry-notes" placeholder="Where to park, dog on premises, etc."
              className="w-full px-3 py-2 border-2 border-[#0f172a] text-sm font-medium resize-none"/>
          </Field>

          {turnsEnabled && (
            <div className={`p-3 border-2 ${f.is_turn ? "border-[#0f172a] bg-amber-50" : "border-slate-300 bg-white"} rounded-lg`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!f.is_turn} data-testid="edit-is-turn"
                  onChange={e => setF(prev => ({ ...prev, is_turn: e.target.checked }))}
                  className="h-5 w-5 accent-[#0f172a]"/>
                <span className="font-black uppercase text-sm tracking-wider">{job.is_turn ? "This is a Turn" : "Convert to Turn"}</span>
                <span className="text-xs text-slate-500 font-medium">(deep clean, paint, repairs)</span>
              </label>
              {f.is_turn && (
                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!f.turn_price_tbd} data-testid="edit-turn-price-tbd"
                      onChange={e => setF(prev => ({ ...prev, turn_price_tbd: e.target.checked, turn_flat_price: e.target.checked ? 0 : prev.turn_flat_price }))}
                      className="h-4 w-4 accent-[#0f172a]"/>
                    <span className="text-xs font-black uppercase tracking-widest">Price N/A — sub will quote</span>
                  </label>
                  <Field label="Flat Price ($)">
                    <input type="number" step="0.01" min="0" value={f.turn_flat_price}
                      disabled={!!f.turn_price_tbd}
                      onChange={e => setF({ ...f, turn_flat_price: e.target.value })}
                      placeholder={f.turn_price_tbd ? "TBD" : "1500.00"} data-testid="edit-turn-price"
                      className="w-full h-11 px-3 border-2 border-[#0f172a] font-mono disabled:bg-slate-100 disabled:text-slate-400"/>
                  </Field>
                  <div className="pt-3 border-t-2 border-amber-200">
                    <RoomConfigFields f={f} setF={setF} testidPrefix="edit-rc"/>
                  </div>
                </div>
              )}
            </div>
          )}
          {!!job.inspection_type && !f.is_turn && (
            <div className="p-3 border-2 border-indigo-300 bg-indigo-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-black uppercase tracking-widest text-indigo-800">Inspection · {job.inspection_type.replace('_',' ')}</span>
              </div>
              <RoomConfigFields f={f} setF={setF} testidPrefix="edit-rc"/>
              <div className="mt-2 text-[11px] text-slate-500 font-medium italic">
                Edit which rooms this inspection covers. Save to update.
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={busy}
              data-testid="edit-cancel-btn"
              className="h-12 border-2 border-[#0f172a] font-black uppercase text-sm tracking-wider bg-white disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              data-testid="edit-save-btn"
              className="tactile-btn h-12 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider disabled:opacity-50">
              {busy ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SmsModal({ to, defaultText, onClose }) {
  const [text, setText] = useState(defaultText || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sms/send", { to, message: text });
      if (data.status === "sent") toast.success("SMS sent");
      else if (data.status === "mocked") toast.info("MOCKED — add Twilio credentials in Settings to send real texts");
      else toast.error(data.message || "SMS failed");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-display font-black text-2xl">Text Customer</h3>
        <p className="mt-1 text-sm text-slate-600">To: <span className="font-mono">{to || "—"}</span></p>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={5} data-testid="sms-textarea"
          className="mt-3 w-full px-3 py-2 border-2 border-[#0f172a] font-medium"/>
        <div className="mt-4 flex gap-2">
          <button onClick={onClose} className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase text-sm tracking-wider">Cancel</button>
          <button onClick={send} disabled={busy || !text.trim() || !to} data-testid="sms-send-btn"
            className="tactile-btn flex-1 h-11 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider disabled:opacity-50">
            {busy ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotoGrid({ title, items, jobId, onDelete }) {
  const [filter, setFilter] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [retagOpen, setRetagOpen] = useState(null);      // file_id of the photo whose popover is open
  const [retagBusy, setRetagBusy] = useState(false);
  if (!items || items.length === 0) return null;
  const allTags = Array.from(new Set(items.flatMap(p => p.tags || [])));
  const shown = filter ? items.filter(p => (p.tags || []).includes(filter)) : items;

  // Derive current bucket from the section title so the popover only offers
  // moves to the OTHER buckets. Keeps the UI clean and prevents no-op calls.
  const currentKind = /before/i.test(title) ? "before"
                    : /receipt/i.test(title) ? "receipt"
                    : "after";
  const otherKinds = [
    { k: "before", label: "Before" },
    { k: "after", label: "After" },
    { k: "receipt", label: "Receipt" },
  ].filter(o => o.k !== currentKind);

  const del = async (fileId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!fileId || !jobId) return;
    if (!window.confirm("Delete this file? This cannot be undone.")) return;
    setDeleting(fileId);
    try {
      await api.delete(`/jobs/${jobId}/photo/${fileId}`);
      toast.success("File deleted");
      if (onDelete) await onDelete();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally { setDeleting(null); }
  };

  const changeKind = async (fileId, toKind) => {
    if (!fileId || !jobId) return;
    setRetagBusy(true);
    try {
      await api.post(`/jobs/${jobId}/photos/change-kind`, { file_id: fileId, to_kind: toKind });
      toast.success(`Moved to ${toKind}`);
      setRetagOpen(null);
      if (onDelete) await onDelete();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Move failed");
    } finally { setRetagBusy(false); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-black text-lg uppercase">{title} ({shown.length}{filter ? ` of ${items.length}` : ""})</h3>
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <button onClick={() => setFilter(null)} className={`px-2 py-1 text-xs font-black uppercase tracking-wider border-2 border-[#0f172a] ${!filter ? "bg-[#0f172a] text-white" : "bg-white"}`}>All</button>
            {allTags.map(t => (
              <button key={t} onClick={() => setFilter(t)} className={`px-2 py-1 text-xs font-black uppercase tracking-wider border-2 border-[#0f172a] ${filter === t ? "bg-[#00C4CC] text-white" : "bg-white"}`}>{t}</button>
            ))}
          </div>
        )}
      </div>
      <div className="mt-3 grid grid-cols-4 gap-3">
        {shown.map((p, i) => {
          const isVideo = p.media_type === "video" || (p.content_type || "").startsWith("video/");
          // ClickUp-mirrored photos have `url` (proxy path). Locally uploaded
          // ones have `path` (served by /files/{path}). Prefer path; fall back
          // to url. Note: `API` already ends in `/api`, so if url starts with
          // `/api/…` we prepend the BACKEND ROOT (strip /api first) to avoid
          // double-api paths like /api/api/clickup/...
          const rawUrl = p.url && (
            p.url.startsWith("http") ? p.url :
            p.url.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${p.url}` :
            `${API}${p.url.startsWith("/") ? "" : "/"}${p.url}`
          );
          const src = p.path ? fileUrl(p.path) : rawUrl;
          return (
            <div key={p.file_id || p.clickup_attachment_id || i} className="relative aspect-square border-2 border-[#0f172a] overflow-hidden bg-slate-100 group" data-testid={`photo-${p.file_id || p.clickup_attachment_id || i}`}>
              <PhotoOpen photo={{...p, path: p.path || null, _fallbackUrl: src}} className="block w-full h-full">
                {isVideo ? (
                  <>
                    <video src={src} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="px-2 py-0.5 text-[10px] font-black uppercase bg-white text-[#0f172a]">▶ VIDEO</span>
                    </div>
                  </>
                ) : (
                  <img src={src} alt="" className="w-full h-full object-cover"/>
                )}
                {p.annotated && <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-black uppercase bg-[#00C4CC] text-white">Marked</span>}
                {p.source === "clickup" && p.clickup_task_id && (
                  <a href={`https://app.clickup.com/t/${p.clickup_task_id}`}
                    target="_blank" rel="noreferrer"
                    onClick={e => e.stopPropagation()}
                    title={`Auto-attached from ClickUp${p.filename ? " · " + p.filename : ""}${p.uploaded_at ? " · " + new Date(p.uploaded_at).toLocaleDateString() : ""}`}
                    data-testid={`clickup-badge-${p.file_id || i}`}
                    className={`absolute ${p.annotated ? "top-6" : "top-1"} left-1 px-1.5 py-0.5 text-[10px] font-black uppercase bg-fuchsia-600 hover:bg-fuchsia-700 text-white inline-flex items-center gap-1`}>
                    <ClipboardText weight="fill" size={10}/> ClickUp
                  </a>
                )}
                {(p.tags || []).length > 0 && (
                  <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5">
                    {(p.tags || []).slice(0, 2).map(t => (
                      <span key={t} className="px-1 py-0.5 text-[9px] font-black uppercase bg-[#0f172a] text-white truncate">{t}</span>
                    ))}
                  </div>
                )}
              </PhotoOpen>
              {jobId && p.file_id && (
                <>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRetagOpen(retagOpen === p.file_id ? null : p.file_id); }}
                    data-testid={`retag-photo-${p.file_id}`}
                    className="absolute top-1 right-9 h-7 w-7 flex items-center justify-center bg-fuchsia-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Change tag (before/after/receipt)">
                    <Sparkle weight="fill" size={14}/>
                  </button>
                  <button
                    onClick={(e) => del(p.file_id, e)}
                    disabled={deleting === p.file_id}
                    data-testid={`delete-photo-${p.file_id}`}
                    className="absolute top-1 right-1 h-7 w-7 flex items-center justify-center bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    title="Delete this file">
                    <Trash weight="fill" size={14}/>
                  </button>
                  {retagOpen === p.file_id && (
                    <div className="absolute inset-x-1 bottom-1 bg-white border-2 border-fuchsia-600 shadow-lg p-1.5 z-10" onClick={e => e.stopPropagation()} data-testid={`retag-popover-${p.file_id}`}>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Move to:</div>
                      <div className="flex gap-1">
                        {otherKinds.map(o => (
                          <button key={o.k} onClick={() => changeKind(p.file_id, o.k)} disabled={retagBusy}
                            data-testid={`retag-to-${o.k}-${p.file_id}`}
                            className="flex-1 h-7 px-1 text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 border border-fuchsia-400 disabled:opacity-40">
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Team page (Office Staff · Field Workers · Sub-Contractors) ----
function ClickUpAttachSummary({ job }) {
  const items = [
    ...(job.before_photos || []).map(p => ({ ...p, bucket: "before" })),
    ...(job.after_photos  || []).map(p => ({ ...p, bucket: "after"  })),
    ...(job.receipts      || []).map(p => ({ ...p, bucket: "receipt" })),
  ].filter(p => p.source === "clickup" && p.clickup_task_id);
  if (items.length === 0) return null;
  const byTask = items.reduce((acc, p) => {
    (acc[p.clickup_task_id] = acc[p.clickup_task_id] || []).push(p);
    return acc;
  }, {});
  const taskIds = Object.keys(byTask);
  const newest = items.reduce((a, b) => ((a.uploaded_at || "") > (b.uploaded_at || "") ? a : b));

  return (
    <div className="mt-6 p-3 border-2 border-fuchsia-300 bg-fuchsia-50 flex items-center gap-3 flex-wrap"
      data-testid="clickup-attach-summary">
      <ClipboardText weight="fill" size={18} className="text-fuchsia-600 flex-none"/>
      <div className="flex-1 min-w-0 text-xs">
        <div className="font-black uppercase tracking-widest text-[10px] text-fuchsia-800">
          Auto-Attached From ClickUp
        </div>
        <div className="text-slate-800 mt-0.5">
          <b>{items.length}</b> file{items.length === 1 ? "" : "s"} from <b>{taskIds.length}</b> task{taskIds.length === 1 ? "" : "s"}
          {newest?.uploaded_at && (
            <span className="ml-1 text-slate-500">· newest {new Date(newest.uploaded_at).toLocaleDateString()}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {taskIds.slice(0, 4).map(tid => (
          <a key={tid} href={`https://app.clickup.com/t/${tid}`} target="_blank" rel="noreferrer"
            data-testid={`clickup-task-link-${tid}`}
            className="h-7 px-2 bg-white border-2 border-fuchsia-400 hover:bg-fuchsia-100 text-fuchsia-800 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            Task · {byTask[tid].length} {byTask[tid].length === 1 ? "file" : "files"}
          </a>
        ))}
        {taskIds.length > 4 && (
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            + {taskIds.length - 4} more
          </span>
        )}
      </div>
    </div>
  );
}


function roleBadge(m) {
  if (m.platform_role === "super_admin") return { label: "Super Admin", cls: "bg-fuchsia-600 text-white" };
  if (m.role === "worker") return { label: "Field Worker", cls: "bg-[#00C4CC] text-white" };
  const or = m.office_role;
  if (!or) return { label: "Owner", cls: "bg-[#0f172a] text-white" };
  if (or === "partner") return { label: "Partner", cls: "bg-violet-700 text-white" };
  if (or === "manager") return { label: "Manager", cls: "bg-emerald-600 text-white" };
  return { label: "Office Staff", cls: "bg-amber-500 text-white" };
}

export function AdminWorkers() {
  useAccessLog("team");
  const { user } = useAuth();
  const isSuper = user?.platform_role === "super_admin";
  const canGrantEnterprise = isSuper || user?.is_founder;
  const [members, setMembers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const [entBusy, setEntBusy] = useState({});
  const load = async () => {
    const [u, s, t] = await Promise.all([
      api.get("/users"),
      api.get("/sub-contractors").catch(() => ({ data: [] })),
      api.get("/tenant").catch(() => ({ data: null })),
    ]);
    setMembers(u.data || []);
    setSubs(s.data || []);
    setTenant(t.data);
  };
  useEffect(() => { load(); }, []);

  const toggleEnterprise = async (m) => {
    const next = !m.enterprise_access;
    setEntBusy(b => ({ ...b, [m.id]: true }));
    try {
      const { data } = await api.patch(`/users/${m.id}/enterprise-access`, { enterprise_access: next });
      setMembers(list => list.map(x => x.id === m.id ? { ...x, enterprise_access: data.enterprise_access } : x));
      toast.success(next ? `Enterprise granted to ${m.name}` : `Enterprise revoked from ${m.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't update Enterprise access"));
    } finally {
      setEntBusy(b => ({ ...b, [m.id]: false }));
    }
  };

  const toggleSubcontractor = async (m) => {
    const next = !m.is_subcontractor;
    setEntBusy(b => ({ ...b, [`sub-${m.id}`]: true }));
    try {
      const { data } = await api.patch(`/users/${m.id}/subcontractor`, { is_subcontractor: next });
      setMembers(list => list.map(x => x.id === m.id ? { ...x, is_subcontractor: data.is_subcontractor } : x));
      toast.success(next ? `${m.name} is now flagged as a subcontractor — pricing hidden` : `${m.name} is no longer flagged as a subcontractor`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't update subcontractor flag"));
    } finally {
      setEntBusy(b => ({ ...b, [`sub-${m.id}`]: false }));
    }
  };

  const norm = (s) => (s || "").toLowerCase();
  const needle = q.trim().toLowerCase();
  const matchU = (m) => !needle || norm(m.name).includes(needle) || norm(m.email).includes(needle) || norm(m.phone).includes(needle);
  const matchS = (s) => !needle || norm(s.name).includes(needle) || norm(s.email).includes(needle) || norm(s.phone).includes(needle) || norm(s.company).includes(needle) || norm(s.trade).includes(needle);
  const office = members.filter(m => m.role === "admin").filter(matchU);
  const field = members.filter(m => m.role === "worker").filter(matchU);
  const subsF = subs.filter(matchS);

  // Duplicate detection — any member whose trimmed lowercase name appears
  // more than once in the full member list gets flagged. Fixes cases like
  // "Mykee has 3 profiles" where field techs got re-invited under new
  // accounts and the old ones stayed lingering.
  const nameCounts = {};
  for (const m of members) {
    const k = norm((m.name || "").trim());
    if (!k) continue;
    nameCounts[k] = (nameCounts[k] || 0) + 1;
  }
  const isDup = (m) => (nameCounts[norm((m.name || "").trim())] || 0) > 1;

  const deleteMember = async (m) => {
    if (!confirm(`Delete duplicate profile "${m.name}" (${m.email})? This cannot be undone.\n\nOnly delete the profiles that AREN'T actually being used by this person.`)) return;
    try {
      await api.delete(`/users/${m.id}`);
      toast.success(`Deleted ${m.name}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't delete user"));
    }
  };

  const MemberCard = ({ m }) => {
    const b = roleBadge(m);
    const showEntToggle = canGrantEnterprise && m.role === "admin" && m.platform_role !== "super_admin" && m.id !== user?.id;
    const dup = isDup(m);
    return (
      <div className={`rugged-card p-5 ${dup ? "ring-2 ring-red-500" : ""}`} data-testid={`team-card-${m.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 bg-[#0f172a] text-white flex items-center justify-center font-display font-black text-lg flex-shrink-0">{(m.name || "?").charAt(0).toUpperCase()}</div>
            <div className="min-w-0">
              <div className="font-display font-black text-lg truncate">{m.name}</div>
              <div className="text-xs text-slate-500 truncate">{m.email}</div>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {dup && (
                  <span className="inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-red-600 text-white" data-testid={`team-dup-badge-${m.id}`} title="More than one profile with this name exists in your team — keep the one this person actually uses and delete the rest.">
                    ⚠ Duplicate name
                  </span>
                )}
                <span className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${b.cls}`}>{b.label}</span>
                {m.enterprise_access && (
                  <span className="inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-fuchsia-600 text-white" data-testid={`team-enterprise-badge-${m.id}`}>🎯 Enterprise</span>
                )}
                {m.is_subcontractor && (
                  <span className="inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-orange-600 text-white" data-testid={`team-sub-badge-${m.id}`}>🚧 Sub · No Pricing</span>
                )}
              </div>
            </div>
          </div>
          {m.platform_role !== "super_admin" && (
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <button onClick={() => setEdit(m)} data-testid={`team-edit-${m.id}`}
                className="text-xs font-black uppercase tracking-wider text-[#00C4CC] underline">Edit</button>
              {dup && m.id !== user?.id && (
                <button onClick={() => deleteMember(m)} data-testid={`team-delete-dup-${m.id}`}
                  className="text-[10px] font-black uppercase tracking-wider text-red-600 underline">🗑 Delete</button>
              )}
            </div>
          )}
        </div>
        {m.phone && <div className="mt-3 text-sm text-slate-600">{m.phone}</div>}
        {m.hourly_rate > 0 && <div className="mt-1 text-[11px] font-black uppercase tracking-widest text-emerald-700">${m.hourly_rate}/hr</div>}
        {(m.role === "worker" || (m.role === "admin" && (user?.is_founder || user?.platform_role === "super_admin"))) && m.platform_role !== "super_admin" && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await api.post(`/users/${m.id}/magic-link`);
                  await navigator.clipboard.writeText(r.data.url);
                  toast.success(`Magic link for ${m.name} copied — paste into a text to them`);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not create magic link"));
                }
              }}
              data-testid={`team-magic-link-${m.id}`}
              title="Copy a single-use, 7-day sign-in link — text it to them, they tap it once, they're in"
              className="h-9 px-2 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1"
            >
              🔗 Copy Link
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm(`Login as ${m.name}?\n\nYou'll see what they see. Use the amber banner at the top to return to your admin account.`)) return;
                try {
                  const cur = localStorage.getItem("access_token");
                  const me = user; // from useAuth
                  const r = await api.post(`/users/${m.id}/magic-link`);
                  const magicTok = r.data.url.split("/m/")[1];
                  const ex = await api.post(`/auth/magic/${magicTok}`);
                  // Stash original admin session so we can swap back
                  localStorage.setItem("admin_before_impersonate", JSON.stringify({
                    token: cur, name: me?.name, email: me?.email, role: me?.role,
                  }));
                  localStorage.setItem("access_token", ex.data.access_token);
                  toast.success(`Now viewing as ${m.name}`);
                  setTimeout(() => { window.location.href = m.role === "admin" ? "/admin" : "/worker"; }, 300);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not switch"));
                }
              }}
              data-testid={`team-login-as-${m.id}`}
              title="See exactly what this tech sees — banner at top lets you return to admin"
              className="h-9 px-2 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1"
            >
              👁️ Login As
            </button>
          </div>
        )}
        {m.role === "admin" && m.platform_role !== "super_admin" && (user?.is_founder || user?.platform_role === "super_admin") && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`team-grants-${m.id}`}>
            {["billing", "branding"].map(area => {
              const g = m.area_grants?.[area]; const active = g && (!g.until || g.until > new Date().toISOString());
              const pending = !!m.area_requests?.[area];
              const grant = async (hours, revoke) => {
                try { await api.post(`/users/${m.id}/grant-area`, { area, hours, revoke }); toast.success(revoke ? `${area} locked` : `${area} unlocked ${hours ? "24h" : "permanently"}`); window.location.reload(); }
                catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
              };
              return (
                <span key={area} className={`inline-flex items-center gap-1 border text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 ${active ? "border-emerald-500 bg-emerald-50 text-emerald-800" : pending ? "border-amber-500 bg-amber-50 text-amber-800 animate-pulse" : "border-slate-300 text-slate-500"}`}>
                  {area}{pending && !active ? " · requested" : active ? (g.until ? " · 24h" : " · open") : ""}
                  {active ? <button onClick={() => grant(null, true)} className="ml-1 underline">lock</button>
                    : <><button onClick={() => grant(24, false)} className="ml-1 underline">24h</button><button onClick={() => grant(null, false)} className="ml-1 underline">always</button></>}
                </span>
              );
            })}
          </div>
        )}
        {m.platform_role !== "super_admin" && (
          <button type="button" data-testid={`team-revoke-links-${m.id}`}
            onClick={async () => {
              if (!window.confirm(`Revoke every outstanding sign-in link for ${m.name}? Any link already sent stops working immediately.`)) return;
              try { const r = await api.post(`/users/${m.id}/revoke-magic-links`); toast.success(`Links revoked for ${m.name} (${r.data.revoked} unused)`); }
              catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not revoke")); }
            }}
            className="mt-2 w-full h-8 border-2 border-red-300 text-red-700 hover:bg-red-50 font-black uppercase text-[10px] tracking-widest">
            ⛔ Revoke all sign-in links
          </button>
        )}
        {showEntToggle && (
          <button onClick={() => toggleEnterprise(m)} disabled={!!entBusy[m.id]}
            data-testid={`team-enterprise-toggle-${m.id}`}
            title="ProjectRadar, Landlord Portfolio & other Enterprise features"
            className={`mt-3 w-full h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-between disabled:opacity-40 ${
              m.enterprise_access
                ? "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                : "bg-white hover:bg-slate-50 text-slate-700"
            }`}>
            <span>🎯 Enterprise Access</span>
            <span className={`px-1.5 py-0.5 text-[9px] font-black ${m.enterprise_access ? "bg-white text-fuchsia-700" : "bg-slate-200 text-slate-600"}`}>
              {entBusy[m.id] ? "…" : (m.enterprise_access ? "ON — tap to revoke" : "OFF — tap to grant")}
            </span>
          </button>
        )}
        {canGrantEnterprise && m.id === user?.id && m.enterprise_access && (
          <div className="mt-3 px-3 h-9 border-2 border-dashed border-fuchsia-400 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-2 text-fuchsia-700 bg-fuchsia-50" data-testid={`team-enterprise-self-${m.id}`}>
            🎯 Enterprise Access · <span className="text-slate-500 normal-case font-medium tracking-normal">(you can't revoke your own)</span>
          </div>
        )}
        {canGrantEnterprise && m.platform_role !== "super_admin" && m.id !== user?.id && (
          <button onClick={() => toggleSubcontractor(m)} disabled={!!entBusy[`sub-${m.id}`]}
            data-testid={`team-sub-toggle-${m.id}`}
            title="Subcontractors NEVER see pricing (cost_cents, quote ceiling, amended-quote PDF)"
            className={`mt-2 w-full h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-between disabled:opacity-40 ${
              m.is_subcontractor
                ? "bg-orange-600 hover:bg-orange-700 text-white"
                : "bg-white hover:bg-slate-50 text-slate-700"
            }`}>
            <span>🚧 Subcontractor · Hide Pricing</span>
            <span className={`px-1.5 py-0.5 text-[9px] font-black ${m.is_subcontractor ? "bg-white text-orange-700" : "bg-slate-200 text-slate-600"}`}>
              {entBusy[`sub-${m.id}`] ? "…" : (m.is_subcontractor ? "ON — tap to unflag" : "OFF — tap to flag")}
            </span>
          </button>
        )}
      </div>
    );
  };

  const SubCard = ({ s }) => (
    <div className="rugged-card p-5" data-testid={`team-sub-card-${s.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-black text-lg truncate">{s.name}</div>
          {s.company && <div className="text-[11px] font-black uppercase tracking-widest text-[#0f172a] mt-0.5 truncate">{s.company}</div>}
          <div className="text-xs text-slate-500 truncate">{s.email}</div>
          <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-orange-600 text-white">Sub-Contractor</span>
        </div>
        <Link to="/admin/sub-contractors" data-testid={`team-sub-manage-${s.id}`}
          className="text-xs font-black uppercase tracking-wider text-[#00C4CC] underline flex-shrink-0">Manage</Link>
      </div>
      {s.phone && <div className="mt-3 text-sm text-slate-600">{s.phone}</div>}
      {s.trade && <div className="mt-1 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{s.trade}</div>}
    </div>
  );

  const Section = ({ title, count, subtitle, testId, children }) => (
    <section className="mt-8" data-testid={testId}>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="font-display font-black text-xl sm:text-2xl uppercase tracking-wide">
          {title} <span className="text-slate-400 text-lg">· {count}</span>
        </h2>
        {subtitle && <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{subtitle}</span>}
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </section>
  );

  return (
    <AdminShell>
      <div className="p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Team</h1>
            <p className="mt-1 text-sm text-slate-600 font-medium">Everyone who moves the needle — office, field, and subs. One crew.</p>
            {tenant && (
              <div className="mt-1 text-[11px] font-bold text-slate-500 uppercase tracking-wider" data-testid="worker-usage">
                {tenant.worker_count} field workers · {(tenant.plan || "").toUpperCase()} PLAN
              </div>
            )}
            {!isSuper && (
              <div className="mt-1 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Admin · You can invite crew members below
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/sub-contractors" data-testid="team-manage-subs-btn"
              className="tactile-btn h-12 px-4 font-black uppercase inline-flex items-center gap-2 bg-white border-2 border-[#0f172a] text-[#0f172a] text-sm">
              <Wrench weight="bold" size={16}/> Manage Subs
            </Link>
            <button onClick={() => setOpen(true)} data-testid="add-worker-btn"
              className="tactile-btn h-12 px-5 font-black uppercase inline-flex items-center gap-2 bg-[#00C4CC] text-white">
              <Plus weight="bold" size={18}/> Invite Team Member
            </button>
          </div>
        </div>

        <div className="mt-6 relative max-w-md">
          <MagnifyingGlass weight="bold" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} data-testid="team-search"
            placeholder="Search name, email, phone, company…"
            className="w-full h-11 pl-10 pr-3 border-2 border-[#0f172a] bg-white font-medium"/>
        </div>

        <Section title="Office Staff" count={office.length} subtitle="Owners · Managers · Dispatch" testId="section-office">
          {office.map(m => <MemberCard key={m.id} m={m}/>)}
          {office.length === 0 && <div className="col-span-full text-center text-slate-400 font-bold py-6">No office staff match.</div>}
        </Section>

        <Section title="Field Workers" count={field.length} subtitle="On-site crew" testId="section-field">
          {field.map(m => <MemberCard key={m.id} m={m}/>)}
          {field.length === 0 && <div className="col-span-full text-center text-slate-400 font-bold py-6">No field workers match.</div>}
        </Section>

        <Section title="Sub-Contractors" count={subsF.length} subtitle="External trades" testId="section-subs">
          {subsF.map(s => <SubCard key={s.id} s={s}/>)}
          {subsF.length === 0 && (
            <div className="col-span-full text-center text-slate-400 font-bold py-6">
              No sub-contractors match. <Link to="/admin/sub-contractors" className="text-[#00C4CC] underline">Add one →</Link>
            </div>
          )}
        </Section>

        {open && <NewWorkerModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }}/>}
        {edit && <EditWorkerModal worker={edit} isSuper={isSuper} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }}/>}
      </div>
    </AdminShell>
  );
}

function EditWorkerModal({ worker, isSuper, onClose, onSaved }) {
  const initialCombined = worker.role === "worker" ? "worker" : (worker.office_role || "owner");
  const [f, setF] = useState({
    name: worker.name, email: worker.email, phone: worker.phone || "",
    password: "", combined: initialCombined,
    hourly_rate: worker.hourly_rate ?? 125, subcontractor_id: worker.subcontractor_id || "",
    skills: worker.skills || [], home_zip: worker.home_zip || "",
  });
  const [busy, setBusy] = useState(false);
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/sub-contractors").then(r => setSubs(r.data)).catch(() => setSubs([])); }, []);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleSkill = (skill) => {
    const has = f.skills.includes(skill);
    setF({ ...f, skills: has ? f.skills.filter(s => s !== skill) : [...f.skills, skill] });
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        name: f.name, email: f.email, phone: f.phone,
        hourly_rate: parseFloat(f.hourly_rate) || 0,
        subcontractor_id: f.subcontractor_id,
        skills: f.skills, home_zip: f.home_zip,
      };
      if (f.password) payload.password = f.password;
      if (f.combined === "worker") { payload.role = "worker"; }
      else if (f.combined === "owner") { payload.role = "admin"; payload.office_role = ""; }
      else { payload.role = "admin"; payload.office_role = f.combined; }
      await api.patch(`/users/${worker.id}`, payload);
      toast.success("Team member updated");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm(`Delete ${worker.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${worker.id}`);
      toast.success("Team member deleted");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-[100] p-4" onClick={onClose} data-testid="edit-worker-modal">
      <div className="w-full max-w-md my-4 bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] flex flex-col max-h-[calc(100vh-2rem)]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-3 flex-shrink-0 border-b border-slate-100 bg-white">
          <h3 className="font-display font-black text-2xl">Edit Team Member</h3>
          <button onClick={onClose} data-testid="edit-w-close"><X weight="bold" size={22}/></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0" data-testid="edit-worker-form">
          <Field label="Name" required><input required value={f.name} onChange={upd("name")} data-testid="edit-w-name" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          <Field label="Email" required><input required type="email" value={f.email} onChange={upd("email")} data-testid="edit-w-email" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          <Field label="Phone"><input value={f.phone} onChange={upd("phone")} data-testid="edit-w-phone" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          <Field label="New Password (leave blank to keep)"><input type="password" minLength={6} value={f.password} onChange={upd("password")} data-testid="edit-w-pass" className="w-full h-11 px-3 border-2 border-[#0f172a]" placeholder="••••••••"/></Field>
          <Field label="Role">
            <select value={f.combined} onChange={upd("combined")} data-testid="edit-w-role" className="w-full h-11 px-2 border-2 border-[#0f172a] bg-white">
              <option value="worker">Field Worker — counts toward plan limit</option>
              <option value="owner">Admin — Owner (full access)</option>
              <option value="partner">Admin — Partner (owner access, partner badge)</option>
              <option value="manager">Admin — Manager (full access)</option>
              <option value="staff">Admin — Office Staff (no profit / branding / delete)</option>
            </select>
          </Field>
          <Field label="Hourly Rate ($/hr)">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-black text-slate-500">$</span>
              <input type="number" min="0" step="1" value={f.hourly_rate} onChange={upd("hourly_rate")}
                data-testid="edit-w-rate"
                className="w-full h-11 pl-7 pr-3 border-2 border-[#0f172a] font-mono font-bold"/>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Snapshot on every Quick Job for accurate billing. Default $125.</div>
          </Field>
          <Field label="Attached to Subcontractor">
            <select value={f.subcontractor_id} onChange={upd("subcontractor_id")} data-testid="edit-w-sub"
              className="w-full h-11 px-2 border-2 border-[#0f172a] bg-white">
              <option value="">— None (in-house) —</option>
              {subs.map(s => (
                <option key={s.id} value={s.id}>{s.name}{s.company ? ` · ${s.company}` : ""}{s.trade ? ` (${s.trade})` : ""}</option>
              ))}
            </select>
            <div className="text-[10px] text-slate-500 mt-1">
              When this tech completes a job, it auto-awards to the sub for pricing/billing.
            </div>
          </Field>

          {/* AUTO-DISPATCH SIGNALS ------------------------------------------------ */}
          <div className="border-2 border-fuchsia-500 bg-fuchsia-50 p-3" data-testid="edit-w-dispatch-block">
            <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-800 mb-1">
              🎯 Auto-Dispatch Signals
            </div>
            <div className="text-[10px] text-fuchsia-800 mb-2">
              Used by Auto-Dispatch when it's turned on in Settings. Skills bump match score by 60; a ZIP within 3mi adds 25.
            </div>
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">Skills</label>
            <div className="mt-1 flex flex-wrap gap-1" data-testid="edit-w-skills-chips">
              {["plumber","electrician","hvac","appliance","handyman","painter","roofer","landscaper","cleaner","carpenter","turnover","general"].map(sk => {
                const on = f.skills.includes(sk);
                return (
                  <button key={sk} type="button" onClick={() => toggleSkill(sk)}
                    data-testid={`edit-w-skill-${sk}`}
                    className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest border-2 transition-colors ${on ? "bg-fuchsia-600 text-white border-[#0f172a]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0f172a]"}`}>
                    {sk}
                  </button>
                );
              })}
            </div>
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 mt-3 block">Home ZIP</label>
            <input value={f.home_zip} onChange={upd("home_zip")} maxLength={5} placeholder="e.g. 19143"
              data-testid="edit-w-home-zip"
              className="w-full h-10 px-2 border-2 border-[#0f172a] font-mono font-bold mt-1"/>
          </div>

          <div className="flex gap-2 pt-2 sticky bottom-0 bg-white border-t border-slate-100 -mx-6 px-6 pb-1">
            {isSuper && (
              <button type="button" onClick={del} data-testid="edit-w-delete" className="h-11 px-4 border-2 border-red-600 text-red-600 font-black uppercase text-sm tracking-wider">
                <Trash weight="bold" size={14} className="inline mr-1"/> Delete
              </button>
            )}
            <button type="submit" disabled={busy} data-testid="edit-w-save" className="tactile-btn flex-1 h-11 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider">{busy ? "Saving..." : "Save Changes"}</button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function NewWorkerModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", role: "worker", office_role: "", hourly_rate: 125 });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...f, hourly_rate: parseFloat(f.hourly_rate) || 0 };
      if (payload.role === "worker" || !payload.office_role) delete payload.office_role;
      await api.post("/auth/register", payload);
      toast.success("Team member added");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // Combined role picker: Worker / Admin (Owner) / Admin (Manager) / Admin (Office Staff)
  const combined = f.role === "worker" ? "worker" : (f.office_role || "owner");
  const setCombined = (v) => {
    if (v === "worker") setF({ ...f, role: "worker", office_role: "" });
    else if (v === "owner") setF({ ...f, role: "admin", office_role: "" });
    else setF({ ...f, role: "admin", office_role: v });
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] flex flex-col max-h-[calc(100vh-2rem)]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-3 flex-shrink-0 border-b border-slate-100 bg-white">
          <h3 className="font-display font-black text-2xl">Add Team Member</h3>
          <button onClick={onClose}><X weight="bold" size={22}/></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0">
          <Field label="Name" required><input required value={f.name} onChange={upd("name")} data-testid="w-name" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          <Field label="Email" required><input required type="email" value={f.email} onChange={upd("email")} data-testid="w-email" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          <Field label="Phone"><input value={f.phone} onChange={upd("phone")} data-testid="w-phone" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          <Field label="Password" required><input required type="password" minLength={6} value={f.password} onChange={upd("password")} data-testid="w-pass" className="w-full h-11 px-3 border-2 border-[#0f172a]"/></Field>
          <Field label="Role">
            <select value={combined} onChange={(e) => setCombined(e.target.value)} data-testid="w-role" className="w-full h-11 px-2 border-2 border-[#0f172a] bg-white">
              <option value="worker">Worker (field tech) — counts toward plan limit</option>
              <option value="owner">Admin — Owner (full access)</option>
              <option value="partner">Admin — Partner (owner access, partner badge)</option>
              <option value="manager">Admin — Manager (full access)</option>
              <option value="staff">Admin — Office Staff (no profit / branding / delete)</option>
            </select>
          </Field>
          <Field label="Hourly Rate ($/hr)">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-black text-slate-500">$</span>
              <input type="number" min="0" step="1" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })}
                data-testid="w-rate"
                className="w-full h-11 pl-7 pr-3 border-2 border-[#0f172a] font-mono font-bold"/>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Used for Quick Job billing snapshots. Default $125.</div>
          </Field>
          <div className="pt-2 sticky bottom-0 bg-white border-t border-slate-100 -mx-6 px-6 pb-1">
            <button type="submit" disabled={busy} data-testid="w-submit" className="tactile-btn w-full h-12 bg-[#00C4CC] text-white font-black uppercase">{busy ? "Adding..." : "Add"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Reports ----
export function AdminReports() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [profit, setProfit] = useState({ totals: null, jobs: [] });
  const isFullAdmin = user && (user.platform_role === "super_admin" || (user.role === "admin" && (!user.office_role || user.office_role === "manager")));
  useEffect(() => {
    api.get("/jobs").then(r => setJobs(r.data));
    if (isFullAdmin) api.get("/reports/profit").then(r => setProfit(r.data)).catch(() => {});
  }, [isFullAdmin]);
  const byStatus = jobs.reduce((a, j) => { a[j.status] = (a[j.status] || 0) + 1; return a; }, {});
  const totalMats = jobs.reduce((s, j) => s + (j.materials || []).reduce((s2, m) => s2 + (m.quantity || 0) * (m.unit_cost || 0), 0), 0);
  const payrollUrl = () => {
    const tok = localStorage.getItem("access_token") || "";
    const p = new URLSearchParams({ auth: tok });
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    return `${API}/reports/payroll?${p.toString()}`;
  };
  return (
    <AdminShell>
      <div className="p-8">
        <h1 className="font-display font-black text-4xl">Reports</h1>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/admin/time-audit" data-testid="reports-time-audit-link"
            className="inline-flex items-center gap-1.5 h-10 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-100 font-black uppercase text-[11px] tracking-widest">
            🕒 Time-Adjust Audit
          </Link>
        </div>
        <div className="mt-6"><PmActivityReportCard/></div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rugged-card p-6">
            <h3 className="font-display font-black text-lg uppercase">Jobs by Status</h3>
            <ul className="mt-3 space-y-2">
              {Object.entries(byStatus).map(([k, v]) => (
                <li key={k} className="flex justify-between border-b border-slate-200 py-2">
                  <span className="font-bold uppercase text-sm">{k.replace("_", " ")}</span>
                  <span className="font-display font-black">{v}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rugged-card p-6">
            <h3 className="font-display font-black text-lg uppercase">Materials Spend</h3>

        {profit.totals && isFullAdmin && (
          <div className="mt-6 rugged-card p-6" data-testid="profit-report">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-display font-black text-lg uppercase">Profit by Job (Admin Only)</h3>
              <span className="text-xs font-black uppercase tracking-widest text-slate-500">
                {profit.totals.job_count} job{profit.totals.job_count === 1 ? "" : "s"} with materials
              </span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="border-2 border-[#0f172a] p-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Billed to Customer</div>
                <div className="mt-1 font-display font-black text-xl sm:text-2xl">${profit.totals.billed.toFixed(2)}</div>
              </div>
              <div className="border-2 border-[#0f172a] p-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Our Cost</div>
                <div className="mt-1 font-display font-black text-xl sm:text-2xl">${profit.totals.internal_cost.toFixed(2)}</div>
              </div>
              <div className="border-2 border-[#00C4CC] p-3 bg-cyan-50">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">Margin</div>
                <div className="mt-1 font-display font-black text-xl sm:text-2xl text-[#00C4CC]">${profit.totals.margin.toFixed(2)}</div>
              </div>
            </div>
            {profit.jobs.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">Job</th>
                      <th className="px-3 py-2 text-right">Billed</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                      <th className="px-3 py-2 text-right">Margin</th>
                      <th className="px-3 py-2 text-right">Stock%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profit.jobs.slice(0, 15).map(r => {
                      const pct = r.billed > 0 ? (r.stock_billed / r.billed) * 100 : 0;
                      return (
                        <tr key={r.job_id} className="border-t border-slate-200 hover:bg-slate-50">
                          <td className="px-3 py-2 font-bold">
                            <Link to={`/admin/jobs/${r.job_id}`} className="hover:text-[#00C4CC]">
                              {r.work_order_number && <span className="mr-1 font-mono text-[10px] text-slate-500">[{r.work_order_number}]</span>}
                              {r.title}
                            </Link>
                            <div className="text-[10px] text-slate-500 font-medium mt-0.5">{r.assigned_to_name || "—"}</div>
                          </td>
                          <td className="px-3 py-2 text-right">${r.billed.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right text-slate-600">${r.internal_cost.toFixed(2)}</td>
                          <td className={`px-3 py-2 text-right font-black ${r.margin > 0 ? "text-green-600" : "text-slate-400"}`}>${r.margin.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right text-xs text-slate-500">{pct.toFixed(0)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {profit.jobs.length > 15 && (
                  <div className="mt-2 text-xs font-bold text-slate-500 text-center">Top 15 by margin · {profit.jobs.length} total</div>
                )}
              </div>
            )}
            {profit.totals.job_count === 0 && (
              <p className="mt-3 text-sm text-slate-500 font-medium">
                No margin to report yet — add parts to your catalog with sell prices, then tap "Use From Truck Stock" on jobs to build up profit history.
              </p>
            )}
          </div>
        )}

            <div className="mt-3 font-display font-black text-5xl">${totalMats.toFixed(2)}</div>
            <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1">All time · from logged materials</div>
          </div>
        </div>

        <div className="mt-6 rugged-card p-6">
          <h3 className="font-display font-black text-lg uppercase">Payroll / Timesheet Export</h3>
          <p className="mt-1 text-sm text-slate-600 font-medium">Download a CSV of every clocked-in hour + materials cost per worker, ready for payroll.</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Start</label>
              <input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="payroll-start"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider">End</label>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="payroll-end"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </div>
            <div className="flex items-end">
              <a href={payrollUrl()} data-testid="payroll-download-btn"
                className="tactile-btn w-full h-11 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider inline-flex items-center justify-center gap-1">
                Download CSV
              </a>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

function PartnerHandledToggle({ job, onDone }) {
  const [busy, setBusy] = useState(false);
  const on = !!job.partner_handled;
  const toggle = async () => {
    setBusy(true);
    try {
      await api.put(`/jobs/${job.id}/partner-handled`, { partner: on ? "" : "Little Bell Mgmt" });
      toast.success(on ? "Back to PMR" : "Tagged: Little Bell Mgmt handling repair — documenting only");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} disabled={busy} data-testid="partner-handled-toggle"
      title={on ? "Click to clear — PMR takes this repair" : "Mark as being repaired by Little Bell Mgmt (we document only)"}
      className={`px-2 py-0.5 border-2 text-[10px] font-black uppercase tracking-widest rounded-sm transition-colors disabled:opacity-60
        ${on ? "border-violet-500 bg-violet-600 text-white hover:bg-violet-700" : "border-slate-300 bg-white text-slate-500 hover:border-violet-400 hover:text-violet-700"}`}>
      {on ? `${job.partner_handled} · repairing` : "Little Bell?"}
    </button>
  );
}

/**
 * CombineWosModal — batch-combine selected WOs onto one parent, each merged
 * ticket becoming its own checklist line. Blocks cross-address combines.
 * Optional "create new parent" mode when the user doesn't want any of the
 * selected WOs to inherit the merge (e.g. brand-new 4171-1 for a fresh
 * property visit).
 *
 * Backend: POST /api/jobs/combine  { parent_id, job_ids }
 */
export function CombineWosModal({ selectedJobs, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [parentMode, setParentMode] = useState("existing"); // existing | new
  const [parentId, setParentId] = useState(selectedJobs[0]?.id || "");
  const jobs = selectedJobs || [];
  // Same-address guard — computed from the selection
  const norm = (a, u) => `${(a || "").trim().toLowerCase()}|${(u || "").trim().toLowerCase()}`;
  const addrs = Array.from(new Set(jobs.map(j => norm(j.property_address, j.unit))));
  const sameAddress = addrs.length === 1 && addrs[0] !== "|";
  const address = jobs[0]?.property_address ? `${jobs[0].property_address}${jobs[0].unit ? ` · Unit ${jobs[0].unit}` : ""}` : "";

  const submit = async () => {
    if (!sameAddress) return;
    setBusy(true);
    try {
      const body = { job_ids: jobs.map(j => j.id) };
      if (parentMode === "existing") body.parent_id = parentId;
      const { data } = await api.post("/jobs/combine", body);
      toast.success(`Combined ${data.combined_count} tickets onto ${data.parent.work_order_number || "parent WO"}`);
      onSaved && onSaved(data.parent?.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't combine");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-[110] p-4 overflow-y-auto"
         onClick={onClose} data-testid="combine-wos-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6 my-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-black text-2xl inline-flex items-center gap-2">
              🔗 Combine on one WO
            </h3>
            <p className="mt-1 text-xs text-slate-600 font-medium">
              Each selected ticket becomes its own checklist line on the parent. Photos, notes, and source WO number are preserved.
            </p>
          </div>
          <button onClick={onClose} className="p-1" data-testid="combine-close">✕</button>
        </div>

        {!sameAddress && (
          <div className="mt-4 p-4 bg-red-50 border-2 border-red-500 text-sm" data-testid="combine-address-block">
            <div className="font-black uppercase tracking-widest text-red-800">Blocked — Different Properties</div>
            <p className="mt-1 text-red-900">
              All selected WOs must be at the same property. Deselect any WO that isn't at the same address.
            </p>
            <ul className="mt-2 text-xs list-disc pl-5 space-y-0.5 text-red-800">
              {jobs.map(j => (
                <li key={j.id}>WO {j.work_order_number || j.id.slice(-6)} · {j.property_address || "—"}{j.unit ? ` · Unit ${j.unit}` : ""}</li>
              ))}
            </ul>
          </div>
        )}

        {sameAddress && (
          <>
            <div className="mt-4 p-3 border-2 border-cyan-400 bg-cyan-50">
              <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">Property</div>
              <div className="mt-0.5 font-semibold text-slate-900">{address}</div>
              <div className="mt-1 text-xs text-slate-600">{jobs.length} WOs selected</div>
            </div>

            <div className="mt-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Parent WO</div>
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => setParentMode("existing")}
                  data-testid="combine-parent-mode-existing"
                  className={`h-9 px-3 text-xs font-black uppercase tracking-widest ${parentMode === "existing" ? "bg-[#0f172a] text-white" : "bg-slate-50 border border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                  Use an existing WO
                </button>
                <button type="button" onClick={() => setParentMode("new")}
                  data-testid="combine-parent-mode-new"
                  className={`h-9 px-3 text-xs font-black uppercase tracking-widest ${parentMode === "new" ? "bg-[#0f172a] text-white" : "bg-slate-50 border border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                  Create a new one (-1)
                </button>
              </div>

              {parentMode === "existing" ? (
                <div className="space-y-1 max-h-52 overflow-y-auto border-2 border-slate-200">
                  {jobs.map(j => (
                    <button key={j.id} type="button" onClick={() => setParentId(j.id)}
                      data-testid={`combine-parent-pick-${j.id}`}
                      className={`w-full text-left p-2.5 border-b border-slate-100 last:border-0 hover:bg-cyan-50 ${parentId === j.id ? "bg-cyan-100 ring-2 ring-cyan-500" : ""}`}>
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        WO {j.work_order_number || j.id.slice(-6)} {parentId === j.id && <span className="text-cyan-700">· ★ PARENT</span>}
                      </div>
                      <div className="mt-0.5 text-sm font-semibold text-slate-900 truncate">
                        {j.title || j.description?.slice(0, 60) || "—"}
                      </div>
                      {j.assigned_to_name && (
                        <div className="text-[11px] text-slate-500">Tech: {j.assigned_to_name}</div>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-3 border-2 border-slate-200 bg-slate-50 text-xs text-slate-600">
                  A fresh parent WO will be created with the next available suffix (e.g. 4171-1). All selected WOs become checklist items on it.
                </div>
              )}
            </div>

            <div className="mt-4 p-3 bg-slate-50 border border-slate-200 text-xs">
              <div className="font-black uppercase tracking-widest text-slate-600 mb-2">Preview · What each ticket becomes on the parent</div>
              <ul className="space-y-1">
                {jobs
                  .filter(j => parentMode !== "existing" || j.id !== parentId)
                  .map(j => (
                    <li key={j.id} className="flex items-start gap-2">
                      <span>☐</span>
                      <span className="flex-1">
                        <b>{j.title || (j.description || "").slice(0, 60) || "Task"}</b>
                        <span className="text-slate-500"> — from WO {j.work_order_number || j.id.slice(-6)}</span>
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-11 px-4 border-2 border-slate-300 font-black uppercase text-xs tracking-widest hover:bg-slate-100"
            data-testid="combine-cancel">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!sameAddress || busy || (parentMode === "existing" && !parentId)}
            className="h-11 px-5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40"
            data-testid="combine-save">
            {busy ? "Combining…" : "Combine on one WO"}
          </button>
        </div>
      </div>
    </div>
  );
}

